/* ============================================================
   Orchard St. - custom JS
   Convention: custom elements registered with os-* prefix.
   ============================================================ */

/* Shared delivery-calendar cell for a "sold out" date: a merchant-blocked day that would
   otherwise be a valid delivery day. Renders the day number plus a small info "i" trigger
   whose tooltip reads "Sold out" (on hover and on keyboard/touch focus - see os-custom.css).
   Defined at module scope so both calendar renderers (the PDP first-add modal <os-cleanse-date-modal>
   and the cart <os-cart-cleanse-date>, each in its own IIFE) share one source of truth. */
function osCalSoldOutCell(dayNum) {
  return '<span class="os-delivery-cal__cell os-delivery-cal__day is-disabled is-soldout" aria-disabled="true">' +
    '<span class="os-delivery-cal__daynum">' + dayNum + '</span>' +
    '<button type="button" class="os-delivery-cal__info" aria-label="Sold out">' +
      '<svg class="os-delivery-cal__info-icon" viewBox="0 0 16 16" width="11" height="11" aria-hidden="true" focusable="false">' +
        '<circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" stroke-width="1.4"></circle>' +
        '<circle cx="8" cy="4.7" r="1" fill="currentColor"></circle>' +
        '<rect x="7.15" y="6.9" width="1.7" height="5" rx="0.85" fill="currentColor"></rect>' +
      '</svg>' +
    '</button>' +
    '<span class="os-delivery-cal__tip" role="tooltip">Sold out</span>' +
  '</span>';
}

/* ------ <os-concern-list> ------ */

class OsConcernList extends HTMLElement {
  connectedCallback() {
    this.items = this.querySelectorAll('.os-concern__item');
    if (!this.items.length) return;

    this.figures = this.querySelectorAll('.os-concern__image');
    this.leftImg = this.querySelector('.os-concern__image--left .os-concern__img');
    this.rightImg = this.querySelector('.os-concern__image--right .os-concern__img');
    this.leftLabel = this.querySelector('.os-concern__image--left .os-concern__caption-label');
    this.leftText = this.querySelector('.os-concern__image--left .os-concern__caption-text');
    this.rightLabel = this.querySelector('.os-concern__image--right .os-concern__caption-label');
    this.rightText = this.querySelector('.os-concern__image--right .os-concern__caption-text');

    // First item renders active; track it so hovering it again is a no-op.
    this.current = this.items[0];
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    for (const item of this.items) {
      item.addEventListener('mouseenter', () => this.activate(item));
      item.addEventListener('focusin', () => this.activate(item));
    }
  }

  activate(item) {
    if (item === this.current) return;
    this.current = item;

    for (const el of this.items) {
      el.classList.toggle('is-active', el === item);
    }

    // The data-* attributes already carry the block value with the section setting baked in
    // as a fallback, so we just apply them straight across.
    const apply = () => {
      this.swapImage(this.leftImg, item.dataset.leftImage);
      this.swapImage(this.rightImg, item.dataset.rightImage);
      this.setText(this.leftLabel, item.dataset.leftLabel);
      this.setText(this.leftText, item.dataset.leftText);
      this.setText(this.rightLabel, item.dataset.rightLabel);
      this.setText(this.rightText, item.dataset.rightText);
    };

    if (this.reduceMotion) {
      apply();
      return;
    }

    // Subtle fade: dim the figures, swap while hidden, then fade back in.
    clearTimeout(this.swapTimer);
    this.setSwapping(true);
    this.swapTimer = setTimeout(() => {
      apply();
      this.setSwapping(false);
    }, 200);
  }

  setSwapping(on) {
    for (const fig of this.figures) fig.classList.toggle('is-swapping', on);
  }

  swapImage(img, src) {
    if (!img || !src) return;
    img.srcset = '';
    img.src = src;
  }

  setText(el, value) {
    if (!el) return;
    el.textContent = value || '';
  }
}

customElements.define('os-concern-list', OsConcernList);

/* ------ <os-quicknav> ------
   The collection sub-category pill row scrolls horizontally, but without a trackpad or a
   tilt-wheel mouse a desktop user has no way to reach the overflow. This overlays prev/next
   chevrons and edge fades, shown only when there is more to scroll in that direction. */

class OsQuicknav extends HTMLElement {
  connectedCallback() {
    this.scroller = this.querySelector('.collection-quick-navigation');
    this.prev = this.querySelector('.os-quicknav__chevron--prev');
    this.next = this.querySelector('.os-quicknav__chevron--next');
    if (!this.scroller || !this.prev || !this.next) return;

    this.prev.addEventListener('click', () => this.page(-1));
    this.next.addEventListener('click', () => this.page(1));

    this.onScroll = () => this.update();
    this.scroller.addEventListener('scroll', this.onScroll, { passive: true });

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this.scroller);
    } else {
      window.addEventListener('resize', this.onScroll);
    }

    this.update();
  }

  disconnectedCallback() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    else window.removeEventListener('resize', this.onScroll);
  }

  page(direction) {
    // Scroll by most of a viewport so consecutive clicks march through the pills.
    const amount = this.scroller.clientWidth * 0.8;
    this.scroller.scrollBy({ left: amount * direction, behavior: 'smooth' });
  }

  update() {
    const { scrollLeft, scrollWidth, clientWidth } = this.scroller;
    // scrollLeft is negative in RTL; compare against the distance from the start/end.
    const start = Math.abs(scrollLeft);
    const maxScroll = scrollWidth - clientWidth;
    const atStart = start <= 1;
    const atEnd = start >= maxScroll - 1;
    const overflows = maxScroll > 1;

    this.prev.hidden = atStart || !overflows;
    this.next.hidden = atEnd || !overflows;
    this.classList.toggle('is-overflow-start', overflows && !atStart);
    this.classList.toggle('is-overflow-end', overflows && !atEnd);
  }
}

customElements.define('os-quicknav', OsQuicknav);

/* ------ Collection toolbar: quick-filter labels + SPLIT Filter / Sort ------ */
/* Figma (332:13192) shows a "Filter" control (left, opens a filters-only drawer) and a
   separate "Sort by: …" control (right). The Stretch theme instead renders a single
   combined "Filter & sort" drawer button with Sort as the first accordion INSIDE the
   drawer. We can't edit the theme's toolbar Liquid, so we restructure in the DOM:
   relabel the button to "Filter", inject the quick-filter labels, build a right-side
   Sort popover from the drawer's sort radios, and hide the in-drawer sort accordion.
   The toolbar re-renders on facet (AJAX) updates, so this is idempotent and re-runs. */
(function () {
  // Runs on the collection grid AND the search results page (same toolbar + facets-drawer
  // markup), so both get the identical "Filter | Brand Price …" bar + Sort control.
  if (!document.body.classList.contains('template-collection') &&
      !document.body.classList.contains('template-search')) return;

  var section = document.querySelector('.shopify-section--main-collection') ||
                document.querySelector('.shopify-section--main-search') || document;
  var observer = null;
  var scheduled = false;

  function buildSort(toolbar, drawer) {
    var radios = drawer.querySelectorAll('input[name="sort_by"]');
    if (!radios.length) return;

    var current = null;
    var options = [];
    radios.forEach(function (r) {
      var id = window.CSS && CSS.escape ? CSS.escape(r.id) : r.id;
      var lab = drawer.querySelector('label[for="' + id + '"]');
      var opt = { value: r.value, label: lab ? lab.textContent.trim() : r.value };
      options.push(opt);
      if (r.checked) current = opt;
    });
    current = current || options[0];

    function hrefFor(value) {
      var u = new URL(window.location.href);
      u.searchParams.set('sort_by', value);
      u.searchParams.delete('page');
      return u.pathname + '?' + u.searchParams.toString();
    }

    var sort = document.createElement('div');
    sort.className = 'os-sort sm-max:hidden';

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'os-sort__btn subheading';
    btn.setAttribute('aria-expanded', 'false');
    btn.append('Sort by: ');
    var cur = document.createElement('span');
    cur.className = 'os-sort__current';
    cur.textContent = current.label;
    btn.appendChild(cur);

    var menu = document.createElement('div');
    menu.className = 'os-sort__menu';
    menu.hidden = true;
    options.forEach(function (o) {
      var a = document.createElement('a');
      a.className = 'os-sort__option';
      a.href = hrefFor(o.value);
      a.textContent = o.label;
      a.setAttribute('data-no-instant', '');
      if (o.value === current.value) a.setAttribute('aria-selected', 'true');
      menu.appendChild(a);
    });

    btn.addEventListener('click', function (e) {
      e.stopPropagation();
      var open = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', String(!open));
      menu.hidden = open;
    });
    sort.appendChild(btn);
    sort.appendChild(menu);
    toolbar.appendChild(sort);

    // Drawer is filters-only (Figma) - hide the sort accordion still used as the data source.
    var acc = radios[0].closest('.accordion');
    if (acc) acc.hidden = true;
  }

  function init() {
    var toolbar = section.querySelector ? section.querySelector('.collection__toolbar') : document.querySelector('.collection__toolbar');
    var drawer = document.getElementById('facets-drawer');
    if (!toolbar || !drawer) return;

    // Idempotent: clear anything we injected before rebuilding.
    toolbar.querySelectorAll('.os-filter-labels, .os-sort').forEach(function (n) { n.remove(); });

    var filterBtn = toolbar.querySelector('.sm-max\\:hidden[aria-controls="facets-drawer"]');

    // Relabel the drawer header to "Filter" (the theme outputs the plural
    // "Filters"; Figma 332:13692 shows the singular).
    var drawerHeader = drawer.querySelector('[slot="header"]');
    if (drawerHeader) drawerHeader.textContent = 'Filter';

    // Open the drawer (and expand a named group). Per the design there is NO standalone
    // "Filter"/"Filter & sort" button - the Brand/Price/… labels ARE the triggers (the
    // theme's faceting buttons are CSS-hidden). Use one as the programmatic opener.
    function openFilters(groupName) {
      if (filterBtn) filterBtn.click();
      if (!groupName) return;
      drawer.querySelectorAll('.accordion__summary').forEach(function (s) {
        var label = (s.textContent || '').trim().toLowerCase();
        var disc = s.closest('.accordion__disclosure') || s.closest('details');
        if (disc && label.indexOf(groupName.toLowerCase()) === 0) disc.setAttribute('open', '');
      });
    }

    // Quick-filter labels (Brand / Price / Ingredient / Concern), excluding Sort.
    var names = [];
    drawer.querySelectorAll('.accordion .accordion__summary').forEach(function (el) {
      var span = el.querySelector('span') || el;
      var text = span.textContent.trim();
      if (text && text.toLowerCase().indexOf('sort') !== 0) names.push(text);
    });
    if (names.length) {
      var wrap = document.createElement('div');
      wrap.className = 'os-filter-labels sm-max:hidden';

      // "Filter" master trigger - plain full-ink text (NOT a pill), opens the whole
      // drawer (Figma 332:9749: "Filter" full ink + a vertical separator, then the
      // muted category labels).
      var filterTrigger = document.createElement('button');
      filterTrigger.type = 'button';
      filterTrigger.className = 'os-filter-label os-filter-label--primary';
      filterTrigger.textContent = 'Filter';
      filterTrigger.addEventListener('click', function () { openFilters(); });
      wrap.appendChild(filterTrigger);

      names.forEach(function (name) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'os-filter-label';
        b.textContent = name;
        b.addEventListener('click', function () { openFilters(name); });
        wrap.appendChild(b);
      });
      var anchor = (filterBtn && filterBtn.closest('.vertically-separated-list')) || toolbar.firstChild;
      if (anchor && anchor.after) anchor.after(wrap); else toolbar.appendChild(wrap);
    }

    // Move the active-filter chips inline into the bar (the theme renders them in a
    // separate row above the grid; the design shows them in the toolbar, 332:13085).
    var stale = toolbar.querySelector('.active-facets');
    if (stale) stale.remove();
    var active = section.querySelector('.collection__results > .active-facets') ||
      section.querySelector('.active-facets');
    if (active) toolbar.appendChild(active);

    // Right-side Sort control.
    buildSort(toolbar, drawer);
  }

  function run() {
    if (observer) observer.disconnect();
    try { init(); } finally {
      if (observer && section.nodeType) observer.observe(section, { childList: true, subtree: true });
    }
  }

  if (window.MutationObserver && section.nodeType === 1) {
    observer = new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(function () { scheduled = false; run(); });
    });
  }

  // Close the sort popover on any outside click.
  document.addEventListener('click', function () {
    var open = document.querySelector('.os-sort__btn[aria-expanded="true"]');
    if (open) {
      open.setAttribute('aria-expanded', 'false');
      var m = open.parentNode.querySelector('.os-sort__menu');
      if (m) m.hidden = true;
    }
  });

  run();
})();

/* ------ Header: live "Cart [n]" label ------ */
/* The header cart link shows a text label "Cart [n]" (Figma 316:1636). The count
   is rendered by CSS via attr(data-cart-count); this keeps that attribute in sync
   with the live cart. */
(function () {
  var links = document.querySelectorAll('.header__cart-link > a');
  if (!links.length) return;

  function setCount(n) {
    links.forEach(function (a) { a.setAttribute('data-cart-count', n); });
  }

  function refresh() {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { setCount(cart.item_count); })
      .catch(function () {});
  }

  setCount(0); // avoid an empty "[]" flash before the fetch resolves
  refresh();

  // Re-sync whenever the theme updates its cart-count element (add/remove items)
  var counter = document.querySelector('cart-count');
  if (counter && window.MutationObserver) {
    new MutationObserver(refresh).observe(counter, {
      attributes: true,
      childList: true,
      subtree: true
    });
  }
  document.addEventListener('cart:refresh', refresh);
})();

/* ------ PDP breadcrumb: clone the single source copy into the mobile flow slot ------ */
/* The breadcrumb is server-rendered once, in the desktop overlay inside the gallery (so it
   survives the sticky gallery + variant re-render). The mobile flow slot ships empty
   (main-product.liquid, data-os-breadcrumb-flow) so the breadcrumb appears once in the
   source; copy the overlay's markup into it here. Media-less products render the flow copy
   server-side instead, so there is nothing to clone. */
(function () {
  var flow = document.querySelector('[data-os-breadcrumb-flow]');
  if (!flow || flow.childElementCount) return;
  var source = document.querySelector('.os-pdp-breadcrumb-overlay .os-pdp-breadcrumb');
  if (source) flow.appendChild(source.cloneNode(true));
})();

/* ------ Mobile nav: progressive tier drawer + breadcrumb wayfinder ------ */
/* The mobile hamburger drawer (#menu-drawer, snippets/main-menu-drawer.liquid) drills tier by
   tier (Figma 316:2990 / 323:3853 / 323:4354 / 323:4879). It reuses the recursive column markup
   (os-shop-drawer-column, namespaced `osmcol-`) but, unlike the desktop Miller columns, shows ONE
   panel at a time and navigates with a BREADCRUMB wayfinder instead of a back arrow. This builds
   the breadcrumb and slides panels in/out by maintaining a stack of open panels. The theme's
   DialogElement still owns drawer open/close; tapping a leaf link navigates as normal. */
(function () {
  var drawer = document.getElementById('menu-drawer');
  if (!drawer) return;
  var mnav = drawer.querySelector('[data-os-mnav]');
  if (!mnav) return;

  var stage = mnav.querySelector('.os-mnav__stage');
  var crumbsNav = mnav.querySelector('.os-mnav__crumbs');
  var crumbList = mnav.querySelector('.os-mnav__crumb-list');
  var root = mnav.querySelector('[data-os-root]');
  if (!stage || !root) return;

  // The subtree columns ship inside a <template> (main-menu-drawer.liquid) so the mobile
  // duplicate of the ~700-link nav is absent from the rendered DOM at load. Clone them into
  // the stage the first time the drawer opens (or the first parent tap), then the existing
  // drill/breadcrumb logic below runs against the live columns.
  var tree = mnav.querySelector('[data-os-mnav-tree]');
  var hydrated = false;
  function hydrate() {
    if (hydrated) return;
    hydrated = true;
    if (tree && tree.content) stage.appendChild(tree.content.cloneNode(true));
  }

  var stack = [root];

  function panels() { return stage.querySelectorAll('.os-mnav__root, .os-drawer-col'); }

  function render() {
    var top = stack[stack.length - 1];

    panels().forEach(function (p) {
      p.classList.remove('is-active', 'is-past');
      if (p === top) {
        p.classList.add('is-active');
        p.removeAttribute('aria-hidden');
      } else {
        if (stack.indexOf(p) !== -1) p.classList.add('is-past');
        p.setAttribute('aria-hidden', 'true');
      }
    });
    top.scrollTop = 0;

    // Reflect the open path on the opener buttons (a11y + chevron state).
    mnav.querySelectorAll('.os-drawer-col__item--parent').forEach(function (btn) {
      var t = document.getElementById(btn.dataset.opens);
      btn.setAttribute('aria-expanded', t && stack.indexOf(t) !== -1 ? 'true' : 'false');
    });

    crumbList.innerHTML = '';
    if (stack.length <= 1) {
      crumbsNav.setAttribute('hidden', '');
      return;
    }
    crumbsNav.removeAttribute('hidden');
    stack.forEach(function (p, i) {
      var li = document.createElement('li');
      li.className = 'os-mnav__crumb';
      var label = p.getAttribute('data-crumb') || '';
      if (i === stack.length - 1) {
        li.classList.add('os-mnav__crumb--current');
        li.setAttribute('aria-current', 'page');
        li.textContent = label;
      } else {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'os-mnav__crumb-link';
        btn.textContent = label;
        (function (idx) { btn.addEventListener('click', function () { popTo(idx); }); })(i);
        li.appendChild(btn);
      }
      crumbList.appendChild(li);
    });
  }

  function openPanel(target) {
    if (!target || stack.indexOf(target) !== -1) return;
    stack.push(target);
    render();
  }

  function popTo(index) {
    if (index < 0 || index >= stack.length - 1) return;
    stack = stack.slice(0, index + 1);
    render();
  }

  function reset() {
    if (stack.length > 1) { stack = [root]; render(); }
  }

  drawer.addEventListener('click', function (event) {
    var btn = event.target.closest('.os-drawer-col__item--parent');
    if (!btn || !mnav.contains(btn)) return;
    event.preventDefault();
    hydrate();
    openPanel(document.getElementById(btn.dataset.opens));
  });

  // Populate the drawer as it opens (before it is visible), so the columns are present
  // before any interaction. Reset to the root tier whenever the drawer closes.
  drawer.addEventListener('dialog:before-show', hydrate);
  drawer.addEventListener('dialog:after-hide', reset);

  render();
})();

/* ------ Shop drawer: cascading Miller columns ------ */
/* Turns the sidebar drawer (#menu-dropdown-sidebar) into progressive columns: a parent row
   slides its child column out from the left (Figma 316:2613 / 323:4207 / 323:4678). Markup is
   server-rendered by os-shop-drawer-column.liquid; this toggles `.is-open` (CSS animates it).
   It also swaps the per-collection feature panel (image/caption/dropcap) on hover of a Tier-1
   collection, and collapses the feature panel for "Shop by concern/brand" so Tier 1 left-aligns.
   The theme's DialogElement still owns open/close + which level shows. */
(function () {
  var drawer = document.getElementById('menu-dropdown-sidebar');
  if (!drawer) return;

  function tierOf(el) { return parseInt(el.dataset.tier, 10); }

  // Close every column at `tier` and deeper within a level, de-activating their parents.
  function collapseFrom(scope, tier) {
    scope.querySelectorAll('.os-drawer-col').forEach(function (col) {
      if (tierOf(col) >= tier) {
        col.classList.remove('is-open');
        col.setAttribute('aria-hidden', 'true');
      }
    });
    scope.querySelectorAll('.os-drawer-col__item--parent').forEach(function (btn) {
      var target = document.getElementById(btn.dataset.opens);
      if (target && tierOf(target) >= tier) {
        btn.setAttribute('aria-expanded', 'false');
        btn.classList.remove('is-active');
      }
    });
  }

  // --- Feature panel (per Tier-1 collection). Set instantly by default; when `animate` is true
  // (switching between collections while the panel is already open) the image/caption/dropcap
  // crossfade via the `.is-swapping` opacity hook in os-custom.css. The first emerge from a
  // collapsed state stays instant - the panel fades its own opacity in as it opens. ---
  function applyFeature(scope, item, animate) {
    var panel = scope.querySelector('.os-drawer-feature');
    if (!panel || !item) return;

    var img = panel.querySelector('.os-drawer-feature__img');
    var fig = panel.querySelector('.os-drawer-feature__fig');
    var text = panel.querySelector('.os-drawer-feature__text');
    var dropcap = panel.querySelector('.os-drawer-feature__dropcap');
    if (dropcap && dropcap._osDefault === undefined) dropcap._osDefault = dropcap.innerHTML;

    var doSwap = function () {
      if (img && item.dataset.featureImage) { img.srcset = ''; img.src = item.dataset.featureImage; }
      if (text && typeof item.dataset.caption === 'string') { text.textContent = item.dataset.caption; }
      if (fig && item.dataset.captionLabel) { fig.textContent = item.dataset.captionLabel; }
      if (dropcap) {
        dropcap.innerHTML = item.dataset.dropcap
          ? '<img class="os-drawer-feature__dropcap-img" src="' + item.dataset.dropcap + '" alt="">'
          : dropcap._osDefault;
      }
    };

    window.clearTimeout(panel._osSwapT);
    if (!animate) {
      panel.classList.remove('is-swapping');
      doSwap();
      return;
    }

    // Fade current content out, swap while invisible, then fade the new content back in.
    panel.classList.add('is-swapping');
    panel._osSwapT = window.setTimeout(function () {
      doSwap();
      requestAnimationFrame(function () { panel.classList.remove('is-swapping'); });
    }, 500); // matches the .5s opacity transition in os-custom.css
  }

  // Does this level have a Tier-1 collection (a non-secondary parent that opens a deeper tier)?
  // If so the feature panel starts collapsed and EMERGES when a collection is clicked. A flat
  // level (e.g. About, no deeper tiers) just shows its own feature panel.
  function hasCollections(scope) {
    return !!scope.querySelector(
      '.os-drawer-col[data-tier="1"] .os-drawer-col__row:not(.os-drawer-col__row--secondary) .os-drawer-col__item--parent'
    );
  }

  // Click a parent → open/close its column. A Tier-1 collection click emerges + updates the
  // feature panel in situ; "Shop by concern/brand" collapse it so Tier 1 sits left-aligned.
  drawer.addEventListener('click', function (event) {
    var btn = event.target.closest('.os-drawer-col__item--parent');
    if (!btn || !drawer.contains(btn)) return;

    var scope = btn.closest('.os-shop-drawer');
    var target = document.getElementById(btn.dataset.opens);
    if (!scope || !target) return;

    var tier = tierOf(target);
    var isOpen = target.classList.contains('is-open') && btn.getAttribute('aria-expanded') === 'true';
    var secondary = !!btn.closest('.os-drawer-col__row--secondary');

    collapseFrom(scope, tier);

    // Single-collection drawers (e.g. standalone "Beauty") keep a fixed hero for that
    // collection - opening a sub-column never collapses or swaps the feature panel.
    var staticFeature = scope.classList.contains('os-shop-drawer--static-feature');

    if (!isOpen) {
      target.classList.add('is-open');
      target.removeAttribute('aria-hidden');
      btn.setAttribute('aria-expanded', 'true');
      btn.classList.add('is-active');

      if (secondary) {
        scope.classList.add('os-shop-drawer--no-feature');          // concern/brand: no promo
      } else if (tier === 2 && !staticFeature) {
        // Animate the swap only when the panel is ALREADY open (switching collections); on first
        // emerge from collapsed, set instantly and let the panel's opacity fade-in cover it.
        var wasVisible = !scope.classList.contains('os-shop-drawer--no-feature');
        scope.classList.remove('os-shop-drawer--no-feature');        // a Tier-1 collection → emerge
        applyFeature(scope, btn, wasVisible);
      }
    } else if (hasCollections(scope) && !staticFeature) {
      scope.classList.add('os-shop-drawer--no-feature');             // toggled closed → tuck promo away
    }
  });

  // Flat levels (no collection parents - e.g. About referencing pages) preview each leaf's
  // feature on hover, INSTANTLY (no fade), since clicking a leaf navigates away. Levels with
  // collection parents stay click-driven (no hover), which avoids the earlier flashing.
  drawer.addEventListener('mouseover', function (event) {
    var item = event.target.closest('.os-drawer-col[data-tier="1"] .os-drawer-col__item');
    if (!item || !drawer.contains(item)) return;
    if (item.closest('.os-drawer-col__row--secondary')) return;
    var scope = item.closest('.os-shop-drawer');
    if (!scope || hasCollections(scope)) return; // hover only on flat levels
    applyFeature(scope, item);
  });

  // Seed/collapse each level's feature panel for the given state.
  function seedFeature(scope) {
    // Static single-collection levels keep their server-rendered hero image, always visible.
    if (scope.classList.contains('os-shop-drawer--static-feature')) {
      scope.classList.remove('os-shop-drawer--no-feature');
      return;
    }
    var collapsed = hasCollections(scope);
    scope.classList.toggle('os-shop-drawer--no-feature', collapsed);
    if (!collapsed) {
      var first = scope.querySelector(
        '.os-drawer-col[data-tier="1"] .os-drawer-col__row:not(.os-drawer-col__row--secondary) .os-drawer-col__item'
      );
      if (first) applyFeature(scope, first); // flat level → show its first item's feature
    }
  }

  // Show one level + reset its cascade; hide the others.
  function showLevel(pos) {
    drawer.querySelectorAll('.menu-drawer__dropdown-sidebar-level').forEach(function (lvl) {
      lvl.classList.toggle('is-visible', lvl.getAttribute('data-level-position') === String(pos));
    });
    var scope = drawer.querySelector('.menu-drawer__dropdown-sidebar-level.is-visible .os-shop-drawer');
    if (scope) { collapseFrom(scope, 2); seedFeature(scope); }
  }

  // Clicking a DIFFERENT top-level link while the drawer is open switches mega-menus in place
  // (closes the current level, opens the other) instead of toggling the drawer shut.
  document.addEventListener('click', function (event) {
    var link = event.target.closest('.header__primary-nav [data-link-position][aria-controls="menu-dropdown-sidebar"]');
    if (!link || !drawer.open) return;
    var current = drawer.querySelector('.menu-drawer__dropdown-sidebar-level.is-visible');
    var pos = link.getAttribute('data-link-position');
    if (current && current.getAttribute('data-level-position') === pos) return; // same → let theme close
    event.preventDefault();
    event.stopImmediatePropagation();
    showLevel(pos);
    document.querySelectorAll('.header__primary-nav [data-link-position][aria-controls="menu-dropdown-sidebar"]').forEach(function (l) {
      l.setAttribute('aria-expanded', l === link ? 'true' : 'false');
    });
  }, true);

  // On open, collapse the feature panel for levels that drive it from collection clicks;
  // leave it shown (seeded) for flat levels.
  drawer.addEventListener('dialog:before-show', function () {
    drawer.querySelectorAll('.os-shop-drawer').forEach(seedFeature);
  });

  // Reset each level to its first column whenever the drawer closes.
  drawer.addEventListener('dialog:after-hide', function () {
    drawer.querySelectorAll('.os-shop-drawer').forEach(function (scope) {
      collapseFrom(scope, 2);
      seedFeature(scope);
    });
  });
})();

/* ------ Footer: move social icons under the VISIT US column (Figma 332:7401) ------ */
/* The theme renders social icons inside the footer aside (hidden in os-custom.css so the
   legal row can live in the black zone). The design places them under "Visit us", so we
   relocate the existing <ul.social-media> into that column. */
(function () {
  function relocateSocial() {
    var footer = document.querySelector('.shopify-section--footer');
    if (!footer) return;
    var social = footer.querySelector('.footer__aside .social-media');
    if (!social) return;

    var target = null;
    footer.querySelectorAll('.footer__block--text').forEach(function (block) {
      if (target) return;
      var head = block.querySelector('.subheading');
      if (head && /visit/i.test(head.textContent)) {
        target = block.querySelector('.v-stack') || block;
      }
    });
    if (!target) target = footer.querySelector('.footer__block--text .v-stack');
    if (target) target.appendChild(social);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', relocateSocial);
  } else {
    relocateSocial();
  }
})();

/* ============================================================================
   <os-purchase-options> - One-time vs Auto-replenish (native selling plans)
   Toggles the hidden `selling_plan` field on the product form and keeps the
   subscription price + frequency in sync. Lives outside the <form> and submits
   to it via the HTML5 form= attribute, so no form re-render is triggered.
   ============================================================================ */
class OsPurchaseOptions extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-selling-plan-input]');
    this.frequency = this.querySelector('[data-frequency]');
    this.subPriceEl = this.querySelector('[data-sub-price]');
    this.subCompareEl = this.querySelector('[data-sub-compare]');
    this.saveEl = this.querySelector('[data-sub-save]');
    this.onetimePriceEls = Array.prototype.slice.call(this.querySelectorAll('[data-onetime-price]'));
    this.modes = Array.prototype.slice.call(this.querySelectorAll('[data-purchase-mode]'));
    this.pill = this.querySelector('.os-po-pill');
    this.pillFreq = this.querySelector('.os-po-pill__freq');
    this.pillDivider = this.querySelector('.os-po-pill__divider');

    // Per-variant price map (one-time price + selling-plan allocations), embedded server-side.
    this.prices = {};
    const priceScript = this.querySelector('[data-variant-prices]');
    if (priceScript) {
      try { this.prices = JSON.parse(priceScript.textContent) || {}; } catch (e) { this.prices = {}; }
    }

    this.onChange = this.update.bind(this);
    this.modes.forEach((r) => r.addEventListener('change', this.onChange));
    if (this.frequency) this.frequency.addEventListener('change', this.onChange);

    // The native <variant-picker> dispatches `variant:change` on the product form. Since this
    // element lives OUTSIDE the form (it submits via the HTML5 form= attribute), we listen on
    // document for the bubbled event and refresh the displayed prices for the new variant.
    this.onVariantChange = (event) => {
      if (!event.detail || !event.detail.variant) return;
      // The event is dispatched on the product <form> (and bubbles to document). We can't trust
      // event.detail.formId: the theme sets it to `form.id`, which a named control (the hidden
      // <input name="id">) shadows, so it resolves to that input element, not the form id string.
      // Read the real id attribute off the target form, and ignore other forms (e.g. quick-buy).
      const form = event.target;
      if (form && form.getAttribute && form.getAttribute('id') !== this.dataset.form) return;
      this.applyVariant(String(event.detail.variant.id));
    };
    document.addEventListener('variant:change', this.onVariantChange);

    this.update();
  }

  disconnectedCallback() {
    this.modes.forEach((r) => r.removeEventListener('change', this.onChange));
    if (this.frequency) this.frequency.removeEventListener('change', this.onChange);
    document.removeEventListener('variant:change', this.onVariantChange);
  }

  // Refresh all price displays for the given variant id from the embedded map.
  applyVariant(variantId) {
    const data = this.prices[variantId];
    if (!data) return;

    if (data.price_f) {
      // Hide $0.00 for unpriced variants (client request): show the price only when it is
      // a real, positive amount, matching the server-rendered gate.
      const onetimeText = data.price > 0 ? data.price_f : '';
      this.onetimePriceEls.forEach((el) => { el.textContent = onetimeText; });
    }

    // Update each frequency option's price data for the new variant, then recompute.
    if (this.frequency && data.plans) {
      Array.prototype.forEach.call(this.frequency.options, (opt) => {
        const plan = data.plans[opt.value];
        if (!plan) return;
        if (plan.price_f) opt.dataset.price = plan.price_f;
        if (plan.compare_f) opt.dataset.compare = plan.compare_f;
        opt.dataset.priceRaw = plan.price;
        opt.dataset.compareRaw = plan.compare;
      });
    }

    this.update();
  }

  update() {
    const checked = this.querySelector('[data-purchase-mode]:checked');
    const value = checked ? checked.value : 'onetime';
    const isSub = value === 'subscription';

    this.querySelectorAll('[data-mode]').forEach((el) => {
      el.classList.toggle('is-selected', el.dataset.mode === value);
    });

    // Frequency only belongs to Auto-replenish - hide it (and collapse the pill to
    // qty-only) for One-time purchase.
    if (this.pill) this.pill.classList.toggle('os-po-pill--qty-only', !isSub);
    if (this.pillFreq) this.pillFreq.hidden = !isSub;
    if (this.pillDivider) this.pillDivider.hidden = !isSub;

    // Refresh the Subscribe row's displayed price from the selected frequency on EVERY update,
    // not only while Subscribe is the active mode. Otherwise a variant change made with One-time
    // selected (including the 3-day auto-preselect on load) leaves the subscription price/compare/
    // save showing the previous variant's figure while the one-time price correctly updates.
    if (this.frequency) {
      const opt = this.frequency.selectedOptions[0];
      if (opt) {
        if (this.subPriceEl && opt.dataset.price) this.subPriceEl.textContent = opt.dataset.price;

        const priceRaw = parseInt(opt.dataset.priceRaw || '0', 10);
        const compareRaw = parseInt(opt.dataset.compareRaw || '0', 10);
        const hasDiscount = compareRaw > priceRaw;

        if (this.subCompareEl) {
          this.subCompareEl.hidden = !hasDiscount;
          if (hasDiscount && opt.dataset.compare) this.subCompareEl.textContent = opt.dataset.compare;
        }
        if (this.saveEl) {
          this.saveEl.hidden = !hasDiscount;
          if (hasDiscount) {
            this.saveEl.textContent = 'Save ' + Math.round(((compareRaw - priceRaw) / compareRaw) * 100) + '%';
          }
        }
      }
    }

    // The submitted selling_plan depends on the active mode only ("" = one-time purchase).
    if (this.input) {
      this.input.value = (isSub && this.frequency) ? this.frequency.value : '';
    }
  }
}

if (!window.customElements.get('os-purchase-options')) {
  window.customElements.define('os-purchase-options', OsPurchaseOptions);
}

/* ============================================================================
   <os-sub-benefits> - subscription-benefits popup (modal display mode of the
   purchase-options block, snippet os-pdp-purchase-options). The merchant can
   show the benefits content either as a hover tooltip on the Save badge
   (handled purely in CSS) or, in modal mode, as a link that opens this popup.
   The trigger link + the <dialog> (reusing the shared .os-bis-modal shell) both
   live inside this element, so this controller just wires open / close. A native
   <dialog>.showModal() gives the focus-trap, Escape-to-close and top-layer
   backdrop for free (same as the BIS / notify / calendar modals).
============================================================================ */
(function () {
  class OsSubBenefits extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-osb-dialog]');
      this.trigger = this.querySelector('[data-osb-open]');
      this.closeBtn = this.querySelector('[data-osb-close]');
      if (!this.dialog || !this.trigger) return;

      this.onOpen = function (e) { e.preventDefault(); this.show(); }.bind(this);
      this.trigger.addEventListener('click', this.onOpen);

      if (this.closeBtn) {
        this.onClose = function () { this.dialog.close(); }.bind(this);
        this.closeBtn.addEventListener('click', this.onClose);
      }

      // Close when the backdrop (the dialog itself, outside .os-bis-modal__inner) is clicked.
      this.onDialogClick = function (e) {
        if (e.target === this.dialog) this.dialog.close();
      }.bind(this);
      this.dialog.addEventListener('click', this.onDialogClick);
    }

    disconnectedCallback() {
      if (this.trigger && this.onOpen) this.trigger.removeEventListener('click', this.onOpen);
      if (this.closeBtn && this.onClose) this.closeBtn.removeEventListener('click', this.onClose);
      if (this.dialog && this.onDialogClick) this.dialog.removeEventListener('click', this.onDialogClick);
    }

    show() {
      if (typeof this.dialog.showModal === 'function') {
        try { this.dialog.showModal(); } catch (e) { this.dialog.setAttribute('open', ''); }
      } else {
        this.dialog.setAttribute('open', '');
      }
    }
  }

  if (!window.customElements.get('os-sub-benefits')) {
    window.customElements.define('os-sub-benefits', OsSubBenefits);
  }
})();

/* ============================================================================
   <os-delivery-gate> - PDP "do we deliver to your postcode?" serviceability
   check for juice / cleanse products. Keeps the product's Add to cart locked
   (via the `os-atc-locked` class on the sibling <buy-buttons>, plus a capture-
   phase submit guard) until the shopper enters a serviceable postcode. The
   serviceable list is embedded as `data-postcodes` from settings.deliverable_
   postcodes. A confirmed postcode is remembered (localStorage) so other gated
   products don't re-ask, and written to a cart attribute so fulfilment can see
   it on the order. Storefront UX only - real delivery-area enforcement happens
   at checkout (Shopify Local Delivery / shipping profiles). Fails closed.
   ============================================================================ */
(function () {
  var STORE_KEY = 'os:delivery-postcode';
  var DATE_KEY = 'os:delivery-date';

  class OsDeliveryGate extends HTMLElement {
    connectedCallback() {
      this.formId = this.dataset.form || '';
      this.input = this.querySelector('[data-postcode-input]');
      this.checkBtn = this.querySelector('[data-postcode-check]');
      this.result = this.querySelector('[data-postcode-result]');

      var tpl = this.querySelector('[data-postcode-unavailable]');
      this.unavailableHtml = tpl ? tpl.innerHTML : '';
      this.confirmTpl = this.dataset.confirm || 'Great - we deliver to {postcode}.';
      this.invalidMsg = this.dataset.invalid || 'Please enter a 4-digit postcode.';

      // Delivery schedule (all evaluated in Australia/Sydney time, see nextDeliveryDate).
      this.deliveryDays = (this.dataset.deliveryDays || '').split(',')
        .map(function (n) { return parseInt(n, 10); })
        .filter(function (n) { return n >= 0 && n <= 6; });
      this.leadDays = parseInt(this.dataset.leadDays, 10);
      if (isNaN(this.leadDays)) this.leadDays = 1;
      this.blocked = {};
      (this.dataset.blocked || '').split(/[\s,]+/).forEach(function (d) {
        if (/^\d{4}-\d{2}-\d{2}$/.test(d)) { this.blocked[d] = true; }
      }, this);
      this.noDateText = 'soon';

      // Build the serviceable-postcode lookup from the embedded list (any non-digit separator).
      this.postcodes = {};
      (this.dataset.postcodes || '').split(/[^0-9]+/).forEach(function (p) {
        if (p) { this.postcodes[p] = true; }
      }, this);

      // Delivery-date calendar. Active only when at least one delivery weekday is configured; if
      // none is set the gate falls back to postcode-only (graceful before the schedule is filled in).
      this.calendarActive = this.deliveryDays.length > 0;
      this.calWrap = this.querySelector('[data-delivery-calendar]');
      this.calGrid = this.querySelector('[data-cal-grid]');
      this.calMonth = this.querySelector('[data-cal-month]');
      this.calPrev = this.querySelector('[data-cal-prev]');
      this.calNext = this.querySelector('[data-cal-next]');
      this.calSummary = this.querySelector('[data-cal-summary]');
      this.calRecurring = this.querySelector('[data-cal-recurring]');
      this.dateSummaryTpl = this.dataset.dateSummary || 'Delivering {date}';
      this.recurringTpl = this.dataset.recurringNote || '';
      this.selectedIso = '';
      this.calInited = false;

      // Two independent gates - Add to cart unlocks only when BOTH are satisfied. When the calendar
      // is inactive, the date gate is pre-satisfied so postcode alone unlocks (as in Phase 1).
      this.postcodeOk = false;
      this.dateOk = !this.calendarActive;

      this.locked = true;

      this.onCheck = this.check.bind(this);
      if (this.checkBtn) this.checkBtn.addEventListener('click', this.onCheck);
      if (this.input) {
        this.onKey = function (e) { if (e.key === 'Enter') { e.preventDefault(); this.check(); } }.bind(this);
        this.input.addEventListener('keydown', this.onKey);
      }

      // Calendar nav + delegated day selection.
      if (this.calPrev) { this.onPrev = function () { this.shiftMonth(-1); }.bind(this); this.calPrev.addEventListener('click', this.onPrev); }
      if (this.calNext) { this.onNext = function () { this.shiftMonth(1); }.bind(this); this.calNext.addEventListener('click', this.onNext); }
      if (this.calGrid) {
        this.onGrid = function (e) {
          var btn = e.target.closest('[data-day]');
          if (btn && this.calGrid.contains(btn)) { this.selectDate(btn.getAttribute('data-day')); }
        }.bind(this);
        this.calGrid.addEventListener('click', this.onGrid);
      }

      // Re-evaluate the subscription note whenever the purchase mode / frequency changes.
      this.onModeChange = function (e) {
        var t = e.target;
        if (t && (t.matches('[data-purchase-mode]') || t.matches('[data-frequency]'))) { this.updateRecurringNote(); }
      }.bind(this);
      document.addEventListener('change', this.onModeChange);

      // The theme re-renders the buy area on variant change - re-resolve it and re-apply our state.
      this.onVariant = function (e) {
        var f = e.target;
        if (!f || !f.getAttribute || f.getAttribute('id') !== this.formId) return;
        if (this.form) this.buyButtons = this.form.querySelector('buy-buttons');
        this.hideDynamicCheckout();
        this.applyProperty();
        this.refreshLock();
      }.bind(this);
      document.addEventListener('variant:change', this.onVariant);

      // The Buy buttons block may parse after this one (depending on block order), so bind to
      // the product form once the DOM is ready.
      if (document.readyState === 'loading') {
        this.onReady = this.bindForm.bind(this);
        document.addEventListener('DOMContentLoaded', this.onReady);
      } else {
        this.bindForm();
      }
    }

    disconnectedCallback() {
      if (this.checkBtn) this.checkBtn.removeEventListener('click', this.onCheck);
      if (this.input && this.onKey) this.input.removeEventListener('keydown', this.onKey);
      if (this.onReady) document.removeEventListener('DOMContentLoaded', this.onReady);
      if (this.form && this.onSubmit) this.form.removeEventListener('submit', this.onSubmit, true);
      if (this.calPrev && this.onPrev) this.calPrev.removeEventListener('click', this.onPrev);
      if (this.calNext && this.onNext) this.calNext.removeEventListener('click', this.onNext);
      if (this.calGrid && this.onGrid) this.calGrid.removeEventListener('click', this.onGrid);
      if (this.onModeChange) document.removeEventListener('change', this.onModeChange);
      if (this.onVariant) document.removeEventListener('variant:change', this.onVariant);
    }

    bindForm() {
      this.form = this.formId ? document.getElementById(this.formId) : null;
      this.buyButtons = this.form ? this.form.querySelector('buy-buttons') : null;

      // Block submission (click, Enter, programmatic) while locked; capture phase so this
      // runs before the theme's <product-form> submit handler.
      if (this.form) {
        this.onSubmit = function (e) {
          if (this.locked) { e.preventDefault(); e.stopImmediatePropagation(); this.nudge(); }
        }.bind(this);
        this.form.addEventListener('submit', this.onSubmit, true);
      }

      // A delivery date rides along as a line-item property; the express (Shop Pay) button bypasses
      // the product form, so it would drop the date. Hide it on gated products - standard Add to
      // cart is the only path that carries the chosen date.
      this.hideDynamicCheckout();

      // Fail closed: lock now, then auto-pass a remembered serviceable postcode.
      this.lock();
      var saved = null;
      try { saved = window.localStorage.getItem(STORE_KEY); } catch (e) {}
      if (saved && this.postcodes[saved]) {
        if (this.input) this.input.value = saved;
        this.pass(saved);
      }
    }

    hideDynamicCheckout() {
      if (this.buyButtons) this.buyButtons.classList.add('os-no-dynamic-checkout');
    }

    normalise(v) { return String(v || '').replace(/\D/g, '').slice(0, 4); }

    check() {
      var pc = this.normalise(this.input ? this.input.value : '');
      if (this.input) this.input.value = pc;
      if (pc.length < 4) { this.showResult('invalid', this.invalidMsg, false); return; }
      if (this.postcodes[pc]) { this.pass(pc); } else { this.blockPostcode(); }
    }

    pass(pc) {
      this.postcodeOk = true;
      try { window.localStorage.setItem(STORE_KEY, pc); } catch (e) {}
      this.setCartAttribute(pc);
      var msg = this.confirmTpl.split('{postcode}').join(pc);
      if (msg.indexOf('{delivery}') !== -1) {
        var d = this.nextDeliveryDate();
        msg = msg.split('{delivery}').join(d ? this.formatDeliveryDate(d) : this.noDateText);
      }
      this.showResult('ok', msg, false);

      // Reveal the delivery-date calendar and carry through any date chosen earlier.
      if (this.calendarActive && this.calWrap) {
        this.calWrap.hidden = false;
        this.initCalendar();
        this.restoreSavedDate();
      }
      this.refreshLock();
    }

    refreshLock() {
      if (this.postcodeOk && this.dateOk) { this.unlock(); } else { this.lock(); }
    }

    // Today's calendar date in Australia/Sydney (independent of the shopper's device timezone).
    sydneyToday() {
      try {
        var parts = new Intl.DateTimeFormat('en-CA', {
          timeZone: 'Australia/Sydney', year: 'numeric', month: '2-digit', day: '2-digit'
        }).formatToParts(new Date());
        var o = {};
        parts.forEach(function (p) { if (p.type !== 'literal') o[p.type] = parseInt(p.value, 10); });
        if (o.year && o.month && o.day) return { y: o.year, m: o.month, d: o.day };
      } catch (e) {}
      var now = new Date();
      return { y: now.getFullYear(), m: now.getMonth() + 1, d: now.getDate() };
    }

    pad2(n) { return (n < 10 ? '0' : '') + n; }

    // The next serviceable delivery date: the soonest enabled weekday at least `leadDays` away
    // (the midnight-before cut-off) that isn't a blocked date. null if none within ~4 months.
    nextDeliveryDate() {
      if (!this.deliveryDays || !this.deliveryDays.length) return null;
      var t = this.sydneyToday();
      for (var i = this.leadDays; i <= this.leadDays + 120; i++) {
        var c = new Date(t.y, t.m - 1, t.d + i);
        if (this.deliveryDays.indexOf(c.getDay()) === -1) continue;
        var iso = c.getFullYear() + '-' + this.pad2(c.getMonth() + 1) + '-' + this.pad2(c.getDate());
        if (this.blocked[iso]) continue;
        return c;
      }
      return null;
    }

    formatDeliveryDate(c) {
      var days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      var months = ['January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'];
      return days[c.getDay()] + ' ' + c.getDate() + ' ' + months[c.getMonth()];
    }

    /* ---- Delivery-date calendar --------------------------------------------- */

    monthIndex(y, m) { return y * 12 + (m - 1); } // m is 1-12

    initCalendar() {
      if (this.calInited) return;
      this.calInited = true;
      var t = this.sydneyToday();
      // Earliest selectable = today + lead (the midnight-before cut-off); window ~4 months ahead.
      this.minDate = new Date(t.y, t.m - 1, t.d + this.leadDays);
      this.maxDate = new Date(t.y, t.m - 1, t.d + 120);
      this.minMonth = this.monthIndex(this.minDate.getFullYear(), this.minDate.getMonth() + 1);
      this.maxMonth = this.monthIndex(this.maxDate.getFullYear(), this.maxDate.getMonth() + 1);
      // Open on the month of the first available date (fall back to the earliest selectable month).
      var first = this.nextDeliveryDate() || this.minDate;
      this.viewYear = first.getFullYear();
      this.viewMonth = first.getMonth() + 1; // 1-12
      this.renderCalendar();
    }

    shiftMonth(delta) {
      var idx = this.monthIndex(this.viewYear, this.viewMonth) + delta;
      if (idx < this.minMonth || idx > this.maxMonth) return;
      this.viewYear = Math.floor(idx / 12);
      this.viewMonth = (idx % 12) + 1;
      this.renderCalendar();
    }

    isoOf(date) {
      return date.getFullYear() + '-' + this.pad2(date.getMonth() + 1) + '-' + this.pad2(date.getDate());
    }

    dateFromIso(iso) {
      var p = iso.split('-');
      return new Date(parseInt(p[0], 10), parseInt(p[1], 10) - 1, parseInt(p[2], 10));
    }

    // Selectable if it's an enabled weekday, within [min, max], and not a blocked date.
    isAvailableDate(date) {
      if (this.deliveryDays.indexOf(date.getDay()) === -1) return false;
      if (date.getTime() < this.minDate.getTime() || date.getTime() > this.maxDate.getTime()) return false;
      if (this.blocked[this.isoOf(date)]) return false;
      return true;
    }

    // "Sold out" = a date the merchant has blocked that would OTHERWISE be a valid delivery day
    // (enabled weekday, inside the booking window). These get the info "i" + "Sold out" tooltip;
    // plain greyed days (weekends, past the cut-off) do not - they aren't sold out, just unavailable.
    isSoldOutDate(date) {
      if (!this.blocked[this.isoOf(date)]) return false;
      if (this.deliveryDays.indexOf(date.getDay()) === -1) return false;
      return date.getTime() >= this.minDate.getTime() && date.getTime() <= this.maxDate.getTime();
    }

    renderCalendar() {
      if (!this.calGrid) return;
      var months = ['January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'];
      if (this.calMonth) this.calMonth.textContent = months[this.viewMonth - 1] + ' ' + this.viewYear;
      var curIdx = this.monthIndex(this.viewYear, this.viewMonth);
      if (this.calPrev) this.calPrev.disabled = curIdx <= this.minMonth;
      if (this.calNext) this.calNext.disabled = curIdx >= this.maxMonth;

      var html = '';
      var wk = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']; // Monday-first (AU)
      for (var h = 0; h < 7; h++) { html += '<span class="os-delivery-cal__dow">' + wk[h] + '</span>'; }

      var first = new Date(this.viewYear, this.viewMonth - 1, 1);
      var lead = (first.getDay() + 6) % 7; // blank cells before day 1, Monday-first
      for (var b = 0; b < lead; b++) { html += '<span class="os-delivery-cal__cell os-delivery-cal__cell--blank"></span>'; }

      var daysInMonth = new Date(this.viewYear, this.viewMonth, 0).getDate();
      for (var d = 1; d <= daysInMonth; d++) {
        var date = new Date(this.viewYear, this.viewMonth - 1, d);
        if (this.isAvailableDate(date)) {
          var iso = this.isoOf(date);
          var sel = iso === this.selectedIso;
          html += '<button type="button" class="os-delivery-cal__cell os-delivery-cal__day' +
            (sel ? ' is-selected' : '') + '" data-day="' + iso + '" aria-pressed="' + (sel ? 'true' : 'false') + '">' +
            d + '</button>';
        } else if (this.isSoldOutDate(date)) {
          html += osCalSoldOutCell(d);
        } else {
          html += '<span class="os-delivery-cal__cell os-delivery-cal__day is-disabled" aria-disabled="true">' + d + '</span>';
        }
      }
      this.calGrid.innerHTML = html;
    }

    selectDate(iso) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return;
      this.selectedIso = iso;
      try { window.localStorage.setItem(DATE_KEY, iso); } catch (e) {}
      this.dateOk = true;
      this.applyProperty();
      this.renderCalendar();
      this.updateSummary();
      this.updateRecurringNote();
      this.refreshLock();
    }

    // Carry a date chosen on another cleanse product through to this one, unless it's no longer
    // available (past the cut-off, blocked, or a now-disabled weekday) - then clear it and re-ask.
    restoreSavedDate() {
      var saved = null;
      try { saved = window.localStorage.getItem(DATE_KEY); } catch (e) {}
      if (!saved || !/^\d{4}-\d{2}-\d{2}$/.test(saved)) return;
      var date = this.dateFromIso(saved);
      if (!this.isAvailableDate(date)) {
        try { window.localStorage.removeItem(DATE_KEY); } catch (e) {}
        return;
      }
      this.selectedIso = saved;
      this.viewYear = date.getFullYear();
      this.viewMonth = date.getMonth() + 1;
      this.dateOk = true;
      this.applyProperty();
      this.renderCalendar();
      this.updateSummary();
      this.updateRecurringNote();
    }

    // Inject / refresh the visible `Delivery date` line-item property on the product form.
    applyProperty() {
      if (!this.form) return;
      var input = this.form.querySelector('[data-os-delivery-prop]');
      if (!this.selectedIso) {
        if (input && input.parentNode) input.parentNode.removeChild(input);
        return;
      }
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'properties[Delivery date]';
        input.setAttribute('data-os-delivery-prop', '');
        this.form.appendChild(input);
      }
      input.value = this.formatFull(this.dateFromIso(this.selectedIso));
    }

    updateSummary() {
      if (!this.calSummary) return;
      if (!this.selectedIso) { this.calSummary.hidden = true; return; }
      this.calSummary.hidden = false;
      this.calSummary.textContent = this.dateSummaryTpl.split('{date}').join(this.formatFull(this.dateFromIso(this.selectedIso)));
    }

    updateRecurringNote() {
      if (!this.calRecurring) return;
      if (this.selectedIso && this.recurringTpl && this.isSubscription()) {
        var wd = this.formatFull(this.dateFromIso(this.selectedIso)).split(' ')[0];
        this.calRecurring.hidden = false;
        this.calRecurring.textContent = this.recurringTpl.split('{weekday}').join(wd);
      } else {
        this.calRecurring.hidden = true;
      }
    }

    // Is a subscription (auto-replenish) selling plan currently selected on this product?
    isSubscription() {
      var po = document.querySelector('os-purchase-options');
      if (po) {
        var checked = po.querySelector('[data-purchase-mode]:checked');
        if (checked) return checked.value === 'subscription';
      }
      var sp = (this.form && this.form.querySelector('[name="selling_plan"]')) ||
        document.querySelector('os-purchase-options [data-selling-plan-input]');
      return !!(sp && sp.value);
    }

    formatFull(date) {
      var days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      var months = ['January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'];
      return days[date.getDay()] + ' ' + date.getDate() + ' ' + months[date.getMonth()] + ' ' + date.getFullYear();
    }

    blockPostcode() {
      this.postcodeOk = false;
      try { window.localStorage.removeItem(STORE_KEY); } catch (e) {}
      this.showResult('no', this.unavailableHtml, true);
      if (this.calWrap) this.calWrap.hidden = true;
      this.refreshLock();
    }

    lock() {
      this.locked = true;
      this.classList.remove('is-confirmed');
      this.classList.add('is-locked');
      if (this.buyButtons) this.buyButtons.classList.add('os-atc-locked');
      var btn = this.form && this.form.querySelector('[type="submit"]');
      if (btn) btn.setAttribute('aria-disabled', 'true');
    }

    unlock() {
      this.locked = false;
      this.classList.remove('is-locked');
      this.classList.add('is-confirmed');
      if (this.buyButtons) this.buyButtons.classList.remove('os-atc-locked');
      var btn = this.form && this.form.querySelector('[type="submit"]');
      if (btn) btn.removeAttribute('aria-disabled');
    }

    // Flash the field to signal a postcode must be checked first.
    nudge() {
      this.classList.remove('os-delivery-gate--nudge');
      void this.offsetWidth; // restart the animation
      this.classList.add('os-delivery-gate--nudge');
      if (this.input) { try { this.input.focus(); } catch (e) {} }
    }

    showResult(state, content, isHtml) {
      if (!this.result) return;
      this.result.hidden = false;
      this.result.className = 'os-delivery-gate__result os-delivery-gate__result--' + state;
      if (isHtml) { this.result.innerHTML = content; } else { this.result.textContent = content; }
    }

    // Record the confirmed postcode on the cart so fulfilment can see it on the order.
    setCartAttribute(pc) {
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      try {
        fetch(root + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ attributes: { 'Delivery postcode': pc } })
        });
      } catch (e) {}
    }
  }

  if (!window.customElements.get('os-delivery-gate')) {
    window.customElements.define('os-delivery-gate', OsDeliveryGate);
  }
})();

/* ============================================================================
   Cleanse delivery-date: order-attribute model.

   The shopper picks ONE delivery date for their whole order, and it is stored as the
   order attribute "Cleanse delivery date" (plus a machine-readable "_cleanse_delivery_iso").
   The cleanse begins the next morning; each cleanse LINE keeps its own delivery date(s),
   start date, boxes and bags - derived from that single shared delivery date and the line's
   length (1 / 3 / 5 day) - as line-item properties, so a basket can hold cleanses of
   different lengths that all share one delivery date. The date maths is the pure engine in
   os-cleanse-schedule.js (semantics-neutral: it just decides which calendar dates are
   pickable and derives dates at fixed offsets from a base - here the base is the delivery).

   Three custom elements + one shared helper live here:
     - OSCleanseCart (helper) - reads the cart, writes the order attribute, and
       recomputes every cleanse line when the shared delivery date changes.
     - <os-cleanse-gate> (PDP) - the delivery-only postcode gate. Postcode must clear
       before Add to cart (these items are delivery-only). On the FIRST add of a gated
       cleanse it opens the shared date modal; once a date is set it is not re-asked.
     - <os-cleanse-date-modal> - the shared delivery-date calendar (snippet
       os-cleanse-date-modal), opened by the gate and by the cart's "Change date".
     - <os-cart-cleanse-date> - the cart-drawer banner + "Change date" button
       (snippet os-cart-cleanse-date), which re-opens the modal for the whole order.
   Reuses the .os-delivery-gate* / .os-delivery-cal* / .os-bis-modal* styling. Fails closed.
   ============================================================================ */
(function () {
  var PC_KEY = 'os:delivery-postcode';    // shared with <os-delivery-gate>
  var DATE_KEY = 'os:cleanse-delivery';   // fast client cache of the chosen delivery date (ISO)
  var DATE_ATTR = 'Cleanse delivery date';
  var DATE_ISO_ATTR = '_cleanse_delivery_iso';
  var WINDOW_DAYS = 120;

  function engine() { return (typeof window !== 'undefined' && window.OSCleanseSchedule) || null; }
  function cartRoot() { return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'; }
  function savedDate() { try { return window.localStorage.getItem(DATE_KEY); } catch (e) { return null; } }

  // Order attributes for a chosen delivery date: the human line + a machine-readable ISO twin.
  function attrsFor(delivery) {
    var S = engine(), a = {};
    a[DATE_ATTR] = S.formatFull(delivery);
    a[DATE_ISO_ATTR] = S.iso(delivery);
    return a;
  }

  /* ---- OSCleanseCart: cart reads / writes shared by all three elements ---------- */
  var OSCleanseCart = {
    root: cartRoot,

    fetchCart: function () {
      return fetch(cartRoot() + 'cart.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); });
    },

    // Merge order attributes; optionally ask the drawer which sections to re-render.
    setAttributes: function (attrs, sections) {
      var body = { attributes: attrs };
      if (sections && sections.length) { body.sections = sections.join(','); }
      return fetch(cartRoot() + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      });
    },

    // The cart-drawer section id(s) to bundle into a cart response so it re-renders.
    bundledSections: function () {
      var set = new Set();
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: set } })
      );
      return Array.from(set);
    },

    // Is this cart line a gated cleanse line (was stamped by the gate / modal)?
    isCleanseLine: function (item) {
      var p = (item && item.properties) || {};
      return !!(p._cleanse_offsets || p._cleanse_start);
    },

    // A line's properties, with the dated fields moved to a new delivery ISO. Boxes and bags are
    // unchanged (they depend on the cleanse length, not the date); everything else is preserved.
    // `iso` is the first delivery date; offsets are counted from it (0 = the first delivery) and
    // the cleanse start is that delivery plus the stored delivery-to-start gap (_cleanse_start_offset).
    recomputeProps: function (props, iso) {
      var S = engine();
      var base = S.fromIso(iso);
      var offsets = S.parseInts(props._cleanse_offsets); if (!offsets.length) { offsets = [0]; }
      var deliveries = offsets.map(function (o) { return S.addDays(base, o); });
      var startOffset = parseInt(props._cleanse_start_offset, 10); if (isNaN(startOffset)) { startOffset = 1; }
      var start = S.addDays(base, startOffset);
      var out = {};
      Object.keys(props).forEach(function (k) { out[k] = props[k]; });
      out['Delivery date'] = S.formatFull(deliveries[0]);
      out['Start date'] = S.formatFull(start);
      out._start_date = S.iso(start);
      out._cleanse_start = S.iso(start);
      out._delivery_1 = S.iso(deliveries[0]);
      out._cleanse_delivery_dow = String(S.dowOf(deliveries[0]));
      if (deliveries.length > 1) {
        out['Second delivery'] = S.formatFull(deliveries[1]);
        out._delivery_2 = S.iso(deliveries[1]);
      } else {
        delete out['Second delivery'];
        delete out._delivery_2;
      }
      return out;
    },

    // Stamp a gate-bypassed cleanse line with the FULL delivery-date property set and write it with
    // /cart/change.js. This is the cart-side equivalent of <os-cleanse-gate>.stampLine (which builds
    // hidden <input>s on the PDP product form): it produces the IDENTICAL property names and values,
    // so a cleanse set up from the cart is indistinguishable from one set up at the gate (same keys
    // the "Change date" recompute, Klaviyo / Flow, and the kitchen rely on). `built` is the resolved
    // schedule for this line's length (offsets / boxes / bags / delivery weekdays + the lead, notice
    // and cut-off), `iso` the chosen DELIVERY date, and `extras` the per-line broth or Mix & Match
    // selection. Existing line properties are preserved; the cleanse keys take precedence.
    stampLineProps: function (lineIndex, existingProps, iso, built, extras) {
      var S = engine(); if (!S) { return Promise.resolve(); }
      built = built || {};
      extras = extras || {};
      var base = S.fromIso(iso);                       // the chosen delivery date
      var offsets = (built.offsets && built.offsets.length) ? built.offsets : [0];
      var derived = S.deriveDeliveries(base, { offsets: offsets, boxes: built.boxes || [], bags: built.bags || 0 });
      var deliveryLead = (built.deliveryLead != null) ? built.deliveryLead : 1;
      var start = S.addDays(base, deliveryLead);        // the cleanse begins after the delivery
      var deliveries = derived.deliveries;
      var props = {};
      if (existingProps) { Object.keys(existingProps).forEach(function (k) { props[k] = existingProps[k]; }); }
      props['Delivery date'] = S.formatFull(deliveries[0]);
      props['Start date'] = S.formatFull(start);
      if (deliveries.length > 1) { props['Second delivery'] = S.formatFull(deliveries[1]); }
      else { delete props['Second delivery']; }
      if (derived.boxes.length) { props[built.boxesLabel || 'Boxes'] = derived.boxes.join(' + '); }
      if (derived.bags) { props['Free Cleanse Bag'] = 'Valued at $50'; props._cleanse_bags = String(derived.bags); }
      props._start_date = S.iso(start);
      props._cleanse_start = S.iso(start);
      deliveries.forEach(function (d, i) { props['_delivery_' + (i + 1)] = S.iso(d); });
      props._cleanse_days = String(built.days || '');
      props._cleanse_offsets = offsets.join(',');       // counted from the delivery
      props._cleanse_dows = (built.deliveryDows || []).join(',');
      props._cleanse_start_offset = String(deliveryLead);
      props._cleanse_lead = String(built.minNotice != null ? built.minNotice : 1);
      props._cleanse_cutoff = String(built.cutoff != null ? built.cutoff : 22);
      props._cleanse_delivery_dow = String(S.dowOf(deliveries[0]));
      if (extras.broth && extras.broth.value) { props[extras.broth.name || 'Broth'] = extras.broth.value; }
      if (extras.mm) {
        if (extras.mm.label) { props[extras.mm.label] = extras.mm.readable || ''; }
        props._mm_items = extras.mm.machine || '';
      }
      return fetch(cartRoot() + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ line: lineIndex, properties: props })
      });
    },

    // Move every cleanse line in the cart to a new delivery ISO. Sequential so the 1-based line
    // indexes stay valid across the property edits. Resolves when all lines are updated.
    recomputeLines: function (iso) {
      var S = engine(); if (!S) { return Promise.resolve(null); }
      var self = this;
      return this.fetchCart().then(function (cart) {
        var items = (cart && cart.items) || [];
        var jobs = [];
        items.forEach(function (it, i) {
          if (self.isCleanseLine(it)) { jobs.push({ line: i + 1, props: self.recomputeProps(it.properties, iso) }); }
        });
        var chain = Promise.resolve();
        jobs.forEach(function (job) {
          chain = chain.then(function () {
            return fetch(cartRoot() + 'cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ line: job.line, properties: job.props })
            });
          });
        });
        return chain;
      });
    },

    // Set the order-level delivery date + recompute existing cleanse lines. When `silent` (used by
    // the gate just before it adds a new item, which will re-render the drawer itself) it skips the
    // loading state + cart:change re-render; the cart "Change date" button uses the loud path.
    setDeliveryDate: function (iso, options) {
      options = options || {};
      var S = engine(); if (!S) { return Promise.resolve(); }
      var delivery = S.fromIso(iso), self = this;
      try { window.localStorage.setItem(DATE_KEY, iso); } catch (e) {}
      if (!options.silent) {
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
      }
      return this.recomputeLines(iso)
        .then(function () {
          return self.setAttributes(attrsFor(delivery), options.silent ? null : self.bundledSections());
        })
        .then(function (res) {
          if (options.silent) { return null; }
          return (res && res.json) ? res.json() : self.fetchCart();
        })
        .then(function (cart) {
          if (!options.silent) {
            document.documentElement.dispatchEvent(
              new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'os-cleanse-date:change', cart: cart } })
            );
          }
          return cart;
        })
        .catch(function (e) { if (window.console) { console.error('[os-cleanse] setDeliveryDate failed', e); } })
        .then(function (cart) {
          if (!options.silent) {
            document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
          }
          return cart;
        });
    },

    // Block / unblock every checkout control. Best-effort at the theme layer: it covers shoppers who
    // return through the storefront and open their cart. Carts that resume straight into checkout
    // (Shopify recovery emails, the Shop app, cart permalinks) never run this code and are caught by
    // the server-side validation function. Re-applied every time a banner (re)connects, so it
    // self-heals across cart re-renders; the document flag lets CSS dim the disabled controls.
    //
    // Reason-keyed so independent guards can coexist without fighting: the stale-date banner
    // (<os-cart-cleanse-date>) uses reason 'stale', the setup banner (<os-cart-cleanse-setup>) uses
    // 'setup'. Checkout stays blocked while ANY reason is active; releasing one reason does not
    // release another. The default reason keeps the old boolean signature working for existing
    // callers. Pass a falsy `blocked` to release that reason.
    _blockReasons: {},
    blockCheckout: function (blocked, reason) {
      reason = reason || 'stale';
      if (blocked) { this._blockReasons[reason] = true; }
      else { delete this._blockReasons[reason]; }
      var any = false;
      for (var k in this._blockReasons) { if (this._blockReasons[k]) { any = true; break; } }
      // The standard checkout submit button (cart drawer + cart page) carries name="checkout".
      document.querySelectorAll('[name="checkout"]').forEach(function (el) {
        el.disabled = any;
        el.setAttribute('aria-disabled', any ? 'true' : 'false');
      });
      // Express / dynamic checkout buttons (Shop Pay, PayPal, Apple/Google Pay, Paypal express):
      // these POST straight to checkout, so make their containers inert too.
      document.querySelectorAll('.additional-checkout-buttons, .shopify-payment-button').forEach(function (el) {
        el.toggleAttribute('inert', any);
        el.style.opacity = any ? '0.4' : '';
        el.style.pointerEvents = any ? 'none' : '';
      });
      document.documentElement.toggleAttribute('data-os-cleanse-date-stale', any);
    },

    // Remove the order-level delivery date attributes + the client cache. Used when the last gated
    // cleanse leaves the cart, so no stale date rides an order that has no cleanse in it.
    clearDeliveryDate: function () {
      try { window.localStorage.removeItem(DATE_KEY); } catch (e) {}
      var attrs = {}, self = this;
      attrs[DATE_ATTR] = '';
      attrs[DATE_ISO_ATTR] = '';
      return this.setAttributes(attrs, this.bundledSections())
        .then(function (res) { return (res && res.json) ? res.json() : self.fetchCart(); })
        .then(function (cart) {
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'os-cleanse-date:cleared', cart: cart } })
          );
          return cart;
        })
        .catch(function (e) { if (window.console) { console.error('[os-cleanse] clearDeliveryDate failed', e); } });
    }
  };
  window.OSCleanseCart = OSCleanseCart;

  // If the cart carries a cleanse delivery date but no gated cleanse line is left (last one removed,
  // or the cart emptied), clear the attribute. Runs on every cart change; guarded so clearing (which
  // fires another cart:change) does not loop - once the attribute is gone the condition is false.
  (function () {
    var clearing = false;
    function check(cart) {
      if (clearing || !cart) { return; }
      var attrs = cart.attributes || {};
      if (!attrs[DATE_ATTR] && !attrs[DATE_ISO_ATTR]) { return; }
      var items = cart.items || [];
      if (items.some(function (it) { return OSCleanseCart.isCleanseLine(it); })) { return; }
      clearing = true;
      OSCleanseCart.clearDeliveryDate().then(function () { clearing = false; }, function () { clearing = false; });
    }
    document.addEventListener('cart:change', function (e) {
      var cart = e.detail && e.detail.cart;
      // The event's cart usually carries attributes + items; if not, fetch the authoritative cart.
      if (cart && cart.attributes && cart.items) { check(cart); }
      else { OSCleanseCart.fetchCart().then(check); }
    });

    // The cart PAGE reloads on a line change (theme.js LineItem) instead of firing cart:change, so the
    // listener above never runs there - a date left on an order whose last cleanse was just removed
    // would persist. Re-run the same check once on load of the cart page to clear an orphaned date.
    function onLoadOrphanCheck() {
      var pt = (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.pageType) || '';
      if (pt !== 'cart') { return; }
      OSCleanseCart.fetchCart().then(check);
    }
    if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', onLoadOrphanCheck); }
    else { onLoadOrphanCheck(); }
  })();

  /* ---- <os-cleanse-gate>: postcode gate + first-add date modal --------------------- */
  class OsCleanseGate extends HTMLElement {
    connectedCallback() {
      this.S = engine();
      if (!this.S && window.console) {
        console.warn('[os-cleanse-gate] os-cleanse-schedule.js not loaded - falling back to postcode-only.');
      }

      this.formId = this.dataset.form || '';
      this.input = this.querySelector('[data-postcode-input]');
      this.checkBtn = this.querySelector('[data-postcode-check]');
      this.result = this.querySelector('[data-postcode-result]');
      this.atcNote = this.querySelector('[data-atc-note]');

      var tpl = this.querySelector('[data-postcode-unavailable]');
      this.unavailableHtml = tpl ? tpl.innerHTML : '';
      this.confirmTpl = this.dataset.confirm || 'Great - we deliver to {postcode}.';
      this.invalidMsg = this.dataset.invalid || 'Please enter a 4-digit postcode.';

      this.postcodes = {};
      (this.dataset.postcodes || '').split(/[^0-9]+/).forEach(function (p) {
        if (p) { this.postcodes[p] = true; }
      }, this);

      // Schedule rules (used to work out selectable delivery dates + derive deliveries/boxes/bags).
      // leadDays = minimum notice to the DELIVERY; deliveryLead = days from the delivery to the start
      // (1 = the cleanse begins the next morning). deliveryLead shifts the calendar's selectable
      // weekdays back from the configured START days and turns a picked delivery into its start date.
      this.leadDays = parseInt(this.dataset.leadDays, 10); if (isNaN(this.leadDays)) { this.leadDays = 1; }
      this.deliveryLead = parseInt(this.dataset.deliveryLead, 10); if (isNaN(this.deliveryLead)) { this.deliveryLead = 1; }
      this.cutoffHour = parseInt(this.dataset.cutoffHour, 10); if (isNaN(this.cutoffHour)) { this.cutoffHour = 22; }
      this.windowDays = WINDOW_DAYS;
      this.blocked = this.S ? this.S.parseBlocked(this.dataset.blocked) : {};
      this.variantDays = this.parseJson(this.dataset.variantDays) || {};
      this.schedule = this.buildSchedule(this.parseJson(this.dataset.schedule) || {});
      this.defaultDays = parseInt(this.dataset.defaultDays, 10) || 0;
      this.fixedDays = parseInt(this.dataset.fixedDays, 10) || 0; // packs with no day variant (Mix & Match)

      // Copy (passed to the shared modal + written onto the line).
      this.calLabel = this.dataset.calLabel || 'Choose your delivery date';
      this.handoverNote = this.dataset.handoverNote || '';
      this.startSummaryTpl = this.dataset.startSummary || 'Delivered {delivery}. Your cleanse starts the next morning, {start}.';
      this.deliverySummaryTpl = this.dataset.deliverySummary || 'Deliveries: {deliveries}';
      this.multiNoteTpl = this.dataset.multiNote || '';
      this.orderMoveTpl = this.dataset.orderMoveNote || '';
      this.boxesLabel = this.dataset.boxesLabel || 'Boxes';
      this.bagsLabel = this.dataset.bagsLabel || 'Bags';

      // Broth choice (Bloom + Deep): a per-line option collected as a required step in the add-to-cart
      // modal and stamped onto the line. Options ride on data-broth-options as a JSON array built from a
      // product list - [{label,type,image}] - so each is shown as a large selectable card (image + type
      // eyebrow + title). Absent on cleanses without a broth choice, so hasBroth is false.
      this.brothPropName = this.dataset.brothProp || 'Broth';
      this.brothTitle = this.dataset.brothTitle || 'Choose your broth';
      this.brothNote = this.dataset.brothNote || '';
      this.brothOptions = [];
      try {
        var parsedBroth = JSON.parse(this.dataset.brothOptions || '[]');
        if (Array.isArray(parsedBroth)) {
          this.brothOptions = parsedBroth.map(function (o) {
            var label = ((o && o.label) || '').trim();
            // value == title so the kitchen note and the re-select-on-return match the display label.
            return { label: label, value: label, type: ((o && o.type) || '').trim(), image: (o && o.image) || '' };
          }).filter(function (o) { return o.value; });
        }
      } catch (e) { this.brothOptions = []; }
      this.hasBroth = this.brothOptions.length > 0;
      this.brothDefault = this.hasBroth ? this.brothOptions[0].value : '';

      // The date step is active only when the engine loaded and at least one length has start days;
      // otherwise the gate degrades to a postcode-only check (like the plain-juice gate).
      this.dateActive = !!this.S && this.hasAnyStartDays();
      this.now = null;
      this.cfg = null;
      this.days = 0;

      // Add to cart is locked on the POSTCODE alone (delivery-only). The date is collected on the
      // Add to cart click via the modal, not pre-gated on the page.
      this.postcodeOk = false;
      this.locked = true;

      this.onCheck = this.check.bind(this);
      if (this.checkBtn) { this.checkBtn.addEventListener('click', this.onCheck); }
      if (this.input) {
        this.onKey = function (e) { if (e.key === 'Enter') { e.preventDefault(); this.check(); } }.bind(this);
        this.input.addEventListener('keydown', this.onKey);
      }

      // On a variant change, clear any stale stamped properties + keep express checkout hidden.
      // Fresh properties are stamped at submit time, so nothing else needs recomputing here.
      this.onVariant = function (e) {
        var f = e.target;
        if (!f || !f.getAttribute || f.getAttribute('id') !== this.formId) { return; }
        if (this.form) { this.buyButtons = this.form.querySelector('buy-buttons'); }
        this.hideDynamicCheckout();
        this.clearProps();
      }.bind(this);
      document.addEventListener('variant:change', this.onVariant);

      if (document.readyState === 'loading') {
        this.onReady = this.bindForm.bind(this);
        document.addEventListener('DOMContentLoaded', this.onReady);
      } else {
        this.bindForm();
      }
    }

    disconnectedCallback() {
      if (this.checkBtn) { this.checkBtn.removeEventListener('click', this.onCheck); }
      if (this.input && this.onKey) { this.input.removeEventListener('keydown', this.onKey); }
      if (this.onReady) { document.removeEventListener('DOMContentLoaded', this.onReady); }
      if (this.form && this.onSubmit) { this.form.removeEventListener('submit', this.onSubmit, true); }
      if (this.onVariant) { document.removeEventListener('variant:change', this.onVariant); }
    }

    parseJson(s) { try { return JSON.parse(s); } catch (e) { return null; } }

    // Which weekdays the calendar offers: the configured START weekdays shifted back by the
    // delivery lead (so a Tuesday start with a 1-day lead is offered as a Monday delivery).
    deliveryDowsFor(startDows) {
      var lead = this.deliveryLead;
      return (startDows || []).map(function (d) { return ((d - lead) % 7 + 7) % 7; });
    }

    buildSchedule(raw) {
      var S = this.S, self = this, out = {};
      Object.keys(raw || {}).forEach(function (k) {
        var r = raw[k] || {};
        var offsets = S ? S.parseInts(r.offsets) : [0];
        if (!offsets.length) { offsets = [0]; }
        var startDows = S ? S.parseWeekdays(r.start) : [];
        out[k] = {
          startDows: startDows,
          deliveryDows: self.deliveryDowsFor(startDows),
          offsets: offsets,
          boxes: S ? S.parseInts(r.boxes) : [],
          bags: S ? (S.parseInts(r.bags)[0] || 0) : 0
        };
      });
      return out;
    }

    hasAnyStartDays() {
      var s = this.schedule;
      return Object.keys(s).some(function (k) { return s[k].startDows && s[k].startDows.length; });
    }

    // Config for the current length; falls back to the 3-day rules (single delivery) for any length
    // that has not been configured, so the calendar never renders empty by surprise.
    cfgFor(days) {
      if (this.schedule[days]) { return this.schedule[days]; }
      var keys = Object.keys(this.schedule);
      var base = this.schedule[3] || this.schedule[keys[0]] || { startDows: [], deliveryDows: [], offsets: [0], boxes: [], bags: 0 };
      return { startDows: base.startDows, deliveryDows: base.deliveryDows, offsets: [0], boxes: days ? [days] : base.boxes, bags: days || base.bags };
    }

    // The cleanse length read straight from the CHECKED variant option ("5 Day" -> 5). The theme's
    // variant picker updates the hidden [name="id"] input only AFTER it has fetched the new variant's
    // HTML, so during that async window the id lags a length behind. The checked radio flips
    // synchronously on click, so reading the length from it is race-free: a shopper who clicks
    // "5 Day" then immediately hits Add to cart still gets the 5-day calendar (Monday-only), not the
    // previously-selected length's rules. Returns null when it can't be read (falls back below).
    daysFromCheckedOption() {
      if (!this.formId) { return null; }
      // The option radios associate to the form via the `form=` attribute, not DOM nesting, so they
      // are NOT descendants of the form element - query the whole document scoped by the form id.
      var checked = document.querySelector('input[data-option-position][form="' + this.formId + '"]:checked');
      if (!checked) { return null; }
      var label = (checked.labels && checked.labels[0]) || document.querySelector('label[for="' + checked.id + '"]');
      var text = label ? label.textContent : '';
      var m = text && text.match(/(\d+)\s*day/i);
      return m ? parseInt(m[1], 10) : null;
    }

    currentDays() {
      if (this.fixedDays > 0) { return this.fixedDays; }
      var fromOption = this.daysFromCheckedOption();
      if (fromOption && this.schedule[fromOption]) { return fromOption; }
      var id = this.form && this.form.querySelector('[name="id"]');
      if (id && this.variantDays[id.value] != null) { return this.variantDays[id.value]; }
      return this.defaultDays;
    }

    ensureNow() { if (!this.now && this.S) { this.now = this.S.sydneyNow(); } return this.now; }

    calOpts() {
      return {
        now: this.ensureNow(),
        allowedDows: (this.cfg && this.cfg.deliveryDows) || [],
        leadDays: this.leadDays,
        cutoffHour: this.cutoffHour,
        blocked: this.blocked,
        windowDays: this.windowDays
      };
    }

    bindForm() {
      this.form = this.formId ? document.getElementById(this.formId) : null;
      this.buyButtons = this.form ? this.form.querySelector('buy-buttons') : null;

      // Reconcile the remembered delivery date against the ACTUAL cart. localStorage can outlive the
      // cart (after checkout, or once it is emptied), so the server-rendered cart attribute is the
      // truth: no cleanse date on the cart -> clear any stale saved date so the shopper is prompted
      // again; a date on the cart -> sync it. Without this, a leftover date silently skips the modal.
      var cartDate = this.dataset.cartDate || '';
      try {
        if (cartDate) { window.localStorage.setItem(DATE_KEY, cartDate); }
        else { window.localStorage.removeItem(DATE_KEY); }
      } catch (e) {}

      if (this.form) {
        this.onSubmit = this.handleSubmit.bind(this);
        this.form.addEventListener('submit', this.onSubmit, true); // capture: before the theme adds
      }

      // Express (Shop Pay) bypasses the product form, so it would drop the chosen date. Hide it on
      // gated products - standard Add to cart is the only path that opens the modal + carries dates.
      this.hideDynamicCheckout();

      // Fail closed: lock now, then auto-pass a remembered serviceable postcode.
      this.lock();
      var saved = null;
      try { saved = window.localStorage.getItem(PC_KEY); } catch (e) {}
      if (saved && this.postcodes[saved]) {
        if (this.input) { this.input.value = saved; }
        this.pass(saved);
      }
    }

    // Capture-phase Add to cart guard. 1) Postcode must clear (delivery-only). 2) If a delivery date
    // is set for the order and valid for this length, stamp this line and let the add through. 3) Else
    // open the shared date modal, then add once the shopper confirms.
    handleSubmit(e) {
      if (!this.postcodeOk) { e.preventDefault(); e.stopImmediatePropagation(); this.nudge(); return; }
      if (!this.dateActive) { return; }

      // Guard the variant-switch race: if the shopper just changed length and the picker is still
      // fetching the new variant, the form's [name="id"] (and the variant it would add) still points
      // at the previous length. Hold the add until the picker settles, then re-fire it - so we never
      // add the wrong variant or size the calendar to the previous length.
      var idEl = this.form && this.form.querySelector('[name="id"]');
      var idDays = idEl ? this.variantDays[idEl.value] : null;
      var optionDays = this.daysFromCheckedOption();
      if (optionDays && idDays != null && optionDays !== idDays) {
        e.preventDefault();
        e.stopImmediatePropagation();
        var self = this;
        var onSettle = function () {
          document.removeEventListener('variant:change', onSettle);
          if (self.form) { self.form.requestSubmit(); }
        };
        document.addEventListener('variant:change', onSettle);
        return;
      }

      this.days = this.currentDays();
      this.cfg = this.cfgFor(this.days);
      if (!this.days || !this.cfg || !(this.cfg.deliveryDows && this.cfg.deliveryDows.length)) { return; }

      var opts = this.calOpts();
      var saved = savedDate();
      var dateOk = saved && this.S.isSelectableStart(this.S.fromIso(saved), opts);

      // Re-fired submit after the modal was confirmed: the date (if any) is set and the broth prop is
      // on the form, so stamp this line's dated props and let the add through.
      if (this._modalDone) {
        this._modalDone = false;
        if (dateOk) { this.stampLine(saved); }
        return;
      }

      // Fast path: a valid delivery date is already set and this cleanse has no broth step -> stamp + add.
      if (dateOk && !this.hasBroth) {
        this.stampLine(saved);
        return;
      }

      // Otherwise collect what's still missing - the delivery date and/or the broth choice - in the
      // required modal, then re-fire the add.
      e.preventDefault();
      e.stopImmediatePropagation();
      this.openOptionsModal(dateOk);
    }

    // Open the shared modal to collect the delivery date (unless one is already set) and/or the broth
    // choice (Bloom + Deep). On confirm it writes the date, stamps the broth prop, then re-fires the add.
    openOptionsModal(dateAlreadyOk) {
      var modal = document.querySelector('os-cleanse-date-modal');
      if (!modal || typeof modal.open !== 'function') {
        if (window.console) { console.warn('[os-cleanse-gate] date modal not found; cannot collect cleanse options.'); }
        return;
      }
      var self = this;
      var needDate = !dateAlreadyOk;
      // If the order already has a cleanse date (an existing cleanse line) and we still need a date, the
      // one set isn't valid for this length, so confirming a new date moves the existing cleanse too -
      // warn the shopper, showing the date currently set.
      var existingIso = savedDate();
      var brothCfg = this.hasBroth ? {
        title: this.brothTitle,
        note: this.brothNote,
        options: this.brothOptions,
        selected: this.currentBroth()
      } : null;
      modal.open({
        showDate: needDate,
        title: needDate ? this.calLabel : this.brothTitle,
        handover: needDate ? this.handoverNote : '',
        note: needDate ? this.buildMultiDropNote() : '',
        warning: (needDate && existingIso) ? this.buildOrderMoveNote(existingIso) : '',
        confirmLabel: this.hasBroth ? 'Add to cart' : 'Confirm delivery date',
        continueLabel: 'Continue',
        opts: this.calOpts(),
        selectedIso: '',
        broth: brothCfg,
        describe: function (iso) { return self.describe(iso); },
        onConfirm: function (iso, extras) {
          var finish = function () {
            if (extras && extras.broth != null) { self.setBrothProp(extras.broth); }
            self._modalDone = true;
            if (self.form) { self.form.requestSubmit(); }
          };
          // When a date was picked, set the order attribute + move any existing cleanse lines first,
          // then add. In broth-only mode (date already set) there is nothing to write - just add.
          if (needDate && iso) {
            OSCleanseCart.setDeliveryDate(iso, { silent: true }).then(finish);
          } else {
            finish();
          }
        }
      });
    }

    // The broth value already chosen this page session (so returning to the step keeps it), or '' so
    // the shopper must pick one the first time.
    currentBroth() {
      var el = this.form && this.form.querySelector('[data-os-broth-prop]');
      return (el && el.value) || '';
    }

    // Find-or-create the single hidden broth line property on the form and set its value. Not marked
    // [data-os-cleanse-prop], so stampLine's clearProps leaves it alone; it rides the add like any
    // other line property and is preserved across "Change date" (recomputeProps copies all props).
    setBrothProp(value) {
      if (!this.form) { return; }
      var el = this.form.querySelector('[data-os-broth-prop]');
      if (!el) {
        el = document.createElement('input');
        el.type = 'hidden';
        el.setAttribute('data-os-broth-prop', '');
        this.form.appendChild(el);
      }
      el.name = 'properties[' + (this.brothPropName || 'Broth') + ']';
      el.value = value;
    }

    // Upfront explainer for lengths that arrive in more than one drop (the 5-day), so the shopper
    // knows the second delivery weekday BEFORE picking. Built from this length's schedule: the single
    // delivery weekday + the gap to the second drop. Empty for single-delivery lengths or when the
    // merchant has cleared the copy. Placeholders: {days} {first} {second} {gap}.
    buildMultiDropNote() {
      var S = this.S, cfg = this.cfg;
      if (!S || !cfg || !this.multiNoteTpl) { return ''; }
      var offsets = cfg.offsets || [0];
      if (offsets.length < 2) { return ''; }
      var gap = offsets[1] - offsets[0];
      var dows = cfg.deliveryDows || [];
      var first = dows.length === 1 ? S.DAY_NAMES[dows[0]] : 'delivery day';
      var second = dows.length === 1 ? S.DAY_NAMES[((dows[0] + gap) % 7 + 7) % 7] : 'second delivery day';
      return this.multiNoteTpl
        .split('{days}').join(this.days)
        .split('{first}').join(first)
        .split('{second}').join(second)
        .split('{gap}').join(gap);
    }

    // Warning shown when adding a cleanse of a length whose rules force the order's already-chosen
    // delivery date to move (the whole order ships on one date). {date} = the date currently set.
    buildOrderMoveNote(iso) {
      if (!this.S || !this.orderMoveTpl || !iso) { return ''; }
      return this.orderMoveTpl.split('{date}').join(this.S.formatFull(this.S.fromIso(iso)));
    }

    // Live summary shown in the modal for a hovered/selected delivery date.
    describe(iso) {
      var S = this.S; if (!S) { return {}; }
      var days = this.currentDays(), cfg = this.cfgFor(days);
      var base = S.fromIso(iso);                        // the chosen delivery date
      var derived = S.deriveDeliveries(base, cfg);      // offsets are counted from the delivery
      var start = S.addDays(base, this.deliveryLead);   // the cleanse begins after the delivery
      var summary = this.startSummaryTpl
        .split('{start}').join(S.formatFull(start))
        .split('{delivery}').join(S.formatFull(derived.deliveries[0]));
      var deliveries = '';
      if (derived.deliveries.length > 1) {
        var list = derived.deliveries.map(function (d) { return S.formatShort(d); }).join(', ');
        deliveries = this.deliverySummaryTpl.split('{deliveries}').join(list);
      }
      var packs = [];
      if (derived.boxes.length) { packs.push(this.boxesLabel + ': ' + derived.boxes.join(' + ')); }
      if (derived.bags) { packs.push(this.bagsLabel + ': ' + derived.bags); }
      return { summary: summary, deliveries: deliveries, packs: packs.join('  ·  ') };
    }

    // Write this line's delivery date(s), boxes, bags + the hidden self-describing config the cart
    // "Change date" recompute and the subscription carry-forward rely on. Rebuilt each submit.
    stampLine(iso) {
      if (!this.S || !this.form) { return; }
      this.days = this.currentDays();
      this.cfg = this.cfgFor(this.days);
      var base = this.S.fromIso(iso);                         // the chosen delivery date
      var derived = this.S.deriveDeliveries(base, this.cfg);  // offsets counted from the delivery
      var start = this.S.addDays(base, this.deliveryLead);    // the cleanse begins after the delivery
      var S = this.S, form = this.form;
      this.clearProps();
      var add = function (name, value) {
        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'properties[' + name + ']';
        input.value = value;
        input.setAttribute('data-os-cleanse-prop', '');
        form.appendChild(input);
      };
      var deliveries = derived.deliveries;
      add('Delivery date', S.formatFull(deliveries[0]));
      add('Start date', S.formatFull(start));
      if (deliveries.length > 1) { add('Second delivery', S.formatFull(deliveries[1])); }
      if (derived.boxes.length) { add(this.boxesLabel, derived.boxes.join(' + ')); }
      // The 3 & 5 day cleanses include a free cleanse bag (bags >= 1). Surface it to the shopper as a
      // readable line-item property so it carries onto checkout + the order ("Free Cleanse Bag:
      // Valued at $50"); the cart renders it as a styled call-out (snippets/os-cart-line-item). The
      // hidden numeric twin keeps the count for fulfilment. recomputeProps copies both onto a changed
      // date, so the bag survives "Change date".
      if (derived.bags) {
        add('Free Cleanse Bag', 'Valued at $50');
        add('_cleanse_bags', String(derived.bags));
      }
      // Hidden ISO twins + self-describing schedule config (leading underscore = not shown to the
      // shopper) so the cart can recompute this line and Flow can carry the dates onto renewals.
      // _cleanse_start keeps the cleanse START date (delivery + start offset) for Klaviyo / Flow.
      add('_start_date', S.iso(start));
      add('_cleanse_start', S.iso(start));
      deliveries.forEach(function (d, i) { add('_delivery_' + (i + 1), S.iso(d)); });
      add('_cleanse_days', String(this.days || ''));
      add('_cleanse_offsets', (this.cfg.offsets || [0]).join(','));   // counted from the delivery
      add('_cleanse_dows', (this.cfg.deliveryDows || []).join(','));  // selectable delivery weekdays
      add('_cleanse_start_offset', String(this.deliveryLead));        // days from delivery to start
      add('_cleanse_lead', String(this.leadDays));
      add('_cleanse_cutoff', String(this.cutoffHour));
      // First delivery's weekday (Sun=0 .. Sat=6; for cleanses: 1=Mon, 3=Wed). Carries onto the
      // subscription so Shopify Flow can recompute each renewal's delivery date off the fixed
      // Sunday billing day (delivery = recurring order date + this weekday number).
      add('_cleanse_delivery_dow', String(S.dowOf(deliveries[0])));
    }

    clearProps() {
      if (!this.form) { return; }
      var nodes = this.form.querySelectorAll('[data-os-cleanse-prop]');
      for (var i = 0; i < nodes.length; i++) {
        if (nodes[i].parentNode) { nodes[i].parentNode.removeChild(nodes[i]); }
      }
    }

    hideDynamicCheckout() {
      if (this.buyButtons) { this.buyButtons.classList.add('os-no-dynamic-checkout'); }
    }

    normalise(v) { return String(v || '').replace(/\D/g, '').slice(0, 4); }

    check() {
      var pc = this.normalise(this.input ? this.input.value : '');
      if (this.input) { this.input.value = pc; }
      if (pc.length < 4) { this.showResult('invalid', this.invalidMsg, false); return; }
      if (this.postcodes[pc]) { this.pass(pc); } else { this.blockPostcode(); }
    }

    pass(pc) {
      this.postcodeOk = true;
      try { window.localStorage.setItem(PC_KEY, pc); } catch (e) {}
      OSCleanseCart.setAttributes({ 'Delivery postcode': pc });
      this.showResult('ok', this.confirmTpl.split('{postcode}').join(pc), false);
      if (this.atcNote) { this.atcNote.hidden = false; }
      this.refreshLock();
    }

    blockPostcode() {
      this.postcodeOk = false;
      try { window.localStorage.removeItem(PC_KEY); } catch (e) {}
      this.showResult('no', this.unavailableHtml, true);
      if (this.atcNote) { this.atcNote.hidden = true; }
      this.refreshLock();
    }

    refreshLock() {
      if (this.postcodeOk) { this.unlock(); } else { this.lock(); }
    }

    lock() {
      this.locked = true;
      this.classList.remove('is-confirmed');
      this.classList.add('is-locked');
      if (this.buyButtons) { this.buyButtons.classList.add('os-atc-locked'); }
      var btn = this.form && this.form.querySelector('[type="submit"]');
      if (btn) { btn.setAttribute('aria-disabled', 'true'); }
    }

    unlock() {
      this.locked = false;
      this.classList.remove('is-locked');
      this.classList.add('is-confirmed');
      if (this.buyButtons) { this.buyButtons.classList.remove('os-atc-locked'); }
      var btn = this.form && this.form.querySelector('[type="submit"]');
      if (btn) { btn.removeAttribute('aria-disabled'); }
    }

    nudge() {
      this.classList.remove('os-delivery-gate--nudge');
      void this.offsetWidth; // restart the animation
      this.classList.add('os-delivery-gate--nudge');
      if (this.input) { try { this.input.focus(); } catch (e) {} }
    }

    showResult(state, content, isHtml) {
      if (!this.result) { return; }
      this.result.hidden = false;
      this.result.className = 'os-delivery-gate__result os-delivery-gate__result--' + state;
      if (isHtml) { this.result.innerHTML = content; } else { this.result.textContent = content; }
    }
  }

  if (!window.customElements.get('os-cleanse-gate')) {
    window.customElements.define('os-cleanse-gate', OsCleanseGate);
  }

  /* ---- <os-cleanse-date-modal>: shared delivery-date calendar dialog --------------- */
  class OsCleanseDateModal extends HTMLElement {
    connectedCallback() {
      this.S = engine();
      this.dialog = this.querySelector('[data-cdm-dialog]');
      if (!this.dialog) { return; }
      this.grid = this.querySelector('[data-cdm-grid]');
      this.monthEl = this.querySelector('[data-cdm-month]');
      this.prevBtn = this.querySelector('[data-cdm-prev]');
      this.nextBtn = this.querySelector('[data-cdm-next]');
      this.summaryEl = this.querySelector('[data-cdm-summary]');
      this.deliveriesEl = this.querySelector('[data-cdm-deliveries]');
      this.packsEl = this.querySelector('[data-cdm-packs]');
      this.titleEl = this.querySelector('[data-cdm-title]');
      this.warningEl = this.querySelector('[data-cdm-warning]');
      this.handoverEl = this.querySelector('[data-cdm-handover]');
      this.noteEl = this.querySelector('[data-cdm-note]');
      this.confirmBtn = this.querySelector('[data-cdm-confirm]');
      this.confirmLabelEl = this.querySelector('[data-cdm-confirm-label]');
      this.closeBtn = this.querySelector('[data-cdm-close]');
      this.calEl = this.querySelector('[data-cdm-cal]');
      this.brothEl = this.querySelector('[data-cdm-broth]');
      this.brothTitleEl = this.querySelector('[data-cdm-broth-title]');
      this.brothNoteEl = this.querySelector('[data-cdm-broth-note]');
      this.brothOptionsEl = this.querySelector('[data-cdm-broth-options]');
      this.recapEl = this.querySelector('[data-cdm-recap]');
      this.backBtn = this.querySelector('[data-cdm-back]');

      this.onCloseClick = function () { this.close(); }.bind(this);
      if (this.closeBtn) { this.closeBtn.addEventListener('click', this.onCloseClick); }
      this.onBackdrop = function (e) { if (e.target === this.dialog) { this.close(); } }.bind(this);
      this.dialog.addEventListener('click', this.onBackdrop);
      this.onCancel = function (e) { e.preventDefault(); this.close(); }.bind(this);
      this.dialog.addEventListener('cancel', this.onCancel);

      if (this.prevBtn) { this.onPrev = function () { this.shiftMonth(-1); }.bind(this); this.prevBtn.addEventListener('click', this.onPrev); }
      if (this.nextBtn) { this.onNext = function () { this.shiftMonth(1); }.bind(this); this.nextBtn.addEventListener('click', this.onNext); }
      if (this.grid) {
        this.onGrid = function (e) {
          var btn = e.target.closest('[data-day]');
          if (btn && this.grid.contains(btn)) { this.select(btn.getAttribute('data-day')); }
        }.bind(this);
        this.grid.addEventListener('click', this.onGrid);
      }
      if (this.confirmBtn) { this.onConfirmClick = function () { this.advance(); }.bind(this); this.confirmBtn.addEventListener('click', this.onConfirmClick); }
      if (this.backBtn) { this.onBackClick = function () { this.back(); }.bind(this); this.backBtn.addEventListener('click', this.onBackClick); }
      if (this.brothOptionsEl) {
        this.onBroth = function (e) {
          var r = e.target;
          if (r && r.name === 'os-cdm-broth') {
            this.brothValue = r.value;
            // Move the chosen-card frame to the selected option (one at a time).
            var opts = this.brothOptionsEl.querySelectorAll('.os-cdm-broth__option');
            for (var i = 0; i < opts.length; i++) {
              opts[i].classList.toggle('is-chosen', opts[i].contains(r));
            }
            this.updateConfirmEnabled();
          }
        }.bind(this);
        this.brothOptionsEl.addEventListener('change', this.onBroth);
      }
    }

    disconnectedCallback() {
      if (this.brothOptionsEl && this.onBroth) { this.brothOptionsEl.removeEventListener('change', this.onBroth); }
      if (this.backBtn && this.onBackClick) { this.backBtn.removeEventListener('click', this.onBackClick); }
      if (this.closeBtn && this.onCloseClick) { this.closeBtn.removeEventListener('click', this.onCloseClick); }
      if (this.dialog && this.onBackdrop) { this.dialog.removeEventListener('click', this.onBackdrop); }
      if (this.dialog && this.onCancel) { this.dialog.removeEventListener('cancel', this.onCancel); }
      if (this.prevBtn && this.onPrev) { this.prevBtn.removeEventListener('click', this.onPrev); }
      if (this.nextBtn && this.onNext) { this.nextBtn.removeEventListener('click', this.onNext); }
      if (this.grid && this.onGrid) { this.grid.removeEventListener('click', this.onGrid); }
      if (this.confirmBtn && this.onConfirmClick) { this.confirmBtn.removeEventListener('click', this.onConfirmClick); }
    }

    // config: { opts, selectedIso, title, handover, confirmLabel, describe(iso), onConfirm(iso) }
    open(config) {
      if (!this.S || !this.dialog) { return; }
      config = config || {};
      this.opts = config.opts || {};
      this.describeFn = typeof config.describe === 'function' ? config.describe : null;
      this.onConfirmFn = typeof config.onConfirm === 'function' ? config.onConfirm : null;
      this.selectedIso = config.selectedIso || '';
      // The date step is shown unless the caller opts out (showDate:false) - used when the order
      // already has a delivery date and the modal is opened only to collect the per-line broth choice.
      this.showDate = config.showDate !== false;

      // Titles + confirm label are applied per step by renderStep(): the date step shows the date
      // title, the broth step shows the broth title.
      this.dateTitle = config.title || '';
      this.brothStepTitle = (config.broth && config.broth.title) || config.title || '';
      if (this.handoverEl) {
        if (config.handover) { this.handoverEl.textContent = config.handover; this.handoverEl.hidden = false; }
        else { this.handoverEl.hidden = true; }
      }
      if (this.warningEl) {
        if (config.warning) { this.warningEl.textContent = config.warning; this.warningEl.hidden = false; }
        else { this.warningEl.hidden = true; }
      }
      if (this.noteEl) {
        if (config.note) { this.noteEl.textContent = config.note; this.noteEl.hidden = false; }
        else { this.noteEl.hidden = true; }
      }

      // Broth step: built from the config the gate passes (null elsewhere, e.g. the cart "Change date").
      this.buildBroth(config.broth);

      // When both the date and the broth are needed this is a two-step flow: step 1 picks the date,
      // step 2 recaps it and takes the broth choice (so step 2 is the confirmation step). A single
      // required thing (date only, or broth only) stays one step.
      this.twoStep = this.showDate && this.brothRequired;
      this.step = 1;
      this.finalConfirmLabel = config.confirmLabel || 'Confirm';
      this.continueLabel = config.continueLabel || 'Continue';

      if (this.showDate) {
        this.now = this.opts.now || this.S.sydneyNow();
        this.today = this.S.ymd(this.now.y, this.now.m, this.now.d);
        this.minMonth = this.monthIndex(this.today.y, this.today.m);
        var far = this.S.addDays(this.today, this.opts.windowDays || WINDOW_DAYS);
        this.maxMonth = this.monthIndex(far.y, far.m);

        var view;
        if (this.selectedIso && this.S.isSelectableStart(this.S.fromIso(this.selectedIso), this.opts)) {
          view = this.S.fromIso(this.selectedIso);
        } else {
          this.selectedIso = '';
          view = this.S.earliestStart(this.opts) || this.today;
        }
        this.viewYear = view.y;
        this.viewMonth = view.m;
        this.render();
      } else {
        this.selectedIso = '';
      }

      this.renderStep();
      this.showModal();
    }

    // Show the right step: toggles the calendar / recap / broth sections and the button labels. The
    // date-context notes (handover / multi-drop / order-move warning) ride with the calendar step.
    renderStep() {
      var onBroth = this.twoStep ? (this.step === 2) : this.brothRequired;
      var onDate = this.twoStep ? (this.step === 1) : this.showDate;

      if (this.calEl) { this.calEl.hidden = !onDate; }
      if (this.brothEl) { this.brothEl.hidden = !onBroth; }
      this.setDateNotesHidden(!onDate);

      // Recap of the chosen date, shown on the broth step of a two-step flow (the calendar is hidden).
      if (this.recapEl) {
        var info = (this.twoStep && this.step === 2 && this.selectedIso && this.describeFn) ? this.describeFn(this.selectedIso) : null;
        if (info && info.summary) { this.recapEl.textContent = info.summary; this.recapEl.hidden = false; }
        else { this.recapEl.hidden = true; }
      }

      if (this.backBtn) { this.backBtn.hidden = !(this.twoStep && this.step === 2); }
      if (this.titleEl) {
        this.titleEl.textContent = (this.twoStep && this.step === 2) ? this.brothStepTitle : this.dateTitle;
      }
      if (this.confirmLabelEl) {
        this.confirmLabelEl.textContent = (this.twoStep && this.step === 1) ? this.continueLabel : this.finalConfirmLabel;
      }

      this.updateSummary();
    }

    // Step 1 -> 2 advance, or the real confirm. The confirm button is this control in both roles.
    advance() {
      if (this.confirmBtn && this.confirmBtn.disabled) { return; }
      if (this.twoStep && this.step === 1) { this.step = 2; this.renderStep(); return; }
      this.confirm();
    }

    back() {
      if (!this.twoStep) { return; }
      this.step = 1;
      this.renderStep();
    }

    // Hide or (re)show the date-context notes by whether they carry text, so going back to the date
    // step restores them and the broth step is clean.
    setDateNotesHidden(hide) {
      [this.handoverEl, this.warningEl, this.noteEl].forEach(function (el) {
        if (!el) { return; }
        el.hidden = hide ? true : !(el.textContent && el.textContent.trim());
      });
    }

    // Build (or clear) the broth step from the gate's config: { title, note, options:[{label,value,type,
    // image}], selected }. Each option is a large selectable card (image + type eyebrow + title) echoing
    // the Mix & Match rows, minus the Details/stepper. Sets brothRequired/brothValue and shows or hides
    // the fieldset. DOM-built (not innerHTML) so merchant-entered labels can't inject markup.
    buildBroth(cfg) {
      this.brothRequired = false;
      this.brothValue = '';
      if (!this.brothEl || !this.brothOptionsEl) { return; }
      this.brothOptionsEl.innerHTML = '';
      var options = (cfg && cfg.options) || [];
      if (!options.length) { this.brothEl.hidden = true; return; }

      this.brothRequired = true;
      if (this.brothTitleEl) { this.brothTitleEl.textContent = cfg.title || 'Choose your broth'; }
      if (this.brothNoteEl) {
        if (cfg.note) { this.brothNoteEl.textContent = cfg.note; this.brothNoteEl.hidden = false; }
        else { this.brothNoteEl.hidden = true; }
      }
      // No default pre-selected: the shopper must actively choose a broth, so Confirm/Add to cart stays
      // disabled until one is ticked (unless they return to this step with a prior choice).
      var selected = cfg.selected || '';
      var self = this;
      options.forEach(function (o) {
        var label = document.createElement('label');
        label.className = 'os-cdm-broth__option';
        var input = document.createElement('input');
        input.type = 'radio';
        input.name = 'os-cdm-broth';
        input.value = o.value;
        if (o.value === selected) { input.checked = true; label.classList.add('is-chosen'); }

        // Circular thumbnail (or a placeholder square) so every card keeps the same height.
        var media = document.createElement('span');
        media.className = 'os-cdm-broth__media';
        media.setAttribute('aria-hidden', 'true');
        if (o.image) {
          var img = document.createElement('img');
          img.className = 'os-cdm-broth__image';
          img.src = o.image;
          img.alt = '';
          img.loading = 'lazy';
          media.appendChild(img);
        }

        var head = document.createElement('span');
        head.className = 'os-cdm-broth__head';
        if (o.type) {
          var type = document.createElement('span');
          type.className = 'subheading os-cdm-broth__type';
          type.textContent = o.type;
          head.appendChild(type);
        }
        var text = document.createElement('span');
        text.className = 'os-cdm-broth__label';
        text.textContent = o.label;
        head.appendChild(text);

        // The custom radio dot sits at the end so the card reads image -> name -> selection.
        var radio = document.createElement('span');
        radio.className = 'os-cdm-broth__radio';
        radio.setAttribute('aria-hidden', 'true');

        label.appendChild(input);
        label.appendChild(media);
        label.appendChild(head);
        label.appendChild(radio);
        self.brothOptionsEl.appendChild(label);
      });
      this.brothValue = selected;
      this.brothEl.hidden = false;
    }

    // Confirm is enabled once both shown steps are satisfied: a date (when the calendar is shown) and
    // a broth choice (when the broth step is shown; it defaults to the first option, so it starts met).
    updateConfirmEnabled() {
      if (!this.confirmBtn) { return; }
      var enabled;
      if (this.twoStep && this.step === 1) {
        enabled = !!this.selectedIso;                              // date step: needs a date
      } else if (this.twoStep && this.step === 2) {
        enabled = !!this.brothValue;                              // broth step: needs a choice
      } else {
        var dateOk = !this.showDate || !!this.selectedIso;
        var brothOk = !this.brothRequired || !!this.brothValue;
        enabled = dateOk && brothOk;                             // single step: whatever is shown
      }
      this.confirmBtn.disabled = !enabled;
    }

    monthIndex(y, m) { return y * 12 + (m - 1); }

    shiftMonth(delta) {
      var idx = this.monthIndex(this.viewYear, this.viewMonth) + delta;
      if (idx < this.minMonth || idx > this.maxMonth) { return; }
      this.viewYear = Math.floor(idx / 12);
      this.viewMonth = (idx % 12) + 1;
      this.render();
    }

    render() {
      if (!this.grid || !this.S) { return; }
      var months = this.S.MONTH_NAMES;
      if (this.monthEl) { this.monthEl.textContent = months[this.viewMonth - 1] + ' ' + this.viewYear; }
      var curIdx = this.monthIndex(this.viewYear, this.viewMonth);
      if (this.prevBtn) { this.prevBtn.disabled = curIdx <= this.minMonth; }
      if (this.nextBtn) { this.nextBtn.disabled = curIdx >= this.maxMonth; }

      var html = '';
      var wk = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']; // Monday-first (AU)
      for (var h = 0; h < 7; h++) { html += '<span class="os-delivery-cal__dow">' + wk[h] + '</span>'; }

      var first = new Date(this.viewYear, this.viewMonth - 1, 1);
      var lead = (first.getDay() + 6) % 7;
      for (var b = 0; b < lead; b++) { html += '<span class="os-delivery-cal__cell os-delivery-cal__cell--blank"></span>'; }

      var opts = this.opts;
      // A date is "sold out" when it's on the blocked list but would otherwise be selectable
      // (same rules minus the block) - those get the info "i" + "Sold out" tooltip.
      var blockFree = Object.assign({}, opts, { blocked: {} });
      var daysInMonth = new Date(this.viewYear, this.viewMonth, 0).getDate();
      for (var d = 1; d <= daysInMonth; d++) {
        var date = this.S.ymd(this.viewYear, this.viewMonth, d);
        if (this.S.isSelectableStart(date, opts)) {
          var iso = this.S.iso(date);
          var sel = iso === this.selectedIso;
          html += '<button type="button" class="os-delivery-cal__cell os-delivery-cal__day' +
            (sel ? ' is-selected' : '') + '" data-day="' + iso + '" aria-pressed="' + (sel ? 'true' : 'false') + '">' +
            d + '</button>';
        } else if ((opts.blocked || {})[this.S.iso(date)] && this.S.isSelectableStart(date, blockFree)) {
          html += osCalSoldOutCell(d);
        } else {
          html += '<span class="os-delivery-cal__cell os-delivery-cal__day is-disabled" aria-disabled="true">' + d + '</span>';
        }
      }
      this.grid.innerHTML = html;
    }

    select(iso) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(iso) || !this.S) { return; }
      if (!this.S.isSelectableStart(this.S.fromIso(iso), this.opts)) { return; }
      this.selectedIso = iso;
      this.render();
      this.updateSummary();
    }

    updateSummary() {
      var has = !!this.selectedIso;
      var info = (has && this.describeFn) ? this.describeFn(this.selectedIso) : null;
      if (this.summaryEl) {
        if (info && info.summary) { this.summaryEl.hidden = false; this.summaryEl.textContent = info.summary; }
        else { this.summaryEl.hidden = true; }
      }
      if (this.deliveriesEl) {
        if (info && info.deliveries) { this.deliveriesEl.hidden = false; this.deliveriesEl.textContent = info.deliveries; }
        else { this.deliveriesEl.hidden = true; }
      }
      if (this.packsEl) {
        if (info && info.packs) { this.packsEl.hidden = false; this.packsEl.textContent = info.packs; }
        else { this.packsEl.hidden = true; }
      }
      this.updateConfirmEnabled();
    }

    confirm() {
      if (this.confirmBtn && this.confirmBtn.disabled) { return; }
      // iso is '' in broth-only mode (date already set). The second arg carries the per-line extras
      // (broth choice); callers that don't use it - e.g. the cart "Change date" - simply ignore it.
      var iso = this.showDate ? this.selectedIso : '';
      var extras = { broth: this.brothRequired ? this.brothValue : null };
      var cb = this.onConfirmFn;
      this.close();
      if (typeof cb === 'function') { cb(iso, extras); }
    }

    showModal() {
      if (!this.dialog) { return; }
      if (typeof this.dialog.showModal === 'function') {
        try { this.dialog.showModal(); } catch (e) { this.dialog.setAttribute('open', ''); }
      } else { this.dialog.setAttribute('open', ''); }
    }

    close() {
      if (!this.dialog) { return; }
      if (typeof this.dialog.close === 'function') {
        try { this.dialog.close(); } catch (e) { this.dialog.removeAttribute('open'); }
      } else { this.dialog.removeAttribute('open'); }
    }
  }

  if (!window.customElements.get('os-cleanse-date-modal')) {
    window.customElements.define('os-cleanse-date-modal', OsCleanseDateModal);
  }

  /* ---- <os-cart-cleanse-date>: cart-drawer "Change date" --------------------------- */
  class OsCartCleanseDate extends HTMLElement {
    connectedCallback() {
      this.S = engine();
      this.btn = this.querySelector('[data-os-cleanse-change]');
      this.note = this.querySelector('[data-os-cleanse-change-note]');
      this.noteText = this.querySelector('[data-os-cleanse-note-text]');
      this.alert = this.querySelector('[data-os-cleanse-alert]');
      this.blocked = this.S ? this.S.parseBlocked(this.dataset.blocked) : {};
      this.current = this.dataset.current || '';
      if (this.btn) { this.onClick = this.openChange.bind(this); this.btn.addEventListener('click', this.onClick); }
      this.revalidate();
    }

    disconnectedCallback() {
      if (this.btn && this.onClick) { this.btn.removeEventListener('click', this.onClick); }
      // Release checkout as this instance leaves the DOM. If the cart still holds a stale cleanse,
      // the banner re-renders and the fresh instance re-blocks on its own revalidate(); if the cart
      // was cleared/emptied, no banner returns and checkout must not stay stuck disabled.
      OSCleanseCart.blockCheckout(false);
    }

    // Re-check the stored delivery date against the CURRENT rules (blocked list + lead + cut-off +
    // window) using the exact same test the picker uses to offer a date. A date that was valid when
    // chosen can since have been blocked by the merchant, or simply have passed. If it no longer
    // holds, surface an alert and block checkout until the shopper picks a new one.
    revalidate() {
      var self = this, S = this.S; if (!S) { return; }
      OSCleanseCart.fetchCart().then(function (cart) {
        var items = (cart && cart.items) || [];
        var lines = items.filter(function (it) { return OSCleanseCart.isCleanseLine(it); });
        if (!lines.length) { self.setStale(false); return; }
        var current = self.current || (lines[0].properties && lines[0].properties._delivery_1) || '';
        var ok = current && S.isSelectableStart(S.fromIso(current), self.buildOpts(lines));
        self.setStale(!ok);
      }).catch(function (e) {
        // Fail open: a cart-fetch error must never strand a valid shopper at a blocked checkout.
        if (window.console) { console.error('[os-cleanse] revalidate failed', e); }
        self.setStale(false);
      });
    }

    // Reflect the stale/valid state in the banner + the shared checkout block.
    setStale(stale) {
      this.classList.toggle('os-cart-cleanse-date--stale', !!stale);
      if (this.alert) {
        this.alert.hidden = !stale;
        if (stale) { this.alert.textContent = 'This delivery date is no longer available. Please choose a new date to continue.'; }
      }
      if (this.btn) { this.btn.textContent = stale ? 'Choose a new date' : 'Change date'; }
      OSCleanseCart.blockCheckout(stale);
    }

    // Visible "working" state while the new date is applied. The recompute runs one request per
    // cleanse line then re-renders/reloads, which takes a moment, so show a spinner + message and
    // disable the button. Cleared by the drawer re-render / the cart-page reload that follows.
    setUpdating(on) {
      this.classList.toggle('os-cart-cleanse-date--updating', !!on);
      if (this.note) { this.note.hidden = !on; }
      if (this.noteText && on) { this.noteText.textContent = 'Applying your new date…'; }
      if (this.btn) { this.btn.disabled = !!on; this.btn.setAttribute('aria-busy', on ? 'true' : 'false'); }
    }

    openChange() {
      var S = this.S; if (!S) { return; }
      var modal = document.querySelector('os-cleanse-date-modal');
      if (!modal || typeof modal.open !== 'function') { return; }
      var self = this;
      OSCleanseCart.fetchCart().then(function (cart) {
        var items = (cart && cart.items) || [];
        var lines = items.filter(function (it) { return OSCleanseCart.isCleanseLine(it); });
        if (!lines.length) { return; }
        modal.open({
          title: 'Change your cleanse delivery date',
          confirmLabel: 'Update delivery date',
          warning: lines.length > 1 ? 'Your order has more than one cleanse. They are delivered together, so the new date applies to them all.' : '',
          note: self.buildNote(lines),
          opts: self.buildOpts(lines),
          selectedIso: self.current || (lines[0].properties && lines[0].properties._delivery_1) || '',
          describe: function (iso) { return self.describe(iso, lines); },
          onConfirm: function (iso) {
            self.setUpdating(true);
            // Which surface are we on? Capture it NOW: the cart:change re-render inside setDeliveryDate
            // can detach this banner, so closest('cart-drawer') would read null by the time it resolves.
            var inDrawer = !!self.closest('cart-drawer');
            OSCleanseCart.setDeliveryDate(iso, { silent: false }).then(function () {
              if (inDrawer) {
                // The drawer is a focus-trapping dialog, so interacting with the modal (a click it does
                // not contain) closes it - hidden behind the modal backdrop. Once the dates are updated
                // and the drawer has re-rendered, re-open it so the shopper lands back on the refreshed
                // cart with the new date, never having to reopen it themselves.
                var drawer = document.querySelector('cart-drawer');
                if (drawer && typeof drawer.show === 'function') {
                  try { drawer.show({ animate: false }); } catch (e) {}
                }
              } else {
                // Cart PAGE (/cart): nothing re-renders the page on cart:change, so the banner would
                // sit on "Updating…" forever. Reload so the new dates show and the server re-renders
                // the checkout button enabled (the chosen date is valid).
                window.location.reload();
              }
            });
          }
        });
      });
    }

    // A calendar valid for EVERY cleanse line at once: allowed weekdays = intersection across the
    // lines (a Monday delivery works for a 3-day and a 5-day); notice + cut-off = the strictest (max).
    buildOpts(lines) {
      var S = this.S, dows = null, lead = 0, cutoff = 0;
      lines.forEach(function (it) {
        var p = it.properties || {};
        var ld = S.parseInts(p._cleanse_dows);
        if (dows === null) { dows = ld.slice(); }
        else { dows = dows.filter(function (x) { return ld.indexOf(x) !== -1; }); }
        var l = parseInt(p._cleanse_lead, 10); if (!isNaN(l) && l > lead) { lead = l; }
        var c = parseInt(p._cleanse_cutoff, 10); if (!isNaN(c) && c > cutoff) { cutoff = c; }
      });
      if (!dows || !dows.length) { dows = S.parseInts((lines[0].properties || {})._cleanse_dows); }
      return { now: S.sydneyNow(), allowedDows: dows, leadDays: lead, cutoffHour: cutoff || 22, blocked: this.blocked, windowDays: WINDOW_DAYS };
    }

    // Upfront two-delivery explainer for a single multi-drop line (the 5-day), mirroring the PDP
    // modal note. Skipped for single-drop lines or a mixed-length order (the per-selection summary
    // then lists every delivery day instead).
    buildNote(lines) {
      var S = this.S;
      if (!S || lines.length !== 1) { return ''; }
      var p = lines[0].properties || {};
      var offsets = S.parseInts(p._cleanse_offsets);
      if (offsets.length < 2) { return ''; }
      var gap = offsets[1] - offsets[0];
      var dows = S.parseInts(p._cleanse_dows);
      var first = dows.length === 1 ? S.DAY_NAMES[dows[0]] : 'chosen delivery day';
      var second = dows.length === 1 ? S.DAY_NAMES[((dows[0] + gap) % 7 + 7) % 7] : 'second delivery day';
      return 'Delivered in two drops: your chosen ' + first + ', then again on the ' + second + ' (' + gap + ' days later).';
    }

    describe(iso, lines) {
      var S = this.S, base = S.fromIso(iso);   // the chosen (first) delivery date
      var p0 = lines[0].properties || {};
      var startOffset = parseInt(p0._cleanse_start_offset, 10); if (isNaN(startOffset)) { startOffset = 1; }
      var start = S.addDays(base, startOffset);
      var summary;
      if (lines.length === 1) {
        // One cleanse: spell out its delivery day(s), from the picked delivery, in plain language.
        var offs0 = S.parseInts(p0._cleanse_offsets); if (!offs0.length) { offs0 = [0]; }
        var days = offs0.map(function (o) { return S.formatFull(S.addDays(base, o)); });
        summary = days.length > 1
          ? ('Hand-delivered ' + days.join(' and ') + '.')
          : ('Hand-delivered ' + days[0] + '.');
      } else {
        // Several cleanses share the date: list every distinct delivery day across the lines.
        var seen = {}, all = [];
        lines.forEach(function (it) {
          var p = it.properties || {};
          var offs = S.parseInts(p._cleanse_offsets); if (!offs.length) { offs = [0]; }
          offs.forEach(function (o) {
            var d = S.addDays(base, o), key = S.iso(d);
            if (!seen[key]) { seen[key] = true; all.push(d); }
          });
        });
        all.sort(function (a, b) { return S.diffDays(a, b); });
        summary = 'Hand-delivered ' + all.map(function (d) { return S.formatShort(d); }).join(', ') + '.';
      }
      var deliveries = 'Your cleanse starts ' + S.formatFull(start) + '.';
      return { summary: summary, deliveries: deliveries, packs: '' };
    }
  }

  if (!window.customElements.get('os-cart-cleanse-date')) {
    window.customElements.define('os-cart-cleanse-date', OsCartCleanseDate);
  }

  /* ---- <os-cart-cleanse-setup>: in-cart setup for a GATE-BYPASSED cleanse ----------
     A cleanse added outside the PDP gate (another channel / a direct /cart/add / the Shop app / a
     recovered dateless cart) carries no delivery date and none of the "_cleanse_*" line properties,
     so it is invisible to <os-cart-cleanse-date> and would slip to checkout with no date. This banner
     lets the shopper complete the setup from the cart - pick the delivery date, and the broth (Bloom
     / Deep) or the drinks (Mix & Match) - then stamps each line exactly like the gate (via
     OSCleanseCart.stampLineProps) and sets the order delivery-date attribute, so checkout unblocks.

     The schedule / broth / pack rules the cart cannot read from the gate block arrive as a
     product-id -> config map (data-config) built from each cleanse product's custom.cleanse_config
     metafield (snippet os-cart-cleanse-setup). Blocks checkout on connect (reason 'setup') until the
     queue of unconfigured lines is empty. A cleanse with no config falls back to the product page. */
  class OsCartCleanseSetup extends HTMLElement {
    connectedCallback() {
      this.S = engine();
      this.btn = this.querySelector('[data-os-cleanse-setup]');
      this.alert = this.querySelector('[data-os-cleanse-setup-alert]');
      this.note = this.querySelector('[data-os-cleanse-setup-note]');
      this.noteText = this.querySelector('[data-os-cleanse-setup-note-text]');

      this.config = this.parseJson(this.dataset.config) || {};
      // Defensive: a json-type metafield normally serialises to a nested object via `| json`, but if
      // any entry round-trips as a JSON string, parse it so cfg.lengths etc. are always objects.
      var cfgMap = this.config;
      Object.keys(cfgMap).forEach(function (k) {
        if (typeof cfgMap[k] === 'string') { try { cfgMap[k] = JSON.parse(cfgMap[k]); } catch (e) {} }
      });
      // Broth options are resolved LIVE by the section (title / type / image per product) and handed
      // in as a product-id -> options map, rather than baked into the metafield where they'd go stale.
      this.brothMap = this.parseJson(this.dataset.broth) || {};
      this.cleanseIds = {};
      (this.dataset.cleanseIds || '').split(',').forEach(function (id) {
        id = (id || '').trim(); if (id) { this.cleanseIds[id] = true; }
      }, this);
      this.blocked = this.S ? this.S.parseBlocked(this.dataset.blocked) : {};
      this.minNotice = parseInt(this.dataset.minNotice, 10); if (isNaN(this.minNotice)) { this.minNotice = 1; }

      if (this.btn) { this.onClick = this.start.bind(this); this.btn.addEventListener('click', this.onClick); }

      // Block immediately (server already rendered the button disabled; this holds it across any
      // client re-render) then confirm against the live cart.
      OSCleanseCart.blockCheckout(true, 'setup');
      this.revalidate();
    }

    disconnectedCallback() {
      if (this.btn && this.onClick) { this.btn.removeEventListener('click', this.onClick); }
      // Release our reason as this instance leaves the DOM; a fresh banner re-blocks on its own
      // revalidate() if setup is still needed, and the stale-date banner keeps its own 'stale' reason.
      OSCleanseCart.blockCheckout(false, 'setup');
    }

    parseJson(s) { try { return JSON.parse(s); } catch (e) { return null; } }

    // The gate-bypassed cleanse lines still needing setup: a cleanse product (its id is in the map the
    // section emitted) with no "_cleanse_start" stamp yet. Each carries its 1-based cart index (for
    // change.js), the cart item, and its resolved per-length schedule (null when the product has no
    // cleanse_config - those fall back to the product page).
    collectSetupLines(cart) {
      var self = this, items = (cart && cart.items) || [], lines = [];
      items.forEach(function (it, i) {
        var pid = String(it.product_id);
        if (!self.cleanseIds[pid]) { return; }
        var props = it.properties || {};
        if (props._cleanse_start) { return; }               // already set up
        var cfg = self.config[pid] || null;
        var days = self.daysFromLine(it, cfg);
        lines.push({
          index: i + 1, item: it, cfg: cfg, days: days,
          built: cfg ? self.cfgForDays(cfg, days) : null
        });
      });
      return lines;
    }

    revalidate() {
      var self = this;
      OSCleanseCart.fetchCart().then(function (cart) {
        var lines = self.collectSetupLines(cart);
        OSCleanseCart.blockCheckout(lines.length > 0, 'setup');
      }).catch(function (e) {
        // Fail open on a fetch error: do not strand the shopper behind a blocked checkout. The
        // server-rendered disabled button already covers the no-JS case.
        if (window.console) { console.error('[os-cleanse] setup revalidate failed', e); }
        OSCleanseCart.blockCheckout(false, 'setup');
      });
    }

    // Cleanse length read from the line's variant option / title ("5 Day" -> 5), falling back to the
    // config's fixed_days (the Mix & Match pack has no day option) then 0.
    daysFromLine(item, cfg) {
      var texts = [];
      (item.options_with_values || []).forEach(function (o) { if (o && o.value) { texts.push(String(o.value)); } });
      if (item.variant_title) { texts.push(String(item.variant_title)); }
      for (var i = 0; i < texts.length; i++) {
        var m = texts[i].match(/(\d+)\s*day/i);
        if (m) { return parseInt(m[1], 10); }
      }
      if (cfg && cfg.fixed_days) { return parseInt(cfg.fixed_days, 10) || 0; }
      return 0;
    }

    // Resolve one length's schedule from the product's cleanse_config, mirroring the gate's
    // buildSchedule + cfgFor: the configured START weekdays shifted back by the delivery lead give the
    // selectable DELIVERY weekdays; offsets / boxes / bags come straight from the length. Falls back to
    // the 3-day (single-drop) rules for an unconfigured length so the calendar never renders empty.
    cfgForDays(cfg, days) {
      var S = this.S;
      var lengths = cfg.lengths || {};
      var lead = this.intOr(cfg.lead_days, 1);
      var raw = lengths[String(days)];
      if (!raw) { raw = lengths['3'] || lengths[Object.keys(lengths)[0]] || {}; }
      var startDows = S ? S.parseWeekdays(raw.start_days || '') : [];
      var deliveryDows = startDows.map(function (d) { return ((d - lead) % 7 + 7) % 7; });
      var offsets = S ? S.parseInts(raw.offsets || '0') : [0]; if (!offsets.length) { offsets = [0]; }
      var boxes = S ? S.parseInts(raw.boxes || '') : [];
      var bags = S ? (S.parseInts(raw.bags || '')[0] || 0) : 0;
      return {
        days: days, startDows: startDows, deliveryDows: deliveryDows, offsets: offsets,
        boxes: boxes, bags: bags, deliveryLead: lead, minNotice: this.minNotice,
        cutoff: this.intOr(cfg.cutoff_hour, 22),
        boxesLabel: cfg.boxes_label || 'Boxes', bagsLabel: cfg.bags_label || 'Bags'
      };
    }

    intOr(v, d) { var n = parseInt(v, 10); return isNaN(n) ? d : n; }

    // A calendar valid for EVERY configured setup line at once: allowed weekdays = the intersection of
    // each line's delivery weekdays (the whole order ships on one date), notice = the shared minimum,
    // cut-off = the strictest. Mirrors <os-cart-cleanse-date>.buildOpts.
    buildCalOpts(builtList) {
      var S = this.S, dows = null, cutoff = 0;
      builtList.forEach(function (b) {
        if (!b) { return; }
        if (dows === null) { dows = b.deliveryDows.slice(); }
        else { dows = dows.filter(function (x) { return b.deliveryDows.indexOf(x) !== -1; }); }
        if (b.cutoff > cutoff) { cutoff = b.cutoff; }
      });
      if (!dows) { dows = []; }
      return { now: S.sydneyNow(), allowedDows: dows, leadDays: this.minNotice, cutoffHour: cutoff || 22, blocked: this.blocked, windowDays: WINDOW_DAYS };
    }

    // Live summary for a hovered/selected delivery date, scoped to the line being set up.
    describe(iso, built) {
      var S = this.S; if (!S || !built) { return {}; }
      var base = S.fromIso(iso);
      var derived = S.deriveDeliveries(base, { offsets: built.offsets, boxes: built.boxes, bags: built.bags });
      var start = S.addDays(base, built.deliveryLead);
      var days = derived.deliveries.map(function (d) { return S.formatFull(d); });
      var summary = (days.length > 1 ? ('Hand-delivered ' + days.join(' and ') + '.') : ('Hand-delivered ' + days[0] + '.'));
      summary += ' Your cleanse starts ' + S.formatFull(start) + '.';
      var deliveries = '';
      if (derived.deliveries.length > 1) {
        deliveries = 'Deliveries: ' + derived.deliveries.map(function (d) { return S.formatShort(d); }).join(', ');
      }
      var packs = [];
      if (derived.boxes.length) { packs.push((built.boxesLabel || 'Boxes') + ': ' + derived.boxes.join(' + ')); }
      if (derived.bags) { packs.push((built.bagsLabel || 'Bags') + ': ' + derived.bags); }
      return { summary: summary, deliveries: deliveries, packs: packs.join('  ·  ') };
    }

    brothCfgFor(line) {
      var cfg = line.cfg || {};
      var b = cfg.broth;
      if (!b || !b.enabled) { return null; }
      var raw = this.brothMap[String(line.item.product_id)] || [];
      if (!Array.isArray(raw) || !raw.length) { return null; }
      var options = raw.map(function (o) {
        var label = ((o && o.label) || '').trim();
        return { label: label, value: label, type: ((o && o.type) || '').trim(), image: (o && o.image) || '' };
      }).filter(function (o) { return o.value; });
      if (!options.length) { return null; }
      var existing = (line.item.properties || {})[b.property || 'Broth'] || '';
      return { property: b.property || 'Broth', title: b.heading || 'Choose your broth', note: b.note || '', options: options, selected: existing };
    }

    // "Complete setup" clicked: work through the gate-bypassed lines. A line with no config can't be
    // set up in the cart - send the shopper to its product page to use the gate. Otherwise run the
    // queue: the first line collects the order delivery date (+ its own broth / drinks); later lines
    // inherit the date and only collect what is still theirs.
    start() {
      var self = this;
      if (!this.S) { return; }
      OSCleanseCart.fetchCart().then(function (cart) {
        var lines = self.collectSetupLines(cart);
        if (!lines.length) { OSCleanseCart.blockCheckout(false, 'setup'); self.finish(cart); return; }
        var unconfigured = lines.filter(function (l) { return !l.cfg; });
        if (unconfigured.length) {
          // Fall back to the PDP gate for a cleanse we have no cart config for.
          var url = unconfigured[0].item.url;
          if (url) { window.location.href = url; }
          return;
        }
        self.inDrawer = !!self.closest('cart-drawer');
        var orderIso = (cart.attributes && cart.attributes._cleanse_delivery_iso) || '';
        self.builtAll = lines.map(function (l) { return l.built; });
        self.processQueue(lines, orderIso);
      });
    }

    // Process one line at a time, re-using the shared modal's existing date / broth / broth-only
    // modes. needDate is true only until the order delivery date is set (the first line that needs
    // one); after that, later lines inherit it and only collect their own broth (or nothing).
    processQueue(lines, orderIso) {
      var self = this;
      if (!lines.length) { self.complete(); return; }
      var line = lines[0], rest = lines.slice(1);
      var needDate = !orderIso;
      var brothCfg = this.brothCfgFor(line);
      var isPack = !!(line.cfg && line.cfg.pack);

      // A Mix & Match pack needs its drinks chosen too: hand off to the pack step (date first, then
      // the builder). Implemented alongside the headless <os-mix-match>.
      if (isPack) { this.processPack(line, rest, orderIso); return; }

      // Nothing left to ask for this line (date already set, no broth): stamp it silently and move on.
      if (!needDate && !brothCfg) {
        this.setUpdating(true);
        this.stampLine(line, orderIso, {}).then(function () { self.processQueue(rest, orderIso); });
        return;
      }

      var modal = document.querySelector('os-cleanse-date-modal');
      if (!modal || typeof modal.open !== 'function') { if (window.console) { console.warn('[os-cleanse-setup] date modal not found'); } return; }
      var built = line.built;
      modal.open({
        showDate: needDate,
        title: needDate ? 'Choose your cleanse delivery date' : (brothCfg ? brothCfg.title : ''),
        handover: needDate ? 'Your cleanse is hand-delivered on your chosen date and begins the next morning.' : '',
        confirmLabel: brothCfg ? 'Save cleanse' : 'Confirm delivery date',
        continueLabel: 'Continue',
        opts: this.buildCalOpts(this.builtAll),
        selectedIso: orderIso || '',
        broth: brothCfg,
        describe: function (iso) { return self.describe(iso, built); },
        onConfirm: function (iso, extras) {
          self.setUpdating(true);
          var useIso = needDate ? iso : orderIso;
          var extraProps = {};
          if (brothCfg && extras && extras.broth != null) { extraProps.broth = { name: brothCfg.property, value: extras.broth }; }
          var p = Promise.resolve();
          if (needDate && iso) { p = OSCleanseCart.setDeliveryDate(iso, { silent: true }); }
          p.then(function () { return self.stampLine(line, useIso, extraProps); })
           .then(function () { self.processQueue(rest, useIso); });
        }
      });
    }

    // Mix & Match pack: collect the delivery date (shared date modal, date-only) if the order has none
    // yet, then open the pack builder (os-cart-pack-modal, hosting the cloned headless <os-mix-match>
    // pool the banner rendered for this product) to choose the drinks. On confirm, stamp the line with
    // both the date props and the Mix & Match props (Your drinks + _mm_items), then move on.
    processPack(line, rest, orderIso) {
      var self = this, needDate = !orderIso, built = line.built;

      var runPack = function (useIso) {
        var tpl = self.querySelector('[data-os-pack-pool="' + line.item.product_id + '"]');
        var modal = document.querySelector('os-cart-pack-modal');
        if (!tpl || !modal || typeof modal.open !== 'function') {
          // No in-cart builder available -> fall back to the pack product page's own builder.
          if (line.item.url) { window.location.href = line.item.url; }
          return;
        }
        var frag = tpl.content.cloneNode(true);
        var builder = frag.querySelector('os-mix-match');
        modal.open({
          title: 'Choose your pack drinks',
          confirmLabel: 'Save drinks',
          poolNode: builder,
          onConfirm: function (sel) {
            self.setUpdating(true);
            var extras = { mm: { label: (sel && sel.label) || 'Your drinks', readable: (sel && sel.readable) || '', machine: (sel && sel.machine) || '' } };
            self.stampLine(line, useIso, extras).then(function () { self.processQueue(rest, useIso); });
          }
        });
      };

      if (!needDate) { runPack(orderIso); return; }

      var dateModal = document.querySelector('os-cleanse-date-modal');
      if (!dateModal || typeof dateModal.open !== 'function') { runPack(orderIso); return; }
      dateModal.open({
        showDate: true,
        title: 'Choose your cleanse delivery date',
        handover: 'Your cleanse is hand-delivered on your chosen date and begins the next morning.',
        confirmLabel: 'Continue to drinks',
        opts: this.buildCalOpts(this.builtAll),
        selectedIso: '',
        broth: null,
        describe: function (iso) { return self.describe(iso, built); },
        onConfirm: function (iso) {
          OSCleanseCart.setDeliveryDate(iso, { silent: true }).then(function () { runPack(iso); });
        }
      });
    }

    stampLine(line, iso, extras) {
      return OSCleanseCart.stampLineProps(line.index, line.item.properties, iso, line.built, extras);
    }

    // All lines stamped + the order date set: release checkout and refresh the cart surface.
    complete() {
      OSCleanseCart.blockCheckout(false, 'setup');
      var self = this;
      if (this.inDrawer) {
        // Re-render the drawer from the server so the banner clears + the dates show, then re-open it
        // (the modal interaction closed the focus-trapping drawer behind its backdrop).
        OSCleanseCart.setAttributes({}, OSCleanseCart.bundledSections())
          .then(function (res) { return (res && res.json) ? res.json() : OSCleanseCart.fetchCart(); })
          .then(function (cart) {
            document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'os-cleanse-setup:done', cart: cart } }));
            var drawer = document.querySelector('cart-drawer');
            if (drawer && typeof drawer.show === 'function') { try { drawer.show({ animate: false }); } catch (e) {} }
          });
      } else {
        // Cart page: nothing re-renders on cart:change, so reload - the server then drops the banner
        // and renders the checkout button enabled.
        window.location.reload();
      }
    }

    // On a cart page with no cleanse left to set up, just drop the banner's block (used when the queue
    // was already empty by the time the shopper clicked).
    finish() { OSCleanseCart.blockCheckout(false, 'setup'); }

    setUpdating(on) {
      this.classList.toggle('os-cart-cleanse-date--updating', !!on);
      if (this.note) { this.note.hidden = !on; }
      if (this.btn) { this.btn.disabled = !!on; this.btn.setAttribute('aria-busy', on ? 'true' : 'false'); }
    }
  }

  if (!window.customElements.get('os-cart-cleanse-setup')) {
    window.customElements.define('os-cart-cleanse-setup', OsCartCleanseSetup);
  }
})();

/* ============================================================================
   <os-mix-match> - "Mix & Match" pack builder for the juice cleanse range. On a
   fixed-price pack product the shopper chooses drinks with quantity steppers; Add
   to cart stays locked until the pack is exactly full, and the chosen drinks ride
   along as line-item properties, so the whole pack is one line item at the pack
   product's own price. Uses its own lock class (os-mm-locked) and a capture-phase
   submit guard, so it composes with the delivery / cleanse gates (each blocks the
   submit independently). Fails closed. Markup: snippets/os-pdp-mixmatch.liquid.
   ============================================================================ */
(function () {
  class OsMixMatch extends HTMLElement {
    connectedCallback() {
      this.formId = this.dataset.form || '';
      this.packSize = parseInt(this.dataset.packSize, 10); if (isNaN(this.packSize) || this.packSize < 1) { this.packSize = 6; }
      this.maxPerDrink = parseInt(this.dataset.maxPerDrink, 10); if (isNaN(this.maxPerDrink) || this.maxPerDrink < 1) { this.maxPerDrink = this.packSize; }
      this.selectionLabel = this.dataset.selectionLabel || 'Your drinks';
      this.counterTpl = this.dataset.counterText || '{count} of {total} chosen';
      this.addHintTpl = this.dataset.addHint || 'Choose {remaining} more';
      this.fullText = this.dataset.fullText || 'Your pack is complete.';

      this.counterEl = this.querySelector('[data-mm-counter]');
      this.hintEl = this.querySelector('[data-mm-hint]');

      this.items = Array.prototype.map.call(this.querySelectorAll('[data-mm-item]'), function (el) {
        return {
          el: el,
          handle: el.getAttribute('data-handle') || '',
          title: el.getAttribute('data-title') || '',
          qty: 0,
          // The type accordion this card lives in (null when the pool is not grouped), so the
          // group summary can show a live count of picks while it is collapsed.
          groupEl: el.closest('[data-mm-group]'),
          qtyEl: el.querySelector('[data-mm-qty]'),
          inc: el.querySelector('[data-mm-inc]'),
          dec: el.querySelector('[data-mm-dec]')
        };
      });

      this.groupEls = Array.prototype.slice.call(this.querySelectorAll('[data-mm-group]'));

      // Animated single-open accordions. Native <details> cannot transition (closed content is
      // display:none), so we intercept the summary click, keep the panel rendered, and animate its
      // height with the Web Animations API (matching the theme's 250ms ease-in-out). Opening one
      // group closes any other open group. Falls back to an instant toggle when the API or motion
      // is unavailable, and to the native <details> behaviour with JS off.
      var mm = this;
      this.reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
      this.canAnimate = typeof Element !== 'undefined' && !!Element.prototype.animate && !this.reduceMotion;

      this.setGroupOpen = function (details, opening) {
        var summary = details.querySelector('summary');
        if (!summary) { return; }
        if (details._mmAnim) { details._mmAnim.cancel(); details._mmAnim = null; }
        details.classList.toggle('is-open', opening);
        if (!mm.canAnimate) {
          details.open = opening;
          details.style.height = '';
          details.style.overflow = '';
          return;
        }
        if (opening) {
          details.open = true;
          var from = summary.offsetHeight;
          var to = details.scrollHeight;
          details.style.overflow = 'hidden';
          var a = details.animate([{ height: from + 'px' }, { height: to + 'px' }], { duration: 250, easing: 'ease-in-out' });
          details._mmAnim = a;
          a.onfinish = function () { details.style.height = ''; details.style.overflow = ''; details._mmAnim = null; };
        } else {
          var full = details.offsetHeight;
          var collapsed = summary.offsetHeight;
          details.style.overflow = 'hidden';
          var b = details.animate([{ height: full + 'px' }, { height: collapsed + 'px' }], { duration: 250, easing: 'ease-in-out' });
          details._mmAnim = b;
          b.onfinish = function () {
            if (!details.classList.contains('is-open')) { details.open = false; }
            details.style.height = '';
            details.style.overflow = '';
            details._mmAnim = null;
          };
        }
      };

      this.onGroupClick = function (e) {
        var summary = e.target.closest('summary');
        if (!summary) { return; }
        var details = summary.parentNode;
        if (!details || !details.hasAttribute || !details.hasAttribute('data-mm-group')) { return; }
        e.preventDefault();
        var opening = !details.classList.contains('is-open');
        if (opening) {
          for (var i = 0; i < mm.groupEls.length; i++) {
            if (mm.groupEls[i] !== details && mm.groupEls[i].classList.contains('is-open')) {
              mm.setGroupOpen(mm.groupEls[i], false);
            }
          }
        }
        mm.setGroupOpen(details, opening);
      };
      for (var gi = 0; gi < this.groupEls.length; gi++) {
        var gSummary = this.groupEls[gi].querySelector('summary');
        if (gSummary) { gSummary.addEventListener('click', this.onGroupClick); }
      }

      this.locked = true;
      this.wasFull = false; // tracks the not-full -> full transition so we collapse + scroll once

      // Delegated stepper clicks (one listener, survives any card re-order).
      this.onClick = function (e) {
        var incBtn = e.target.closest('[data-mm-inc]');
        var decBtn = e.target.closest('[data-mm-dec]');
        if (!incBtn && !decBtn) { return; }
        var card = (incBtn || decBtn).closest('[data-mm-item]');
        var item = null;
        for (var i = 0; i < this.items.length; i++) { if (this.items[i].el === card) { item = this.items[i]; break; } }
        if (!item) { return; }
        this.change(item, incBtn ? 1 : -1);
      }.bind(this);
      this.addEventListener('click', this.onClick);

      if (document.readyState === 'loading') {
        this.onReady = this.bindForm.bind(this);
        document.addEventListener('DOMContentLoaded', this.onReady);
      } else {
        this.bindForm();
      }

      this.refresh();
    }

    disconnectedCallback() {
      if (this.onClick) { this.removeEventListener('click', this.onClick); }
      if (this.onReady) { document.removeEventListener('DOMContentLoaded', this.onReady); }
      if (this.form && this.onSubmit) { this.form.removeEventListener('submit', this.onSubmit, true); }
      if (this.onGroupClick && this.groupEls) {
        for (var i = 0; i < this.groupEls.length; i++) {
          var s = this.groupEls[i].querySelector('summary');
          if (s) { s.removeEventListener('click', this.onGroupClick); }
        }
      }
    }

    bindForm() {
      this.form = this.formId ? document.getElementById(this.formId) : null;
      this.buyButtons = this.form ? this.form.querySelector('buy-buttons') : null;

      // Block submission while the pack isn't full; capture phase so this runs before the theme's
      // <product-form> handler. Independent of any delivery/cleanse gate guard on the same form.
      if (this.form) {
        this.onSubmit = function (e) {
          if (this.locked) { e.preventDefault(); e.stopImmediatePropagation(); this.nudge(); }
        }.bind(this);
        this.form.addEventListener('submit', this.onSubmit, true);
      }

      // The chosen drinks ride as line-item properties on the product form, which the express
      // (Shop Pay) button bypasses - hide it so the built pack can't be lost.
      if (this.buyButtons) { this.buyButtons.classList.add('os-no-dynamic-checkout'); }

      this.refresh(); // apply the initial lock once the form is known
    }

    total() {
      var t = 0;
      for (var i = 0; i < this.items.length; i++) { t += this.items[i].qty; }
      return t;
    }

    change(item, delta) {
      var next = item.qty + delta;
      if (next < 0 || next > this.maxPerDrink) { return; }
      if (delta > 0 && this.total() >= this.packSize) { return; }
      item.qty = next;
      this.refresh();
    }

    refresh() {
      var total = this.total();
      var full = total >= this.packSize;

      for (var i = 0; i < this.items.length; i++) {
        var it = this.items[i];
        if (it.qtyEl) { it.qtyEl.textContent = it.qty; }
        if (it.inc) { it.inc.disabled = full || it.qty >= this.maxPerDrink; }
        if (it.dec) { it.dec.disabled = it.qty <= 0; }
        it.el.classList.toggle('is-chosen', it.qty > 0);
      }

      // Per-group selection badges: how many picks sit inside each type accordion, so a
      // collapsed group still shows its contribution to the pack.
      for (var g = 0; g < this.groupEls.length; g++) {
        var gEl = this.groupEls[g];
        var gCount = 0;
        for (var k = 0; k < this.items.length; k++) {
          if (this.items[k].groupEl === gEl) { gCount += this.items[k].qty; }
        }
        var badge = gEl.querySelector('[data-mm-group-selected]');
        if (badge) { badge.textContent = gCount > 0 ? gCount : ''; }
        gEl.classList.toggle('has-selected', gCount > 0);
      }

      if (this.counterEl) {
        this.counterEl.textContent = this.counterTpl.split('{count}').join(total).split('{total}').join(this.packSize);
      }
      if (this.hintEl) {
        var remaining = this.packSize - total;
        this.hintEl.textContent = remaining > 0
          ? this.addHintTpl.split('{remaining}').join(remaining)
          : this.fullText;
      }

      this.writeProperties();

      // Unlock only when the pack is exactly full.
      this.locked = total !== this.packSize;
      if (this.locked) { this.lock(); } else { this.unlock(); }

      // Report the current selection so a headless host (the in-cart pack modal, which runs this
      // builder with no product form) can enable its own confirm + read the chosen drinks. Harmless
      // on the PDP, where nothing listens.
      this.dispatchEvent(new CustomEvent('os-mm:change', { bubbles: true, detail: this.selection() }));

      // On the moment the pack fills, collapse the groups and bring Add to cart into view.
      if (full && !this.wasFull) { this.onPackFull(); }
      this.wasFull = full;
    }

    // The current pack as plain data: a readable list ("Green juice ×2, Beet tonic") and a hidden
    // machine list ("green-juice:2,beet-tonic:1"), plus the fill state. Used to write the line-item
    // properties on the PDP form AND handed to the in-cart setup flow when a Mix & Match pack added
    // outside the builder is completed from the cart.
    selection() {
      var chosen = this.items.filter(function (it) { return it.qty > 0; });
      var readable = chosen.map(function (it) { return it.qty > 1 ? it.title + ' ×' + it.qty : it.title; }).join(', ');
      var machine = chosen.map(function (it) { return it.handle + ':' + it.qty; }).join(',');
      return {
        full: this.total() === this.packSize, count: this.total(), total: this.packSize,
        label: this.selectionLabel, readable: readable, machine: machine
      };
    }

    // Pack just became full: close every open type group and scroll to Add to cart so the
    // shopper can check out without scrolling back past the (now complete) picker.
    onPackFull() {
      if (this.groupEls) {
        for (var i = 0; i < this.groupEls.length; i++) {
          if (this.groupEls[i].classList.contains('is-open')) { this.setGroupOpen(this.groupEls[i], false); }
        }
      }
      var target = this.buyButtons || this.form;
      if (!target || !target.scrollIntoView) { return; }
      var behavior = this.reduceMotion ? 'auto' : 'smooth';
      // Let the collapse animation settle so we land on the final resting position, not a
      // point mid-tween.
      var delay = this.canAnimate ? 280 : 0;
      window.setTimeout(function () {
        try { target.scrollIntoView({ behavior: behavior, block: 'center' }); }
        catch (e) { target.scrollIntoView(); }
      }, delay);
    }

    // Rebuild the line-item properties: a readable list plus a hidden machine list. Only present
    // once at least one drink is chosen; removed entirely when the pack is emptied.
    writeProperties() {
      if (!this.form) { return; }
      var existing = this.form.querySelectorAll('[data-os-mm-prop]');
      for (var e = 0; e < existing.length; e++) {
        if (existing[e].parentNode) { existing[e].parentNode.removeChild(existing[e]); }
      }
      var sel = this.selection();
      if (!sel.count) { return; }

      var readable = sel.readable;
      var machine = sel.machine;

      var form = this.form;
      var add = function (name, value) {
        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'properties[' + name + ']';
        input.value = value;
        input.setAttribute('data-os-mm-prop', '');
        form.appendChild(input);
      };
      add(this.selectionLabel, readable);
      add('_mm_items', machine);
    }

    lock() {
      this.classList.add('is-locked');
      this.classList.remove('is-complete');
      if (this.buyButtons) { this.buyButtons.classList.add('os-mm-locked'); }
      var btn = this.form && this.form.querySelector('[type="submit"]');
      if (btn) { btn.setAttribute('aria-disabled', 'true'); }
    }

    unlock() {
      this.classList.remove('is-locked');
      this.classList.add('is-complete');
      if (this.buyButtons) { this.buyButtons.classList.remove('os-mm-locked'); }
      var btn = this.form && this.form.querySelector('[type="submit"]');
      // Only clear aria-disabled if no other gate on the form still holds it locked.
      if (btn && !(this.buyButtons && this.buyButtons.classList.contains('os-atc-locked'))) {
        btn.removeAttribute('aria-disabled');
      }
    }

    // Flash the counter to signal the pack must be completed first.
    nudge() {
      this.classList.remove('os-mixmatch--nudge');
      void this.offsetWidth; // restart the animation
      this.classList.add('os-mixmatch--nudge');
      if (this.counterEl && this.counterEl.scrollIntoView) {
        try { this.counterEl.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (e) {}
      }
    }
  }

  if (!window.customElements.get('os-mix-match')) {
    window.customElements.define('os-mix-match', OsMixMatch);
  }

  /* ---- <os-cart-pack-modal>: choose a Mix & Match pack's drinks from the cart ----------
     Opened by the in-cart cleanse setup flow (<os-cart-cleanse-setup>) for a pack added outside the
     PDP builder. The setup flow clones the headless <os-mix-match> pool (carried in a hidden template
     the cart banner renders for the pack product) and hands it in; this shell hosts it, keeps Confirm
     disabled until the builder reports the pack is full (the bubbling "os-mm:change" event), and
     returns the chosen drinks on confirm. Reuses the .os-bis-modal shell (snippet os-cart-pack-modal). */
  class OsCartPackModal extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-cpm-dialog]');
      if (!this.dialog) { return; }
      this.body = this.querySelector('[data-cpm-body]');
      this.titleEl = this.querySelector('[data-cpm-title]');
      this.introEl = this.querySelector('[data-cpm-intro]');
      this.confirmBtn = this.querySelector('[data-cpm-confirm]');
      this.confirmLabelEl = this.querySelector('[data-cpm-confirm-label]');
      this.closeBtn = this.querySelector('[data-cpm-close]');

      this.onCloseClick = function () { this.close(); }.bind(this);
      if (this.closeBtn) { this.closeBtn.addEventListener('click', this.onCloseClick); }
      this.onBackdrop = function (e) { if (e.target === this.dialog) { this.close(); } }.bind(this);
      this.dialog.addEventListener('click', this.onBackdrop);
      this.onCancel = function (e) { e.preventDefault(); this.close(); }.bind(this);
      this.dialog.addEventListener('cancel', this.onCancel);
      this.onConfirmClick = function () { this.confirmClick(); }.bind(this);
      if (this.confirmBtn) { this.confirmBtn.addEventListener('click', this.onConfirmClick); }
      // The cloned builder bubbles its fill state up to this host.
      this.onMm = function (e) { this.onChange(e.detail); }.bind(this);
      this.addEventListener('os-mm:change', this.onMm);
    }

    disconnectedCallback() {
      if (this.closeBtn && this.onCloseClick) { this.closeBtn.removeEventListener('click', this.onCloseClick); }
      if (this.dialog && this.onBackdrop) { this.dialog.removeEventListener('click', this.onBackdrop); }
      if (this.dialog && this.onCancel) { this.dialog.removeEventListener('cancel', this.onCancel); }
      if (this.confirmBtn && this.onConfirmClick) { this.confirmBtn.removeEventListener('click', this.onConfirmClick); }
      if (this.onMm) { this.removeEventListener('os-mm:change', this.onMm); }
    }

    // config: { poolNode, title, confirmLabel, intro, onConfirm(selection) }
    open(config) {
      if (!this.dialog) { return; }
      config = config || {};
      this.onConfirmFn = typeof config.onConfirm === 'function' ? config.onConfirm : null;
      if (this.titleEl && config.title) { this.titleEl.textContent = config.title; }
      if (this.confirmLabelEl && config.confirmLabel) { this.confirmLabelEl.textContent = config.confirmLabel; }
      if (this.introEl) {
        if (config.intro) { this.introEl.textContent = config.intro; this.introEl.hidden = false; }
        else { this.introEl.hidden = true; }
      }
      this.selectionData = null;
      if (this.confirmBtn) { this.confirmBtn.disabled = true; }
      if (this.body) {
        this.body.innerHTML = '';
        this.builder = config.poolNode || null;
        if (this.builder) { this.body.appendChild(this.builder); } // connect -> builder emits its first os-mm:change
      }
      this.showModal();
    }

    onChange(detail) {
      this.selectionData = detail;
      if (this.confirmBtn) { this.confirmBtn.disabled = !(detail && detail.full); }
    }

    confirmClick() {
      if (this.confirmBtn && this.confirmBtn.disabled) { return; }
      var sel = this.selectionData;
      if (!sel && this.builder && typeof this.builder.selection === 'function') { sel = this.builder.selection(); }
      var cb = this.onConfirmFn;
      this.close();
      if (typeof cb === 'function') { cb(sel); }
    }

    showModal() {
      if (typeof this.dialog.showModal === 'function') {
        try { this.dialog.showModal(); } catch (e) { this.dialog.setAttribute('open', ''); }
      } else { this.dialog.setAttribute('open', ''); }
    }

    close() {
      if (typeof this.dialog.close === 'function') {
        try { this.dialog.close(); } catch (e) { this.dialog.removeAttribute('open'); }
      } else { this.dialog.removeAttribute('open'); }
      // Drop the cloned builder so the next open starts clean (and its listeners go with it).
      if (this.body) { this.body.innerHTML = ''; }
      this.builder = null;
    }
  }

  if (!window.customElements.get('os-cart-pack-modal')) {
    window.customElements.define('os-cart-pack-modal', OsCartPackModal);
  }
})();

/* ============================================================================
   <os-variant-size> - swaps the size/capacity subtitle (under the title) to the
   selected variant's value on `variant:change`, so the size is variant-driven
   rather than a static metafield. Matches the form by its real id attribute
   (event.detail.formId is unreliable - shadowed by the <input name="id">).
   ============================================================================ */
class OsVariantSize extends HTMLElement {
  connectedCallback() {
    this.valueEl = this.querySelector('[data-size-value]');
    this.sizes = {};
    const data = this.querySelector('[data-sizes]');
    if (data) {
      try { this.sizes = JSON.parse(data.textContent) || {}; } catch (e) { this.sizes = {}; }
    }

    this.onVariantChange = (event) => {
      if (!event.detail || !event.detail.variant) return;
      const form = event.target;
      if (form && form.getAttribute && form.getAttribute('id') !== this.dataset.form) return;
      const size = this.sizes[String(event.detail.variant.id)];
      if (size != null && this.valueEl) this.valueEl.textContent = size;
    };
    document.addEventListener('variant:change', this.onVariantChange);
  }

  disconnectedCallback() {
    document.removeEventListener('variant:change', this.onVariantChange);
  }
}

if (!window.customElements.get('os-variant-size')) {
  window.customElements.define('os-variant-size', OsVariantSize);
}


/* ------ <os-directory> ------
   Client-side search, faceted filtering, sort + pagination for the Directory.
   - Free-text search matches each row's data-search haystack.
   - Faceted filters: category / group selections are UNIONed; Location selections
     INTERSECT. Category checkboxes carry data-dir-opt (matched against a row's
     data-cats); group facets with no sub-options become a single data-dir-toggle
     button (matched against data-groups); Location checkboxes carry data-dir-loc
     (matched against data-state).
   - Sorts the filtered set (A→Z / Z→A by data-sortname) and paginates client-side,
     reordering via CSS `order` and zebra-striping visible rows.
*/
class OsDirectory extends HTMLElement {
  connectedCallback() {
    this.rows = Array.from(this.querySelectorAll('[data-dir-entry]'));
    this.list = this.querySelector('[data-dir-list]');
    this.search = this.querySelector('[data-dir-search]');
    this.searchClear = this.querySelector('[data-dir-search-clear]');
    this.countEls = Array.from(this.querySelectorAll('[data-dir-count]'));
    this.emptyEl = this.querySelector('[data-dir-empty]');
    this.promptEl = this.querySelector('[data-dir-prompt]');
    this.resultsBar = this.querySelector('[data-dir-results-bar]');
    this.previewPrefix = this.querySelector('[data-dir-preview-prefix]');
    this.clearBtns = Array.from(this.querySelectorAll('[data-dir-clear]'));
    this.inlineClear = this.querySelector('.os-dir__clear');
    this.sortSel = this.querySelector('[data-dir-sort]');
    this.facets = Array.from(this.querySelectorAll('[data-dir-facet]'));
    this.pagination = this.querySelector('[data-dir-pagination]');
    this.pagesEl = this.querySelector('[data-dir-pages]');
    this.prevBtn = this.querySelector('[data-dir-prev]');
    this.nextBtn = this.querySelector('[data-dir-next]');

    // Mobile filter drawer
    this.filterTrigger = this.querySelector('[data-dir-filter-open]');
    this.filterClose = this.querySelector('[data-dir-filter-close]');
    this.filterApply = this.querySelector('[data-dir-filter-apply]');
    this.filterOverlay = this.querySelector('[data-dir-filter-overlay]');
    this.filtersPanel = this.querySelector('[data-dir-filters]');
    this.triggerBadge = this.querySelector('[data-dir-trigger-badge]');

    this.perPage = parseInt(this.dataset.perPage, 10) || 8;
    this.previewCount = parseInt(this.dataset.previewCount, 10);
    if (Number.isNaN(this.previewCount)) this.previewCount = 5;
    this.preview = this.pickPreview();
    this.query = '';
    this.selectedGroups = new Set();
    this.selectedCats = new Set();
    this.selectedLocs = new Set();
    this.selectedCities = new Set();
    this.onlineOnly = false;
    this.cityFacet = this.querySelector('[data-dir-city-facet]');
    this.cityOpts = Array.from(this.querySelectorAll('[data-dir-city-opt]'));
    this.onlineToggle = this.querySelector('[data-dir-online-toggle]');
    this.sort = 'az';
    this.page = 1;

    this.bind();
    this.apply();
    this.classList.add('os-dir--ready');
  }

  bind() {
    if (this.search) {
      this.search.addEventListener('input', () => {
        this.query = this.search.value.trim().toLowerCase();
        if (this.searchClear) this.searchClear.hidden = this.query === '';
        this.page = 1;
        this.apply();
      });
    }
    if (this.searchClear) {
      this.searchClear.addEventListener('click', () => {
        this.search.value = '';
        this.query = '';
        this.searchClear.hidden = true;
        this.search.focus();
        this.page = 1;
        this.apply();
      });
    }

    // Facet dropdowns
    this.facets.forEach((facet) => {
      const menuBtn = facet.querySelector('[data-dir-facet-btn]');
      if (menuBtn) {
        menuBtn.addEventListener('click', (e) => { e.stopPropagation(); this.toggleMenu(menuBtn); });
      }
      const toggleBtn = facet.querySelector('[data-dir-toggle]');
      if (toggleBtn) {
        toggleBtn.addEventListener('click', () => {
          const g = toggleBtn.dataset.dirToggle;
          if (this.selectedGroups.has(g)) this.selectedGroups.delete(g);
          else this.selectedGroups.add(g);
          this.page = 1;
          this.apply();
        });
      }
    });

    // Checkboxes
    this.querySelectorAll('[data-dir-opt]').forEach((input) => {
      input.addEventListener('change', () => {
        if (input.checked) this.selectedCats.add(input.value);
        else this.selectedCats.delete(input.value);
        this.page = 1;
        this.apply();
      });
    });
    // "Search all {category}" - filters by the whole search_group. Supersedes this facet's
    // sub-category picks (they become redundant once the group matches), so clear + disable
    // them while it is on, and restore them when it is switched off.
    this.querySelectorAll('[data-dir-group]').forEach((input) => {
      input.addEventListener('change', () => {
        const facet = input.closest('[data-dir-facet]');
        const opts = facet ? facet.querySelectorAll('[data-dir-opt]') : [];
        if (input.checked) {
          this.selectedGroups.add(input.value);
          opts.forEach((o) => {
            if (o.checked) { o.checked = false; this.selectedCats.delete(o.value); }
            o.disabled = true;
          });
        } else {
          this.selectedGroups.delete(input.value);
          opts.forEach((o) => { o.disabled = false; });
        }
        this.page = 1;
        this.apply();
      });
    });
    this.querySelectorAll('[data-dir-loc]').forEach((input) => {
      input.addEventListener('change', () => {
        if (input.checked) this.selectedLocs.add(input.value);
        else this.selectedLocs.delete(input.value);
        this.page = 1;
        this.apply();
      });
    });
    // Sub-location (city) checkboxes - intersect within the selected state(s).
    this.querySelectorAll('[data-dir-city]').forEach((input) => {
      input.addEventListener('change', () => {
        if (input.checked) this.selectedCities.add(input.value);
        else this.selectedCities.delete(input.value);
        this.page = 1;
        this.apply();
      });
    });
    // Online-consults toggle.
    if (this.onlineToggle) {
      this.onlineToggle.addEventListener('click', () => {
        this.onlineOnly = !this.onlineOnly;
        this.page = 1;
        this.apply();
      });
    }

    this.clearBtns.forEach((btn) => btn.addEventListener('click', () => this.reset()));

    // Mobile filter drawer
    if (this.filterTrigger) this.filterTrigger.addEventListener('click', () => this.openFilters());
    if (this.filterClose) this.filterClose.addEventListener('click', () => this.closeFilters());
    if (this.filterApply) this.filterApply.addEventListener('click', () => this.closeFilters());
    if (this.filterOverlay) this.filterOverlay.addEventListener('click', () => this.closeFilters());

    if (this.sortSel) {
      this.sortSel.addEventListener('change', () => {
        this.sort = this.sortSel.value;
        this.page = 1;
        this.apply();
      });
    }

    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.goToPage(this.page - 1));
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.goToPage(this.page + 1));
    if (this.pagesEl) {
      this.pagesEl.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-page]');
        if (btn) this.goToPage(parseInt(btn.dataset.page, 10));
      });
    }

    // Close any open facet menu on outside click / Escape
    document.addEventListener('click', (e) => {
      if (!e.target.closest('[data-dir-facet]')) this.closeMenus();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { this.closeMenus(); this.closeFilters(); }
    });
  }

  openFilters() {
    this.classList.add('is-filter-open');
    // Lift the panel into the top layer so it sits above any injected popup
    // (e.g. Klaviyo). This drawer is not a theme DialogElement, so we opt in
    // directly (see the top-layer promoter at the end of this file).
    if (window.OsTopLayer && this.filtersPanel) {
      window.OsTopLayer.promote(this.filtersPanel);
      window.OsTopLayer.syncFallback();
    }
    if (this.filterOverlay) this.filterOverlay.hidden = false;
    if (this.filterTrigger) this.filterTrigger.setAttribute('aria-expanded', 'true');
    // Present the panel as a modal dialog only while it acts as a mobile drawer.
    if (this.filtersPanel) {
      this.filtersPanel.setAttribute('role', 'dialog');
      this.filtersPanel.setAttribute('aria-modal', 'true');
      if (!this.filtersPanel.hasAttribute('aria-label')) this.filtersPanel.setAttribute('aria-label', 'Filters');
    }
    document.documentElement.style.overflow = 'hidden';
    if (this.filterClose) this.filterClose.focus();
    this.trapFilters = (e) => {
      if (e.key !== 'Tab' || !this.filtersPanel) return;
      var f = this.filtersPanel.querySelectorAll('button:not([hidden]), [href], input, select, [tabindex]:not([tabindex="-1"])');
      f = Array.prototype.filter.call(f, (el) => el.offsetParent !== null);
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', this.trapFilters);
  }

  closeFilters() {
    if (!this.classList.contains('is-filter-open')) return;
    this.classList.remove('is-filter-open');
    if (window.OsTopLayer && this.filtersPanel) {
      window.OsTopLayer.depromote(this.filtersPanel);
      window.OsTopLayer.syncFallback();
    }
    if (this.filterOverlay) this.filterOverlay.hidden = true;
    if (this.filterTrigger) this.filterTrigger.setAttribute('aria-expanded', 'false');
    if (this.filtersPanel) {
      this.filtersPanel.removeAttribute('role');
      this.filtersPanel.removeAttribute('aria-modal');
    }
    if (this.trapFilters) { document.removeEventListener('keydown', this.trapFilters); this.trapFilters = null; }
    this.closeMenus();
    document.documentElement.style.overflow = '';
    if (this.filterTrigger) this.filterTrigger.focus();
  }

  toggleMenu(btn) {
    const expanded = btn.getAttribute('aria-expanded') === 'true';
    this.closeMenus();
    if (!expanded) {
      btn.setAttribute('aria-expanded', 'true');
      const menu = btn.parentElement.querySelector('[data-dir-facet-menu]');
      if (menu) menu.hidden = false;
    }
  }

  closeMenus() {
    this.facets.forEach((facet) => {
      const btn = facet.querySelector('[data-dir-facet-btn]');
      if (btn) btn.setAttribute('aria-expanded', 'false');
      const menu = facet.querySelector('[data-dir-facet-menu]');
      if (menu) menu.hidden = true;
    });
  }

  rowMatches(row) {
    if (this.query && !(row.dataset.search || '').includes(this.query)) return false;

    if (this.selectedGroups.size || this.selectedCats.size) {
      const groups = (row.dataset.groups || '').split(/\s+/).filter(Boolean);
      const cats = (row.dataset.cats || '').split(/\s+/).filter(Boolean);
      const inGroup = groups.some((g) => this.selectedGroups.has(g));
      const inCat = cats.some((c) => this.selectedCats.has(c));
      if (!inGroup && !inCat) return false;
    }

    if (this.selectedLocs.size && !this.selectedLocs.has(row.dataset.state)) return false;
    if (this.selectedCities.size && !this.selectedCities.has(row.dataset.city)) return false;
    if (this.onlineOnly && row.dataset.online !== 'true') return false;

    return true;
  }

  hasActivity() {
    return this.query !== ''
      || this.selectedGroups.size > 0
      || this.selectedCats.size > 0
      || this.selectedLocs.size > 0
      || this.selectedCities.size > 0
      || this.onlineOnly;
  }

  /* The city facet only makes sense within a chosen state. Show it (and only the cities
     belonging to the selected state[s]) once at least one state is picked; hide it and drop
     any now-irrelevant city selections otherwise. */
  updateCityFacet() {
    const states = this.selectedLocs;
    let visibleCount = 0;
    this.cityOpts.forEach((opt) => {
      const show = states.size > 0 && states.has(opt.dataset.locState);
      opt.hidden = !show;
      if (show) visibleCount += 1;
      // Drop a selected city whose state is no longer selected.
      const input = opt.querySelector('[data-dir-city]');
      if (input && !show && input.checked) {
        input.checked = false;
        this.selectedCities.delete(input.value);
      }
    });
    if (this.cityFacet) this.cityFacet.hidden = visibleCount === 0;
  }

  /* Fisher-Yates shuffle, then take the first N as the start-state preview. */
  pickPreview() {
    const pool = this.rows.slice();
    for (let i = pool.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      const tmp = pool[i];
      pool[i] = pool[j];
      pool[j] = tmp;
    }
    return pool.slice(0, Math.max(0, this.previewCount));
  }

  apply() {
    this.updateCityFacet();
    const active = this.hasActivity();
    // Start state: hide the prompt and show a random handful of profiles, so shoppers
    // can see the kind of entries the directory returns. The prompt is only a fallback
    // for when there are no entries to preview.
    if (this.promptEl) this.promptEl.hidden = active || this.rows.length > 0;
    // Keep the results bar in the start state too (unless the directory is empty) so it
    // can read "5 of 138 results" as a teaser, reverting to "X results" once filtering.
    if (this.resultsBar) this.resultsBar.hidden = !active && this.rows.length === 0;

    let pageRows;
    let total;
    let pages = 1;
    if (active) {
      const filtered = this.rows.filter((r) => this.rowMatches(r));
      const dir = this.sort === 'za' ? -1 : 1;
      filtered.sort((a, b) => dir * (a.dataset.sortname || '').localeCompare(b.dataset.sortname || ''));

      total = filtered.length;
      pages = Math.max(1, Math.ceil(total / this.perPage));
      if (this.page > pages) this.page = pages;
      if (this.page < 1) this.page = 1;
      const start = (this.page - 1) * this.perPage;
      pageRows = filtered.slice(start, start + this.perPage);
    } else {
      pageRows = this.preview;
      total = this.rows.length;
    }

    // Start state shows "<shown> of <total>"; active search drops the prefix.
    if (this.previewPrefix) {
      this.previewPrefix.textContent = active ? '' : `${pageRows.length} of `;
    }

    this.rows.forEach((r) => {
      r.hidden = true;
      r.style.order = '';
      r.classList.remove('os-dir__row--alt');
    });
    pageRows.forEach((r, i) => {
      r.hidden = false;
      r.style.order = i;
      if (i % 2 === 1) r.classList.add('os-dir__row--alt');
    });

    this.countEls.forEach((el) => { el.textContent = active ? total : this.rows.length; });
    if (this.emptyEl) this.emptyEl.hidden = !active || total !== 0;

    this.renderPagination(active ? pages : 1);
    this.updateBadges();
  }

  renderPagination(pages) {
    if (!this.pagination) return;
    if (pages <= 1) { this.pagination.hidden = true; this.pagesEl.innerHTML = ''; return; }
    this.pagination.hidden = false;

    const html = this.pageItems(this.page, pages).map((it) => {
      if (it === '...') return '<span class="os-dir__page-ellipsis" aria-hidden="true">&hellip;</span>';
      const current = it === this.page;
      return `<button type="button" class="os-dir__page${current ? ' is-current' : ''}" data-page="${it}" aria-current="${current ? 'page' : 'false'}">${it}</button>`;
    }).join('');

    this.pagesEl.innerHTML = html;
    if (this.prevBtn) this.prevBtn.disabled = this.page === 1;
    if (this.nextBtn) this.nextBtn.disabled = this.page === pages;
  }

  /* Compact page list: 1 … current-1 current current+1 … total (fewer siblings on mobile). */
  pageItems(current, total) {
    const siblings = window.matchMedia('(max-width: 699px)').matches ? 1 : 2;
    const items = [1];
    const left = Math.max(2, current - siblings);
    const right = Math.min(total - 1, current + siblings);
    if (left > 2) items.push('...');
    for (let p = left; p <= right; p += 1) items.push(p);
    if (right < total - 1) items.push('...');
    if (total > 1) items.push(total);
    return items;
  }

  updateBadges() {
    let anyFilter = this.query !== '';
    this.facets.forEach((facet) => {
      const key = facet.dataset.dirFacetKey;
      const badge = facet.querySelector('[data-dir-facet-badge]');
      const toggleBtn = facet.querySelector('[data-dir-toggle]');
      let n = 0;
      if (toggleBtn) {
        n = this.selectedGroups.has(key) ? 1 : 0;
        toggleBtn.setAttribute('aria-pressed', n ? 'true' : 'false');
      } else {
        facet.querySelectorAll('[data-dir-group], [data-dir-opt], [data-dir-loc], [data-dir-city]').forEach((i) => { if (i.checked) n += 1; });
      }
      facet.classList.toggle('is-active', n > 0);
      if (n > 0) anyFilter = true;
      if (badge) { badge.textContent = n; badge.hidden = n === 0; }
    });

    // Online-consults toggle (standalone button, not a facet dropdown).
    if (this.onlineToggle) {
      this.onlineToggle.setAttribute('aria-pressed', this.onlineOnly ? 'true' : 'false');
      this.onlineToggle.classList.toggle('is-active', this.onlineOnly);
      if (this.onlineOnly) anyFilter = true;
    }

    if (this.inlineClear) this.inlineClear.hidden = !anyFilter;

    // Mobile trigger badge = active MODALITY selections only. Location / Area / Online now live
    // in the on-page quick-filters strip (outside this drawer), so they carry their own visible
    // active states and are not counted here.
    const totalSel = this.selectedGroups.size + this.selectedCats.size;
    if (this.triggerBadge) { this.triggerBadge.textContent = totalSel; this.triggerBadge.hidden = totalSel === 0; }
    if (this.filterTrigger) this.filterTrigger.classList.toggle('is-active', totalSel > 0);
  }

  goToPage(p) {
    this.page = p;
    this.apply();
    const results = this.querySelector('[data-dir-results]');
    if (results) results.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  reset() {
    this.query = '';
    this.selectedGroups.clear();
    this.selectedCats.clear();
    this.selectedLocs.clear();
    this.selectedCities.clear();
    this.onlineOnly = false;
    this.page = 1;
    if (this.search) this.search.value = '';
    if (this.searchClear) this.searchClear.hidden = true;
    this.querySelectorAll('[data-dir-opt], [data-dir-loc], [data-dir-city]').forEach((i) => { i.checked = false; i.disabled = false; });
    this.querySelectorAll('[data-dir-group]').forEach((i) => { i.checked = false; });
    this.querySelectorAll('[data-dir-toggle]').forEach((b) => b.setAttribute('aria-pressed', 'false'));
    this.closeMenus();
    this.apply();
  }
}

if (!window.customElements.get('os-directory')) {
  window.customElements.define('os-directory', OsDirectory);
}

/* ----------------------------------------------------------------------------
 * PDP "Kirsten's Field notes" overlay/row → smooth-scroll to the editorial
 * Field notes section. The link is a plain in-page anchor (#os-pdp-fieldnotes);
 * this just upgrades the jump to a smooth scroll that respects scroll-margin-top.
 * -------------------------------------------------------------------------- */
document.addEventListener('click', (event) => {
  const link = event.target.closest('a.os-pdp-field-notes');
  if (!link) return;

  const href = link.getAttribute('href') || '';
  if (href.charAt(0) !== '#') return; // external override link, let it navigate

  const target = document.getElementById(href.slice(1));
  if (!target) return;

  // Capture phase + stopPropagation so the gallery's own click/zoom handlers can't
  // swallow or override the scroll.
  event.preventDefault();
  event.stopPropagation();
  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  if (history.replaceState) history.replaceState(null, '', href);
}, true);

/* ============================================================
   Cart-drawer one-time / subscription switcher (<os-cart-plan>)
   Figma 332:813 "Add to cart options". Document-level event delegation so it
   keeps working no matter when the theme cart drawer re-renders / replaces its
   contents (per-element connectedCallback binding is unreliable there).
   Switches the line item's selling plan by POSTing to /cart/change.js, then
   lets the cart drawer re-render through the shared "cart:change" event (the
   same flow the stock quantity stepper uses).
   ============================================================ */
(function () {
  function closeMenus(except) {
    document.querySelectorAll('os-cart-plan').forEach(function (planEl) {
      if (planEl === except) return;
      var menu = planEl.querySelector('.os-cart-line__plan-menu');
      var toggle = planEl.querySelector('.os-cart-line__plan-toggle');
      if (menu) menu.hidden = true;
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    });
  }

  function optionsOf(planEl) {
    return Array.prototype.slice.call(planEl.querySelectorAll('.os-cart-line__plan-option'));
  }

  function openMenu(planEl, focusIndex) {
    var menu = planEl.querySelector('.os-cart-line__plan-menu');
    var toggle = planEl.querySelector('.os-cart-line__plan-toggle');
    if (!menu) return;
    closeMenus(planEl);
    menu.hidden = false;
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
    var opts = optionsOf(planEl);
    if (opts.length) opts[Math.max(0, Math.min(focusIndex || 0, opts.length - 1))].focus();
  }

  function focusToggle(planEl) {
    var toggle = planEl && planEl.querySelector('.os-cart-line__plan-toggle');
    if (toggle) toggle.focus();
  }

  async function changePlan(planEl, planId) {
    if (!planEl) return;
    var lineKey = planEl.getAttribute('data-line-key');
    var quantity = parseInt(planEl.getAttribute('data-quantity'), 10) || 1;
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    var sections = new Set();
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sections } })
    );

    try {
      var response = await fetch(root + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: lineKey,
          quantity: quantity,
          selling_plan: planId === '' ? null : Number(planId),
          sections: Array.from(sections).join(',')
        })
      });
      var cart = await response.json();
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'os-cart-plan:change', cart: cart } })
      );
    } catch (error) {
      console.error('[os-cart-plan] failed to change selling plan', error);
    } finally {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
  }

  document.addEventListener('click', function (event) {
    // 1. Toggle the dropdown
    var toggle = event.target.closest('.os-cart-line__plan-toggle');
    if (toggle && !toggle.disabled) {
      event.preventDefault();
      var planEl = toggle.closest('os-cart-plan');
      var menu = planEl && planEl.querySelector('.os-cart-line__plan-menu');
      if (!menu) return;
      var willOpen = menu.hidden;
      closeMenus(planEl);
      menu.hidden = !willOpen;
      toggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      return;
    }

    // 2. Pick an option
    var option = event.target.closest('.os-cart-line__plan-option');
    if (option) {
      var optionPlan = option.closest('os-cart-plan');
      closeMenus(null);
      if (option.getAttribute('aria-selected') !== 'true') {
        changePlan(optionPlan, option.getAttribute('data-plan-id') || '');
      }
      return;
    }

    // 3. Click anywhere else closes any open menu
    closeMenus(null);
  });

  document.addEventListener('keydown', function (event) {
    var toggle = event.target.closest('.os-cart-line__plan-toggle');
    if (toggle && !toggle.disabled) {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp' && event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      var planEl = toggle.closest('os-cart-plan');
      var menu = planEl && planEl.querySelector('.os-cart-line__plan-menu');
      if (!menu) return;
      if (menu.hidden) {
        var opts = optionsOf(planEl);
        var sel = opts.findIndex(function (o) { return o.getAttribute('aria-selected') === 'true'; });
        openMenu(planEl, sel < 0 ? 0 : sel);
      } else {
        closeMenus(null);
      }
      return;
    }

    var option = event.target.closest('.os-cart-line__plan-option');
    if (!option) return;
    var plan = option.closest('os-cart-plan');
    var list = optionsOf(plan);
    var idx = list.indexOf(option);
    switch (event.key) {
      case 'ArrowDown': event.preventDefault(); list[Math.min(idx + 1, list.length - 1)].focus(); break;
      case 'ArrowUp': event.preventDefault(); list[Math.max(idx - 1, 0)].focus(); break;
      case 'Home': event.preventDefault(); list[0].focus(); break;
      case 'End': event.preventDefault(); list[list.length - 1].focus(); break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (option.getAttribute('aria-selected') === 'true') {
          closeMenus(null);
          focusToggle(plan);
        } else {
          changePlan(plan, option.getAttribute('data-plan-id') || '');
        }
        break;
      case 'Escape':
        event.preventDefault();
        closeMenus(null);
        focusToggle(plan);
        break;
      case 'Tab':
        closeMenus(null);
        break;
    }
  });
})();

/* ============================================================
   Cart-drawer loading state
   The stock quantity stepper (and the plan switcher above) fire
   "theme:loading:start" then re-render the drawer asynchronously, which left a
   dead pause with no feedback. Mark the drawer busy (dim + spinner via
   .os-cart-busy in os-custom.css) from loading start until the drawer reports
   it has re-rendered ("cart-drawer:refreshed"), with a safety timeout in case
   that event never arrives (e.g. a non-cart loading op or a network error).
   ============================================================ */
(function () {
  var BUSY = 'os-cart-busy';
  var safety;

  function setBusy(on) {
    document.querySelectorAll('cart-drawer').forEach(function (drawer) {
      drawer.classList.toggle(BUSY, on);
    });
  }

  document.addEventListener('theme:loading:start', function () {
    setBusy(true);
    clearTimeout(safety);
    safety = setTimeout(function () { setBusy(false); }, 6000);
  });

  document.addEventListener('cart-drawer:refreshed', function () {
    clearTimeout(safety);
    setBusy(false);
  });
})();

/* ============================================================================
   <os-faq> - FAQ category scroll-spy (sections/os-faq.liquid)
   Highlights the jump-nav link for the category group in view. Anchor links work
   without it; no-op when the section has no jump-nav.
   ============================================================================ */
(function () {
  if (!('IntersectionObserver' in window)) return;

  class OsFaq extends HTMLElement {
    connectedCallback() {
      this.links = this.querySelectorAll('[data-faq-nav]');
      this.groups = this.querySelectorAll('[data-faq-group]');
      if (!this.links.length || !this.groups.length) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            this.setActive(entry.target.getAttribute('data-faq-group'));
          });
        },
        // Bias the active zone to the upper third so the highlight tracks reading.
        { rootMargin: '-20% 0px -70% 0px', threshold: 0 }
      );

      this.groups.forEach((group) => this.observer.observe(group));
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    setActive(handle) {
      this.links.forEach((link) => {
        link.classList.toggle('is-active', link.getAttribute('data-faq-nav') === handle);
      });
    }
  }

  if (!customElements.get('os-faq')) {
    customElements.define('os-faq', OsFaq);
  }
})();

/* ============================================================================
   <os-glossary> - A-Z glossary controller (sections/os-glossary.liquid)
   Three progressive-enhancement behaviours over an already-rendered A-Z list:
     1. Category filter buttons + a live search box that hide non-matching terms
        (and any letter group left empty), with a no-results message.
     2. An A-Z jump-nav scroll-spy that highlights the letter currently in view
        and dims letters whose group is filtered out.
   Anchor links and the full list work without JS; this only adds affordances.
   ============================================================================ */
(function () {
  class OsGlossary extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-glossary-item]'));
      this.groups = Array.from(this.querySelectorAll('[data-glossary-group]'));
      this.navLinks = Array.from(this.querySelectorAll('[data-glossary-nav]'));
      this.filters = Array.from(this.querySelectorAll('[data-glossary-filter]'));
      this.searchInput = this.querySelector('[data-glossary-search]');
      this.noResults = this.querySelector('[data-glossary-noresults]');

      this.category = 'all';
      this.query = '';

      this.filters.forEach((btn) => {
        btn.addEventListener('click', () => {
          this.category = btn.getAttribute('data-glossary-filter');
          this.filters.forEach((b) => b.classList.toggle('is-active', b === btn));
          this.apply();
        });
      });

      if (this.searchInput) {
        this.searchInput.addEventListener('input', () => {
          this.query = this.searchInput.value.trim().toLowerCase();
          this.apply();
        });
      }

      this.initScrollSpy();
    }

    apply() {
      let visible = 0;
      this.items.forEach((item) => {
        const matchCat = this.category === 'all' || item.getAttribute('data-category') === this.category;
        const matchSearch = this.query === '' || (item.getAttribute('data-search') || '').indexOf(this.query) !== -1;
        const show = matchCat && matchSearch;
        item.hidden = !show;
        if (show) visible += 1;
      });

      // Hide any letter group that has no visible terms, and dim its nav letter.
      this.groups.forEach((group) => {
        const hasVisible = group.querySelector('[data-glossary-item]:not([hidden])') !== null;
        group.hidden = !hasVisible;
        const letter = group.getAttribute('data-glossary-group');
        const link = this.navLinks.find((l) => l.getAttribute('data-glossary-nav') === letter);
        if (link) link.classList.toggle('os-glossary__nav-link--empty', !hasVisible);
      });

      if (this.noResults) this.noResults.hidden = visible !== 0;
    }

    initScrollSpy() {
      if (!('IntersectionObserver' in window) || !this.navLinks.length || !this.groups.length) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            this.setActive(entry.target.getAttribute('data-glossary-group'));
          });
        },
        { rootMargin: '-25% 0px -70% 0px', threshold: 0 }
      );

      this.groups.forEach((group) => this.observer.observe(group));
    }

    setActive(letter) {
      this.navLinks.forEach((link) => {
        link.classList.toggle('is-active', link.getAttribute('data-glossary-nav') === letter);
      });
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }
  }

  if (!customElements.get('os-glossary')) {
    customElements.define('os-glossary', OsGlossary);
  }
})();

/* ============================================================================
   <os-credits> - footer credits reveal (sections/os-footer-legal.liquid)
   The "Credits" label toggles a CSS-animated panel above the legal row (click
   again / click away / Escape closes). Only toggles the .is-open class + the
   matching aria/hidden state; links stay in the DOM when JS is off.
   ============================================================================ */
(function () {
  class OsCredits extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('.os-credits__toggle');
      this.panel = this.querySelector('.os-credits__panel');
      if (!this.toggle || !this.panel) return;

      this.onToggle = this.onToggle.bind(this);
      this.onOutside = this.onOutside.bind(this);
      this.onKey = this.onKey.bind(this);

      this.toggle.addEventListener('click', this.onToggle);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onOutside);
      document.removeEventListener('keydown', this.onKey);
    }

    onToggle(event) {
      event.preventDefault();
      this.setOpen(!this.classList.contains('is-open'));
    }

    setOpen(open) {
      this.classList.toggle('is-open', open);
      this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      this.panel.setAttribute('aria-hidden', open ? 'false' : 'true');

      if (open) {
        document.addEventListener('click', this.onOutside);
        document.addEventListener('keydown', this.onKey);
      } else {
        document.removeEventListener('click', this.onOutside);
        document.removeEventListener('keydown', this.onKey);
      }
    }

    onOutside(event) {
      if (!this.contains(event.target)) this.setOpen(false);
    }

    onKey(event) {
      if (event.key === 'Escape') {
        this.setOpen(false);
        this.toggle.focus();
      }
    }
  }

  if (!customElements.get('os-credits')) {
    customElements.define('os-credits', OsCredits);
  }
})();

/* ============================================================================
   <os-bis> - Klaviyo back-in-stock modal controller. One instance wraps the
   single shared modal (snippets/os-bis-modal) rendered near </body>. Every
   "Notify me when available" button on the page (snippets/os-bis-trigger, used
   in place of Add to cart on sold-out PDPs and collection cards) carries
   data-os-bis-open + data-variant + product/variant titles; a delegated click
   handler opens this modal pre-filled with that variant. On submit it POSTs the
   email + variant to Klaviyo's client-side Back in Stock endpoint using the
   public API key (data-company). Uses a native <dialog> so focus-trap, Escape
   and the backdrop come for free. Klaviyo's own Back in Stock FLOW (built in the
   Klaviyo app) sends the actual "it's back" email - this only registers the
   signup. Renders nothing / does nothing unless a public API key is configured.
   ============================================================================ */
(function () {
  var SUBSCRIBED_KEY = 'os:bis-subscribed';
  // JSON:API revision pinned so a future breaking change to the endpoint can't
  // silently alter the request shape. Bump deliberately if Klaviyo requires it.
  var KLAVIYO_REVISION = '2025-07-15';

  class OsBis extends HTMLElement {
    connectedCallback() {
      this.company = this.dataset.company || '';
      this.dialog = this.querySelector('[data-os-bis-dialog]');
      this.form = this.querySelector('[data-os-bis-form]');
      this.emailInput = this.querySelector('[data-os-bis-email]');
      this.productEl = this.querySelector('[data-os-bis-product]');
      this.messageEl = this.querySelector('[data-os-bis-message]');
      this.submitBtn = this.querySelector('[data-os-bis-submit]');
      this.submitLabel = this.querySelector('[data-os-bis-submit-label]');
      this.closeBtn = this.querySelector('[data-os-bis-close]');
      var successTpl = this.querySelector('[data-os-bis-success]');
      this.successCopy = successTpl ? successTpl.innerHTML.trim() : "You're on the list.";
      this.defaultSubmitLabel = this.submitLabel ? this.submitLabel.textContent : 'Notify me';

      if (!this.dialog || !this.form || !this.company) return;

      this.currentVariant = null;

      // Delegated: any trigger anywhere on the page opens this one modal. CAPTURE phase so we
      // intercept the click before the theme's product-card / link handlers can act on it - a
      // sold-out card's Add-to-cart-replacement button sits over the card's image link, so a
      // bubble-phase handler could lose the click to that navigation. We fully own the click.
      this.onTriggerClick = this.onTriggerClick.bind(this);
      document.addEventListener('click', this.onTriggerClick, true);

      this.onSubmit = this.onSubmit.bind(this);
      this.form.addEventListener('submit', this.onSubmit);

      if (this.closeBtn) {
        this.onClose = function () { this.dialog.close(); }.bind(this);
        this.closeBtn.addEventListener('click', this.onClose);
      }

      // Close when the backdrop (the dialog element itself, outside .os-bis-modal__inner) is clicked.
      this.onDialogClick = function (e) {
        if (e.target === this.dialog) this.dialog.close();
      }.bind(this);
      this.dialog.addEventListener('click', this.onDialogClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onTriggerClick, true);
      if (this.form) this.form.removeEventListener('submit', this.onSubmit);
      if (this.closeBtn && this.onClose) this.closeBtn.removeEventListener('click', this.onClose);
      if (this.dialog && this.onDialogClick) this.dialog.removeEventListener('click', this.onDialogClick);
    }

    onTriggerClick(event) {
      var trigger = event.target.closest('[data-os-bis-open]');
      if (!trigger) return;
      // Own the click completely: stop it reaching the card's link/navigation, and block the
      // default action. Runs in capture phase (see connectedCallback) so we win the race.
      event.preventDefault();
      event.stopPropagation();
      if (event.stopImmediatePropagation) event.stopImmediatePropagation();
      this.open(trigger);
    }

    open(trigger) {
      this.currentVariant = trigger.getAttribute('data-variant') || '';
      var productTitle = trigger.getAttribute('data-product-title') || '';
      var variantTitle = trigger.getAttribute('data-variant-title') || '';

      // Context line: "Product" or "Product - Size" (skip Shopify's "Default Title").
      if (this.productEl) {
        var label = productTitle;
        if (variantTitle && variantTitle !== 'Default Title') {
          label = label ? label + ' - ' + variantTitle : variantTitle;
        }
        if (label) {
          this.productEl.textContent = label;
          this.productEl.hidden = false;
        } else {
          this.productEl.hidden = true;
        }
      }

      this.resetMessage();
      this.setLoading(false);
      this.form.hidden = false;

      // If this browser already subscribed to this exact variant, show the confirmation up front.
      if (this.currentVariant && this.hasSubscribed(this.currentVariant)) {
        this.showSuccess();
      } else if (this.emailInput) {
        this.emailInput.value = this.emailInput.value || '';
      }

      this.trigger = trigger;
      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }

      if (this.emailInput && this.form.hidden === false) {
        try { this.emailInput.focus(); } catch (e) {}
      }
    }

    onSubmit(event) {
      event.preventDefault();
      if (this.submitting) return;

      var email = this.emailInput ? this.emailInput.value.trim() : '';
      if (!this.isValidEmail(email)) {
        this.showMessage('Please enter a valid email address.', 'error');
        if (this.emailInput) { try { this.emailInput.focus(); } catch (e) {} }
        return;
      }
      if (!this.currentVariant) {
        this.showMessage('Something went wrong. Please refresh and try again.', 'error');
        return;
      }

      this.submitting = true;
      this.setLoading(true);
      this.resetMessage();

      this.subscribe(email, this.currentVariant)
        .then(function () {
          this.rememberSubscribed(this.currentVariant);
          this.showSuccess();
        }.bind(this))
        .catch(function () {
          this.showMessage("Sorry, we couldn't sign you up just now. Please try again.", 'error');
        }.bind(this))
        .then(function () {
          this.submitting = false;
          this.setLoading(false);
        }.bind(this));
    }

    subscribe(email, variantId) {
      var url = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' + encodeURIComponent(this.company);
      var body = {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            profile: { data: { type: 'profile', attributes: { email: email } } },
            channels: ['EMAIL']
          },
          relationships: {
            variant: {
              data: { type: 'catalog-variant', id: '$shopify:::$default:::' + variantId }
            }
          }
        }
      };

      return fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: KLAVIYO_REVISION
        },
        body: JSON.stringify(body)
      }).then(function (res) {
        // 202 Accepted is the documented success; treat any 2xx as success.
        if (!res.ok) { throw new Error('Klaviyo BIS failed: ' + res.status); }
        return res;
      });
    }

    showSuccess() {
      if (this.form) this.form.hidden = false;
      this.showMessage(this.successCopy, 'success');
      // Collapse the input + submit once subscribed; leave the message + close button.
      if (this.emailInput) this.emailInput.closest('.os-bis-modal__field').hidden = true;
      if (this.submitBtn) this.submitBtn.hidden = true;
    }

    setLoading(loading) {
      if (this.submitBtn) {
        this.submitBtn.setAttribute('aria-busy', loading ? 'true' : 'false');
        this.submitBtn.disabled = !!loading;
      }
    }

    showMessage(text, kind) {
      if (!this.messageEl) return;
      this.messageEl.textContent = text;
      this.messageEl.hidden = false;
      this.messageEl.classList.toggle('os-bis-modal__message--error', kind === 'error');
      this.messageEl.classList.toggle('os-bis-modal__message--success', kind === 'success');
    }

    resetMessage() {
      if (!this.messageEl) return;
      this.messageEl.hidden = true;
      this.messageEl.textContent = '';
      this.messageEl.classList.remove('os-bis-modal__message--error', 'os-bis-modal__message--success');
      // Restore the input + submit (they may have been hidden by a prior success).
      var field = this.emailInput ? this.emailInput.closest('.os-bis-modal__field') : null;
      if (field) field.hidden = false;
      if (this.submitBtn) this.submitBtn.hidden = false;
    }

    isValidEmail(value) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    }

    readSubscribed() {
      try { return JSON.parse(window.localStorage.getItem(SUBSCRIBED_KEY)) || {}; } catch (e) { return {}; }
    }

    hasSubscribed(variantId) {
      return !!this.readSubscribed()[variantId];
    }

    rememberSubscribed(variantId) {
      try {
        var store = this.readSubscribed();
        store[variantId] = true;
        window.localStorage.setItem(SUBSCRIBED_KEY, JSON.stringify(store));
      } catch (e) {}
    }
  }

  if (!customElements.get('os-bis')) {
    customElements.define('os-bis', OsBis);
  }
})();

/* ============================================================================
   <os-notify> - "Tell me when you deliver to my area" sign-up modal controller.
   One instance wraps the single shared modal (snippets/os-notify-modal) rendered
   right after the back-in-stock modal near </body>. When a shopper checks a
   postcode we DON'T deliver to, the delivery gates append a CTA button (carrying
   data-os-notify-open) inside their "not available yet" message; a delegated,
   capture-phase click handler opens this modal - prefilling the postcode the
   shopper just tried. On submit it POSTs the email, interest choice and optional
   postcode/suburb to Klaviyo's client-side subscriptions endpoint, adding the
   profile to the configured list (data-list) using the public key (data-company)
   - the same list the merchant's hosted sign-up page uses. Uses a native <dialog>
   so focus-trap, Escape and the backdrop come for free, and mirrors the <os-bis>
   modal for an identical look and feel. Renders nothing / does nothing unless a
   public API key AND a list ID are configured.
   ============================================================================ */
(function () {
  // JSON:API revision pinned so a future breaking change to the endpoint can't
  // silently alter the request shape. Bump deliberately if Klaviyo requires it.
  var KLAVIYO_REVISION = '2025-07-15';

  class OsNotify extends HTMLElement {
    connectedCallback() {
      this.company = this.dataset.company || '';
      this.list = this.dataset.list || '';
      this.property = this.dataset.property || '';
      this.source = this.dataset.source || 'Storefront';
      this.dialog = this.querySelector('[data-os-notify-dialog]');
      this.form = this.querySelector('[data-os-notify-form]');
      this.emailInput = this.querySelector('[data-os-notify-email]');
      this.zipInput = this.querySelector('[data-os-notify-zip]');
      this.cityInput = this.querySelector('[data-os-notify-city]');
      this.messageEl = this.querySelector('[data-os-notify-message]');
      this.submitBtn = this.querySelector('[data-os-notify-submit]');
      this.closeBtn = this.querySelector('[data-os-notify-close]');
      var successTpl = this.querySelector('[data-os-notify-success]');
      this.successCopy = successTpl ? successTpl.innerHTML.trim() : "You're on the list.";

      // Inert unless the modal, a company (public key) and a list are all present.
      if (!this.dialog || !this.form || !this.company || !this.list) return;

      // Delegated + capture phase: any trigger anywhere opens this one modal, and the
      // <os-bis> handler ignores our button (it matches [data-os-bis-open], not ours).
      this.onTriggerClick = this.onTriggerClick.bind(this);
      document.addEventListener('click', this.onTriggerClick, true);

      this.onSubmit = this.onSubmit.bind(this);
      this.form.addEventListener('submit', this.onSubmit);

      if (this.closeBtn) {
        this.onClose = function () { this.dialog.close(); }.bind(this);
        this.closeBtn.addEventListener('click', this.onClose);
      }

      // Close when the backdrop (the dialog element itself, outside the inner panel) is clicked.
      this.onDialogClick = function (e) {
        if (e.target === this.dialog) this.dialog.close();
      }.bind(this);
      this.dialog.addEventListener('click', this.onDialogClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onTriggerClick, true);
      if (this.form) this.form.removeEventListener('submit', this.onSubmit);
      if (this.closeBtn && this.onClose) this.closeBtn.removeEventListener('click', this.onClose);
      if (this.dialog && this.onDialogClick) this.dialog.removeEventListener('click', this.onDialogClick);
    }

    onTriggerClick(event) {
      var trigger = event.target.closest('[data-os-notify-open]');
      if (!trigger) return;
      event.preventDefault();
      event.stopPropagation();
      if (event.stopImmediatePropagation) event.stopImmediatePropagation();
      this.open(trigger);
    }

    open(trigger) {
      this.resetMessage();
      this.setLoading(false);
      // Restore any fields collapsed by a previous successful sign-up in this session.
      this.setFieldsHidden(false);
      if (this.submitBtn) this.submitBtn.hidden = false;

      // Prefill the post code from the gate the shopper just failed, if we can find it.
      if (this.zipInput) {
        var gate = trigger.closest('.os-delivery-gate');
        var pcInput = gate ? gate.querySelector('[data-postcode-input]') : null;
        if (pcInput && pcInput.value) {
          this.zipInput.value = pcInput.value.replace(/[^0-9]/g, '').slice(0, 4);
        }
      }

      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }

      if (this.emailInput) { try { this.emailInput.focus(); } catch (e) {} }
    }

    onSubmit(event) {
      event.preventDefault();
      if (this.submitting) return;

      var email = this.emailInput ? this.emailInput.value.trim() : '';
      if (!this.isValidEmail(email)) {
        this.showMessage('Please enter a valid email address.', 'error');
        if (this.emailInput) { try { this.emailInput.focus(); } catch (e) {} }
        return;
      }
      var interestEl = this.querySelector('[data-os-notify-interest]:checked');
      if (!interestEl) {
        this.showMessage('Please pick one of the options above.', 'error');
        return;
      }

      this.submitting = true;
      this.setLoading(true);
      this.resetMessage();

      this.subscribe(email, interestEl.value)
        .then(function () {
          this.showSuccess();
        }.bind(this))
        .catch(function () {
          this.showMessage("Sorry, we couldn't sign you up just now. Please try again.", 'error');
        }.bind(this))
        .then(function () {
          this.submitting = false;
          this.setLoading(false);
        }.bind(this));
    }

    subscribe(email, interest) {
      var url = 'https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(this.company);
      var attrs = { email: email };

      var zip = this.zipInput ? this.zipInput.value.trim() : '';
      var city = this.cityInput ? this.cityInput.value.trim() : '';
      if (zip || city) {
        attrs.location = {};
        if (zip) attrs.location.zip = zip;
        if (city) attrs.location.city = city;
      }
      if (this.property && interest) {
        attrs.properties = {};
        attrs.properties[this.property] = interest;
      }

      var body = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: this.source,
            profile: { data: { type: 'profile', attributes: attrs } }
          },
          relationships: {
            list: { data: { type: 'list', id: this.list } }
          }
        }
      };

      return fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: KLAVIYO_REVISION
        },
        body: JSON.stringify(body)
      }).then(function (res) {
        // 202 Accepted is the documented success; treat any 2xx as success.
        if (!res.ok) { throw new Error('Klaviyo subscribe failed: ' + res.status); }
        return res;
      });
    }

    showSuccess() {
      this.showMessage(this.successCopy, 'success');
      // Collapse the inputs once subscribed; leave the message + close button.
      this.setFieldsHidden(true);
      if (this.submitBtn) this.submitBtn.hidden = true;
    }

    setFieldsHidden(hidden) {
      var groups = this.querySelectorAll('.os-bis-modal__field, .os-notify-modal__choice, .os-notify-modal__row');
      Array.prototype.forEach.call(groups, function (el) { el.hidden = hidden; });
    }

    setLoading(loading) {
      if (this.submitBtn) {
        this.submitBtn.setAttribute('aria-busy', loading ? 'true' : 'false');
        this.submitBtn.disabled = !!loading;
      }
    }

    showMessage(text, kind) {
      if (!this.messageEl) return;
      this.messageEl.textContent = text;
      this.messageEl.hidden = false;
      this.messageEl.classList.toggle('os-bis-modal__message--error', kind === 'error');
      this.messageEl.classList.toggle('os-bis-modal__message--success', kind === 'success');
    }

    resetMessage() {
      if (!this.messageEl) return;
      this.messageEl.hidden = true;
      this.messageEl.textContent = '';
      this.messageEl.classList.remove('os-bis-modal__message--error', 'os-bis-modal__message--success');
    }

    isValidEmail(value) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    }
  }

  if (!customElements.get('os-notify')) {
    customElements.define('os-notify', OsNotify);
  }
})();

/* ============================================================================
   <os-cleanse-modal> - "What's Included" modal for the cleanse landing cards
   (sections/os-cleanse-cards). One instance wraps the single shared <dialog>
   rendered per section. Every card's "Learn more" button carries
   data-os-cleanse-open + data-title and a sibling <template data-cleanse-included>
   holding that cleanse's What's Included markup. A delegated capture-phase click
   clones the clicked card's template into the dialog body, sets the heading, and
   opens the dialog. A modal is used (not an inline accordion) so the 4-across row
   never grows and the page behind never shifts. Native <dialog> gives focus-trap,
   Escape and the backdrop for free.
   ============================================================================ */
(function () {
  class OsCleanseModal extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-os-cleanse-dialog]');
      this.headingEl = this.querySelector('[data-os-cleanse-heading]');
      this.bodyEl = this.querySelector('[data-os-cleanse-body]');
      this.shopBtn = this.querySelector('[data-os-cleanse-shop]');
      this.closeBtn = this.querySelector('[data-os-cleanse-close]');

      if (!this.dialog || !this.bodyEl) return;

      // Delegated + capture phase so we own the click before any card link/handler acts.
      this.onTriggerClick = this.onTriggerClick.bind(this);
      document.addEventListener('click', this.onTriggerClick, true);

      if (this.closeBtn) {
        this.onClose = function () { this.dialog.close(); }.bind(this);
        this.closeBtn.addEventListener('click', this.onClose);
      }

      // Close when the backdrop (the dialog element itself, outside the inner panel) is clicked.
      this.onDialogClick = function (e) {
        if (e.target === this.dialog) this.dialog.close();
      }.bind(this);
      this.dialog.addEventListener('click', this.onDialogClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onTriggerClick, true);
      if (this.closeBtn && this.onClose) this.closeBtn.removeEventListener('click', this.onClose);
      if (this.dialog && this.onDialogClick) this.dialog.removeEventListener('click', this.onDialogClick);
    }

    onTriggerClick(event) {
      var trigger = event.target.closest('[data-os-cleanse-open]');
      if (!trigger) return;

      var card = trigger.closest('.os-cleanse-card');
      var tpl = card ? card.querySelector('[data-cleanse-included]') : null;
      if (!tpl) return;

      // A page can hold more than one OS Cleanse Cards section, each with its own modal. Only the
      // modal inside the trigger's own section should respond.
      var section = trigger.closest('.os-cleanse-cards');
      if (section && !section.contains(this)) return;

      event.preventDefault();
      event.stopPropagation();
      if (event.stopImmediatePropagation) event.stopImmediatePropagation();
      this.open(trigger, tpl);
    }

    open(trigger, tpl) {
      if (this.headingEl) { this.headingEl.textContent = trigger.getAttribute('data-title') || ''; }
      // Point the header's Shop now button at this cleanse's PDP (hide it if we have no URL).
      if (this.shopBtn) {
        var url = trigger.getAttribute('data-url') || '';
        if (url) { this.shopBtn.setAttribute('href', url); this.shopBtn.hidden = false; }
        else { this.shopBtn.hidden = true; }
      }
      // Swap in this card's What's Included markup.
      this.bodyEl.textContent = '';
      this.bodyEl.appendChild(tpl.content.cloneNode(true));

      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }
      // Reset the scroll position of long lists on each open.
      try { this.bodyEl.scrollTop = 0; } catch (e) {}
    }
  }

  if (!customElements.get('os-cleanse-modal')) {
    customElements.define('os-cleanse-modal', OsCleanseModal);
  }
})();

/* ============================================================================
   Safari srcset re-evaluation on resize
   WebKit has a long-standing bug: when the viewport SHRINKS and then grows back
   (e.g. resizing the desktop window down to mobile widths and out again), Safari
   keeps the low-resolution srcset candidate it picked at the narrow width instead
   of upgrading, so wide banners scale up blurry. Chrome and Firefox re-run the
   candidate-selection algorithm on resize automatically; Safari does not.

   Fix: on a debounced resize (Safari only), overwrite each responsive image's
   `sizes` attribute with its rendered width x device pixel ratio (physical px).
   Re-assigning the declarative value to itself (e.g. "58vw" -> "58vw") is a no-op
   WebKit ignores; swapping in a concrete pixel value changes the string (forcing
   WebKit to re-run candidate selection). WebKit does NOT re-apply DPR to a manually
   set `sizes`, so a bare CSS width would pick the 1x (half-resolution) candidate on
   Retina; multiplying by DPR here makes it select the full-resolution source. The
   higher-res image swaps in once decoded (no blanking, so no flash). Scoped to the
   collection banner hero, the reported surface, to keep the blast radius small.
   ========================================================================== */
(function () {
  var ua = navigator.userAgent;
  // Safari on macOS/iOS, excluding Chrome (chrome/crios), Firefox (fxios) and Android.
  var isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
  if (!isSafari) return;

  var timer = null;

  function refreshImages() {
    // Device pixel ratio: on a Retina display the image needs a source of
    // rendered-width x DPR PHYSICAL pixels to look sharp. When we force a re-pick by
    // writing an explicit `sizes`, WebKit selects a candidate matched to that string's
    // CSS px only (it does not re-apply DPR here), so a bare CSS width picks the 1x
    // (half-resolution) source and the banner stays soft. Baking DPR into the value
    // makes Safari choose the full-resolution candidate. Safari-only block, so this
    // never costs bandwidth on other browsers.
    var dpr = window.devicePixelRatio || 1;
    // The collection banner hero: matched via the section modifier class, with the
    // two hero image containers as fallbacks in case that class is ever absent.
    var imgs = document.querySelectorAll(
      '.shopify-section--collection-banner img[srcset][sizes],' +
      '.content-box__background-media img[srcset][sizes],' +
      'img.os-collection-hero__image[srcset][sizes]'
    );
    imgs.forEach(function (img) {
      var width = Math.round(img.getBoundingClientRect().width * dpr);
      // Set an explicit physical-px width so the string changes (forcing WebKit to
      // re-pick) AND resolves to the full-resolution srcset candidate.
      if (width > 0) img.sizes = width + 'px';
    });
  }

  window.addEventListener('resize', function () {
    if (timer) window.clearTimeout(timer);
    timer = window.setTimeout(refreshImages, 200);
  }, { passive: true });

  // Run once after load so the very first resize also has an accurate baseline.
  if (document.readyState === 'complete') {
    refreshImages();
  } else {
    window.addEventListener('load', refreshImages, { once: true });
  }
})();

/* ----------------------------------------------------------------------------
 * Top-layer drawer promoter
 * ---------------------------------------------------------------------------
 * Every drawer/modal in the theme (cart, mobile menu, facets, search, address,
 * modal blocks) plus our own directory filter drawer are z-index overlays. A
 * third-party popup injected into <body> at a very high z-index (e.g. the
 * Klaviyo signup form) can paint over them and swallow taps on their controls.
 * Promoting an open drawer into the browser TOP LAYER via the Popover API makes
 * it sit above ANY z-index, regardless of value or DOM order - the same
 * guarantee our BIS modal already gets from dialog.showModal().
 *
 * This only listens to lifecycle events the base theme already dispatches and
 * mirrors a pattern we already use; it makes NO change to theme.js and does not
 * alter how any drawer opens, closes, animates or traps focus. The events do not
 * bubble, so we listen in the CAPTURE phase at document level (capture still
 * reaches non-bubbling events on the way down to the target).
 *
 * Scope note: we promote full-viewport overlays only (cart / menu / facets /
 * x-drawer / x-modal), NOT anchored mini-popovers (x-popover quantity-breaks,
 * cart note/shipping x-drawer-popover) which are positioned against a trigger
 * and would mis-place if lifted into the top layer.
 * -------------------------------------------------------------------------- */
(function () {
  // Full-overlay drawers/menus/modals that should always sit above injected popups.
  // TEMPORARILY EMPTY: auto-promoting the base-theme drawers (cart / mobile menu /
  // facets / x-drawer / x-modal) is disabled pending per-drawer QA -- promoting the
  // mobile menu surfaced layout regressions on live. Only our directory filter
  // drawer (which opts in directly from openFilters/closeFilters and IS verified)
  // is promoted for now. Re-add the base tags here once each is QA'd in the top layer.
  var PROMOTE_SEL = '';
  // Same set (plus our own overlays) used to detect "is any drawer open" for the
  // no-Popover-API fallback below.
  var OPEN_SEL = 'cart-drawer[open], header-menu-drawer[open], facets-drawer[open], ' +
                 'x-drawer[open], x-modal[open], .os-dir.is-filter-open, dialog[open]';

  var supported = typeof HTMLElement !== 'undefined' &&
    typeof HTMLElement.prototype.showPopover === 'function' &&
    typeof HTMLElement.prototype.hidePopover === 'function';

  // Lift an element into the top layer. Idempotent; ignores native <dialog>
  // (its showModal() already puts it in the top layer) and detached nodes.
  function promote(el) {
    if (!supported || !el || typeof el.matches !== 'function' || el.matches('dialog')) return;
    try {
      if (!el.hasAttribute('popover')) el.setAttribute('popover', 'manual');
      if (!el.matches(':popover-open')) el.showPopover();
    } catch (e) { /* not connected yet / already shown - safe to ignore */ }
  }

  function depromote(el) {
    if (!el || typeof el.hasAttribute !== 'function' || !el.hasAttribute('popover')) return;
    try { if (el.matches(':popover-open')) el.hidePopover(); } catch (e) {}
    el.removeAttribute('popover');
  }

  // Fallback for browsers without the Popover API (e.g. Safari < 17): flag the
  // document while any drawer is open so CSS can hide the injected popup instead
  // (see os-custom.css). Recompute from the DOM so it self-corrects.
  function syncFallback() {
    if (supported) return;
    var open = false;
    try { open = !!document.querySelector(OPEN_SEL); } catch (e) {}
    document.documentElement.classList.toggle('os-drawer-open', open);
  }

  document.addEventListener('dialog:after-show', function (e) {
    if (PROMOTE_SEL && e.target && e.target.matches && e.target.matches(PROMOTE_SEL)) promote(e.target);
    if (!supported) window.requestAnimationFrame(syncFallback);
  }, true);
  ['dialog:before-hide', 'dialog:after-hide', 'dialog:force-close'].forEach(function (name) {
    document.addEventListener(name, function (e) {
      depromote(e.target);
      if (!supported) window.requestAnimationFrame(syncFallback);
    }, true);
  });

  // Exposed so our own class-toggled overlays (e.g. the directory filter drawer,
  // which is not a theme DialogElement and fires no lifecycle events) can opt in.
  window.OsTopLayer = { promote: promote, depromote: depromote, supported: supported, syncFallback: syncFallback };
})();

/* ------------------------------------------------------------------ *
 * <os-cleanse-day-tabs> - the "A day on the cleanse" timeline
 * (sections/os-cleanse-rhythm.liquid). Progressive enhancement: the
 * markup is an accessible tablist that, with JS off, shows every panel
 * stacked (see .os-cdt CSS). With JS, it behaves as WAI-ARIA tabs -
 * one panel visible at a time, roving tabindex, arrow-key navigation -
 * and the large image swaps with the selected time.
 * ------------------------------------------------------------------ */
(function () {
  'use strict';
  var TAG = 'os-cleanse-day-tabs';

  function Tabs(el) {
    this.el = el;
    this.tabs = Array.prototype.slice.call(el.querySelectorAll('[role="tab"]'));
    this.panels = Array.prototype.slice.call(el.querySelectorAll('[role="tabpanel"]'));
    if (this.tabs.length < 2) return;
    el.classList.add('os-cdt--enhanced');
    this.tablist = this.tabs[0].parentNode;
    this.active = 0;
    var self = this;
    this.tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () { self.select(i, true); });
      tab.addEventListener('keydown', function (e) { self.onKey(e, i); });
    });
    // Draw the initial progress to the first time (once laid out), and keep it aligned on resize.
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(function () { self.updateProgress(self.active); });
    } else {
      this.updateProgress(this.active);
    }
    this.onResize = function () { self.updateProgress(self.active); };
    window.addEventListener('resize', this.onResize);
  }

  Tabs.prototype.select = function (i, focus) {
    var self = this;
    this.tabs.forEach(function (tab, j) {
      var active = j === i;
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.setAttribute('tabindex', active ? '0' : '-1');
      tab.classList.toggle('is-active', active);
      tab.classList.toggle('is-passed', j < i);   // earlier times: darken their dots
      if (self.panels[j]) self.panels[j].hidden = !active;
    });
    this.active = i;
    this.updateProgress(i);
    if (focus && this.tabs[i]) this.tabs[i].focus();
  };

  // Grow the axis progress overlay to the centre of the active time's dot.
  Tabs.prototype.updateProgress = function (i) {
    if (!this.tablist) return;
    var tab = this.tabs[i];
    if (!tab) return;
    var center = tab.offsetLeft + (tab.offsetWidth / 2);
    this.tablist.style.setProperty('--os-cdt-progress', center + 'px');
  };

  Tabs.prototype.onKey = function (e, i) {
    var n = this.tabs.length, next = -1;
    switch (e.key) {
      case 'ArrowRight': case 'ArrowDown': next = (i + 1) % n; break;
      case 'ArrowLeft': case 'ArrowUp': next = (i - 1 + n) % n; break;
      case 'Home': next = 0; break;
      case 'End': next = n - 1; break;
      default: return;
    }
    e.preventDefault();
    this.select(next, true);
  };

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(scope.querySelectorAll(TAG), function (el) {
      if (el.__osCdt) return;
      el.__osCdt = true;
      new Tabs(el);
    });
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', function () { init(); });
  // Re-init when a section is re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
